import React,{ useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback, version } from 'react'
import { useDrag, useDrop, DndProvider, useDragLayer, XYCoord } from 'react-dnd'
import { HTML5Backend, getEmptyImage } from 'react-dnd-html5-backend'
import {ChainHeader,MatrixChain,BrowserChain,MatrixCellItem,MatrixModuleCellView,MatrixRenderCB,
    IMatrixRenderEvent,ConflictDialog,TConflictDialogArgs,ConflictInfoDialog,TConflictInfoArgs} from './chain'
import {klass,Div,Span,onClick,TOAST_STYLE} from "./html"
import {ModelXform,ChainMatrixControl,IMatrixCellItem,IDragged,IDragObserver, IMidiController } from './logic'
import {ModuleRole,TRecChain,TRecDeviceDef,IModule,IDeviceDef,IDevice,IChain,IChainController,
    IChainControllerState,IChainCondition,TModulePair, TStatusPair, IChainBoxController, TRoleType,
    AppState, IAppState, SupportPayload,
    IChainCell,
    SCHEMA_VER,
    } from './model'
import { DeSer } from './deser'
import {Names,Device,DeviceDef,SubModule,ModuleDef,IModuleDef,ChainCell,Module,Chain,ChainCellItem,
    ChainLane,IModuleDefController,IModuleController,TAlertArgs } from './model'
import { TRecSession, ISession, IActiveSession, Session } from './models/session'
import {TPromptArgs,WolffLogo,SearchBox,NodeIcon,DeviceIcon,ISessionControl,Snapper, KeyboardShortcutsModal, CreateSessionDialog, ICreateSessionResult, TSupportDialogArgs, SupportDialog} from './widgets'
import * as hotkeys from './hotkeys'
import {ingest,excrete,IngestError} from './devices'
import * as net from './net'
import * as mod from './module'
import * as dbg from './debug'
import * as widget from './widgets'
import * as store from './storage'
import $COLORS from './colors'
import { IMidiNoteEvent, MidiMonitor,MidiNote, IMidiNote,MIDI_OCTAVES, MIDI_NOTES, MIDI_CHANS, IMidiLearn, TMidiSink, MidiSink, TMidiLearnState, TMidiPort, TMidiPortRec} from './models/midi'
import { DeviceConfig, BrowserDevice, deviceInUse } from './device'
import { SystemConfig, DevicesConfig, BackupConfig, MidiStorageReference, TSysConfigTab } from './sysconfig'
import { IMenuChoice, TriPop, PopupMenu } from './menu'
import './index.css'
import './widgets.css'
import './device.css'
import './chain.css'
import './tag.css'
import './midi.css'
import {ShowSaveFilePickerOptions,FileSystemFileHandle,AppEnv} from './system'
import { inspect } from './log'

import { Tag } from './tag'
import { TagCache, ITag } from './models/tag'
import { DateTime, nowTimestamp } from './time'
import { encodeSessionKey, decodeSessionKey } from './encoding'

import { Identity } from './models/id'
import { demoSessionNeedsReset, refreshDemoSessionForArch, Snapshots } from './sessions'
import { Events } from './event'
import { Toaster, toast } from 'sonner'
import { changelogHtml } from './release-notes'

import { IArch, ChannelPort, Arch, ArchPP128A} from './arch'

const D = (...args) => window.console.log(...args)
const L = console.log
const W = console.warn
const E = console.error

// OVERRIDE module var to prevent unintended var aliasing
let module: never

// DEFAULT timeout for API operations
const DEFAULT_API_TIMEOUT = 1000

// IMMUTABLE "document" parameters
interface IDocParams {
    apihost?: string,
    apiproxy?: string,
    serial?: string,
    apistorage?: string,
    friendlyname?: string,
    fname?: string,
    reset?: boolean,
    dump?: boolean,
    debug?: boolean,
    updatepend?: boolean,
    hw_model?: string,
    bundled_version?: string,
    bundled_build?: string,
    bundled_channel?: string,
    email?: string,
    firstname?: string,
}

// KNOWN, "RAW" document parameters
const DOC_PARAMS: IDocParams = {} as IDocParams

// INDEX and NORMALIZE known document parameters
new URLSearchParams(window.location.search)
    .forEach((v,k) => {
        k = k.toLowerCase().replace('.','_')
        // IGNORE string-valued params of form `?A=&...`, `?A&...`
        if (['reset','dump','debug','updatepend'].includes(k)) {
            DOC_PARAMS[k] = true
        }
        else if (v?.length) {
            DOC_PARAMS[k] = v
        }
        // else IGNORE
    })


// DEFAULT API host port
const DEFAULT_HOST_API_PORT = 9999

// DECORATE API HOST w/ DEFAULT port
if (DOC_PARAMS.apihost && !DOC_PARAMS.apihost.match(/:/)) {
    DOC_PARAMS.apihost = `${DOC_PARAMS.apihost}:${DEFAULT_HOST_API_PORT}`
}
// ADD SCHEME
if (DOC_PARAMS.apiproxy && !DOC_PARAMS.apiproxy.match(/^http(s)?:/)) {
    DOC_PARAMS.apiproxy = `http://${DOC_PARAMS.apiproxy}`.replace(/"/g,'')
}
// ADD SCHEME
if (DOC_PARAMS.apistorage && !DOC_PARAMS.apistorage.match(/^http(s)?:/)) {
    DOC_PARAMS.apistorage = `http://${DOC_PARAMS.apistorage}`.replace(/"/g,'')
}

// (BUILT_AT/BUILT_FOR injected by webpack plugin)
declare const BUILT_AT: string
declare const BUILT_FOR: string
declare const VERSION: string

const APP_ENV = {
    version: VERSION ?? '',
    channel: BUILT_FOR ?? '',
    build: BUILT_AT ?? '',
    wolffhound: null,
}

AppEnv
    .system()
    .then(env => {
        Object.assign(APP_ENV,env)
        APP_ENV['unit s/n'] = OP_PARAMS.UNIT ?? null
    })
    .catch(e => D('UNABLE to introspect app env',e))

D('DOC URL',document.URL)
D('DOCUMENT PARAMS',DOC_PARAMS)

// IMMUTABLE operating parameters
const OP_PARAMS = {
    RESET: DOC_PARAMS.reset != null,
    DUMP: DOC_PARAMS.dump != null,
    DEBUG: DOC_PARAMS.debug != null,
    API_HOST: net.UOurL.parseURL(DOC_PARAMS.apihost),
    API_PROXY: net.UOurL.parseURL(DOC_PARAMS.apiproxy),
    API_STORE: net.UOurL.parseURL(DOC_PARAMS.apistorage),
    FRIEND_NAME: DOC_PARAMS.friendlyname ?? DOC_PARAMS.fname ?? DOC_PARAMS.apihost?.replace(/:\d+/,''),
    UNIT: DOC_PARAMS.serial as string|undefined,
    ARCH: Arch.from(DOC_PARAMS.hw_model || 'PP128R')!,
}

const APP = {
    version: DOC_PARAMS['version'] ?? APP_ENV.version,
    channel: DOC_PARAMS['channel'] ?? APP_ENV.channel,
    build: DOC_PARAMS['build'] ?? APP_ENV.build,
}

const DEMO = APP.channel === 'demo'
const PRODUCTION = APP.channel === 'prod'
const SUPPORT_URL = `https://${BUILT_FOR}-pp.telem.wolffaud.io/support`

D(`OPERATING PARAMS:
    API_HOST:  ${OP_PARAMS.API_HOST}
    API_PROXY: ${OP_PARAMS.API_PROXY}
    API_STORE: ${OP_PARAMS.API_STORE}
    UNIT:      ${OP_PARAMS.UNIT}
    DEBUG:     ${OP_PARAMS.DEBUG}
    ARCH:      ${OP_PARAMS.ARCH.model.name}
    VERSION:   ${APP.version}
    CHANNEL:   ${APP.channel}
    BUILD:     ${APP.build}
`)

// DEFAULT session name
const SESSION_DEFAULT = 'Default'
const SESSION_DEMO = 'Demo'

const KEY_DEMO_FIRST = 'demo.firstVisit'
const KEY_DEMO_VER = 'demo.version'
const KEY_DEMO_HW_MODEL = 'demo.hwModel'
const KEY_UPDATE_LAST_VER = 'update.lastSeenVersion'

// Wolffhound "Demo Device" window has no backing hardware
const DEMO_DEVICE = OP_PARAMS.UNIT === 'DEMO-PP-UI'

// DEEP COPY
function clone<T>(object:T) : T {
    if (object == null) return object
    return JSON.parse(JSON.stringify(object)) as T
}

// CURRENT item being DRAGGED
let dragItem: IDragged | undefined

////////////////////////////////////////////////////////////////////////////////
// CUSTOM DRAG LAYER
////////////////////////////////////////////////////////////////////////////////
const DragLayer: React.FC = () => {
    const {item, offset, dragStart,isDragging} = useDragLayer(monitor => ({
        item: monitor.getItem(),
        offset: monitor.getSourceClientOffset(),
        isDragging: monitor.isDragging(),
        // X+Y of drag start
        dragStart: {
            source: monitor.getInitialSourceClientOffset()!,
            client: monitor.getInitialClientOffset()!,
        },
    }))

    if (!isDragging || !offset || !dragStart) {
        return
    }

    const xDiff = dragStart.client.x - dragStart.source.x
    const yDiff = dragStart.client.y - dragStart.source.y

    const xFudge = -50
    const yFudge = -20
    const x = offset.x + xDiff + xFudge
    const y = offset.y + yDiff + yFudge

    const style = {
        opacity: '70%',
        zIndex: 999,
        position: 'absolute' as React.CSSProperties['position'],
        transform: `translate(${x}px, ${y}px)`,
        pointerEvents: 'none' as React.CSSProperties['pointerEvents'],
    }

    if (item) {
        const $item = item as IDragged
        if (!dragItem) dragItem = $item
        dragItem.from = {
            x: x - 10,
            y: y - yDiff + yFudge - 10,
        }
        if ($item.module) {
            const module = $item.module as IModule
            return <div style={style}>
                <mod.ChainModule {...{
                    cell:MatrixModuleCellView({module: module,dragging:true}),
                    split: false,
                    bypassable: false,
                    active: {},
                    bypassed: {},
                    conjoined: module.stereo,
                 }}/>
            </div>
        }
        else if ($item.item) {
            const {item:cell,lane} = $item.item
            return <div style={style}>
                <MatrixCellItem {...{
                    cell,
                    lane,
                    dragging:true,
                }}/>
            </div>
        }
        else if (item.type === 'chain-card') {
            // Clone actual card element for pixel-perfect preview
            const cardEl = document.getElementById(`chain-card-${item.id}`)
            if (!cardEl) return null
            return <div
                style={style}
                dangerouslySetInnerHTML={{ __html: cardEl.outerHTML }}
            />
        }
        else {
            return null
        }
    }
}

/**
 * SUPPORT remote persistence of app state
 */
class RemoteStore {

    api: store.StoreAPI

    constructor ({ url, basepath }: { url: URL, basepath: string }) {
        this.api = new store.StoreAPI({url,basepath,timeout:DEFAULT_API_TIMEOUT})
    }

    /**
     * LOAD sessions from remote store for current unit
     * @returns available sessions for unit
     */
    loadSessions () : Promise<TRecSession> | undefined {
        D('REMOTE LOAD SESSIONS')
        const path = 'sessions'
        return (async () => {
            // DISCOVER session keys
            const keys = await this.api.list(path)

            // DECODE keys to names (encoded keys have hash suffix, literal keys pass through)
            const keyMappings = await Promise.all(
                keys.map(async key => {
                    const decoded = await decodeSessionKey(key)
                    return { key, name: decoded ?? key }
                })
            )

            // LOAD all sessions
            const sessions: Array<{name:string,session:ISession}> = await Promise.all(
                keyMappings.map(({key, name}) =>
                    this.api
                        .get(`${path}/${key}`)
                        .then(data => ({name, session: DeSer.unpackSession(JSON.parse(data))}))
                )
            )

            return sessions
                .reduce((p,c) => {
                    p[c.name] = c.session
                    return p
                },{} as TRecSession)
        })()
    }
    
    /**
     * SAVE session, remotely
     */
    async saveSession ({name,session}:{name:string,session:ISession}) {
        D('REMOTE SAVE SESSION')
        const encodedKey = await encodeSessionKey(name)
        const path = `sessions/${encodedKey}`
        const data = JSON.stringify(DeSer.packSession(session))
        await this.api.put({path,data})

        // Migration: attempt to delete old literal-keyed version
        try {
            await this.api.remove(`sessions/${name}`)
        } catch {
            // Ignore - old key may not exist
        }
    }

    /**
     * REMOVE remote session
     */
    async removeSession ({name}:{name:string}) {
        D('REMOTE DELETE SESSION')
        const encodedKey = await encodeSessionKey(name)

        // Remove both encoded and literal keys (handles migration period)
        try { await this.api.remove(`sessions/${encodedKey}`) } catch {}
        try { await this.api.remove(`sessions/${name}`) } catch {}
    }

    /**
     * REMOVE all sessions from remote store for current unit
     */
    removeSessions () {
        D('REMOTE REMOVE SESSIONS')
        const path = 'sessions'
        return (async () => {
            // FIND session names
            const names = await this.api.list(path)
            await Promise.all(
                names
                .map(name => this.api
                    .remove(`${path}/${name}`)
                ))
        })()
    }
}

/**
 * SUPPORT LOCAL persistence of application state
 */
class LocalStore {

    static saveChains (chains:TRecChain,indent?:boolean) {
        localStore?.set({
            key: 'chains',
            data: JSON.stringify(DeSer.writeChains(chains),null,indent ? 2 : 0)
        })
    }

    static saveActive (active:IActiveSession | undefined) {
        if (!active) {
            localStore?.delete('active')
        }
        else {
            localStore?.set({
                key: 'active',
                data: JSON.stringify(DeSer.writeActive(active),null,2)
            })
        }
    }

    static loadChains (arch: IArch): IChain[] | undefined {
        const stored = localStore?.get('chains')
        if (stored) {
            try {
                const chains = DeSer.readChains(JSON.parse(stored))
                if (chains) {
                    chains.forEach(chain => {
                        if (Chain.invalid(chain,arch)) {
                            delete chain.cond.active
                        }
                    })
                }
                return chains
            } catch (e) {
                E('LOAD CHAINS',e)
            }
        }
    }

    static loadDevices () : TRecDeviceDef | undefined {
        const stored = localStore?.get('devices')
        if (stored) {
            try {
                return DeSer.readDevices(JSON.parse(stored))
            } catch (e) {
                E('LOAD DEVICES',e)
            }
        }
    }

    static loadActive () : IActiveSession | undefined {
        const stored = localStore?.get('active')
        if (stored) {
            try {
                return DeSer.readActive(JSON.parse(stored))
            } catch (e) {
                E('LOAD ACTIVE SESSION',e)
            }
        }
    }

    static loadApp () : IAppState | undefined {
        const stored = localStore?.get('app')
        if (stored) {
            try {
                return DeSer.readAppState(JSON.parse(stored))
            } catch (e) {
                E('LOAD APP STATE',e)
            }
        }
    }

    static saveApp (app:IAppState,indent?:boolean) {
        localStore?.set({
            key: 'app',
            data: JSON.stringify(DeSer.writeAppState(app),null,indent ? 2 : 0)
        })
    }

    static saveDevices (devices:TRecDeviceDef,indent?:boolean) {
        localStore?.set({
            key: 'devices',
            data: JSON.stringify(DeSer.writeDevices(devices),null,indent ? 2 : 0)
        })
    }

    static saveSessions (sessions:TRecSession,indent?:boolean) {
        localStore?.set({
            key: 'sessions',
            data: JSON.stringify(DeSer.packSessions(sessions),null,indent ? 2 : 0)
        })
    }

    static loadSessions (): TRecSession | undefined {
        const stored = localStore?.get('sessions')
        if (stored) {
            try {
                return DeSer.readSessions(JSON.parse(stored))
            } catch (e) {
                E('LOAD SESSIONS',e)
            }
        }
    }

    static resetAll () {
        localStore?.delete('devices')
        localStore?.delete('chains')
        localStore?.delete('sessions')
        localStore?.delete('active')
        localStore?.delete('app')
    }
}

/**
 * COLLECT all in-use (active) modules of chain
 * (utilize the "visual" model for this as it reflects the logical status of any module
 * implicitly bypassed because its upstream source was deactivated)
 * 
 * XXX explain necessity for `split` param
 * XXX use graph representation to replace use of ModelXform
 */
function getActiveModules (chain:IChain,arch:IArch) : IModule[] {
    const cells:IMatrixCellItem[] = ModelXform({chain,arch})
        .lanes.map(_ => _.cells)
        .flat()
        .filter(_ => _.fill == false) as IMatrixCellItem[]

    const actives = Object
        .values(
            cells
            // *exists* modules and *active*
            .filter(({active}) => active.L || active.R)
            .reduce((p:Record<string,IModule>,{modules,active,bypassed}) => {
                getActiveItemModules(modules,true,active,bypassed)
                    .flat()
                    .forEach(module => p[module.id] = module)
                return p
            },{}))
    return actives

    function getActiveItemModules (modulePair:TModulePair,split:boolean,active:TStatusPair,bypassed?:TStatusPair) : IModule[] {
        const conjoined = split && modulePair.L?.stereo && modulePair.L.id === modulePair.R?.id
        const modules =  conjoined
            ? Module.getItemModules(SubModule.split(modulePair.L!)!) 
            : [modulePair.L,modulePair.R]
        const {L,R} = {L: modules[0], R: modules[1]}
        const actives:IModule[] = []
        if (L && active.L && (!bypassed || !bypassed.L)) {
            actives[0] = L
        }
        if (R && active.R && (!bypassed || !bypassed.R)) {
            actives.push(R)
        }
        return actives
    }
}


/**
 * COLLECT all in-use (active) ports of chain
 * @param chain 
 * @param split 
 * @returns 
 */
function getActivePorts (chain:IChain,arch:IArch) : { from: Record<string,IModule>, to: Record<string,IModule> }
{
    return getActiveModules(chain,arch)
        .reduce((p:{from:{},to:{}},module) => {
            const ports = Module.ports(module,arch)
            ports.from.forEach(port => p.from[port] = module)
            ports.to.forEach(port => p.to[port] = module)
            return p
        },{from:{},to:{}})
}

// DESCRIBE same-chain port conflicts: pairs of active Nodes WITHIN one chain whose ports
// collide on a shared physical matrix path (direct or indirect, e.g. FP15 vs D7 on PP64R).
// Mirrors Module.conflicting's from/from and to/to comparison — a writer+reader on one
// channel is a route, not a conflict, so cross-side pairs are intentionally not compared.
// Used to inform the user when an action creates such a conflict.
function sameChainConflictItems (chain:IChain, arch:IArch) : Array<{a:string,b:string}> {
    const nodes = Chain.activeNodes(chain)
    const label = (m:IModule, port:string) => `${m.device.name} → ${m.name} (${port})`
    const seen = new Set<string>()
    const items: Array<{a:string,b:string}> = []
    for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
            const a = nodes[i], b = nodes[j]
            const ap = Module.ports(a,arch), bp = Module.ports(b,arch)
            const pairs: Array<[string,string]> = [
                ...ap.from.flatMap(x => bp.from.map(y => [x,y] as [string,string])),
                ...ap.to.flatMap(x => bp.to.map(y => [x,y] as [string,string])),
            ]
            const hit = pairs.find(([x,y]) => arch.ports.isConflict(x,y))
            if (hit) {
                const key = [a.id,b.id].sort().join('|')
                if (!seen.has(key)) {
                    seen.add(key)
                    items.push({a: label(a,hit[0]), b: label(b,hit[1])})
                }
            }
        }
    }
    return items
}

// CURRENT listeners of chain evenvts (e.g. onRender)
const ChainListeners = {}

// HOLDS ID of newly created chain whose "card" title should be focused/editable
// (additedly a hack)
let NEW_CHAIN: undefined | string

// CYCLE "random" color assignment
// XXX make truly random???
let colorIdx = 0

interface DraggedChain {
    id: string
    index: number
    type: string
}

/**
 * Overlay shown when processors are bypassed.
 * Dynamically positions content centered over processor modules.
 */
function BypassOverlay({chainId, selected, locked, procCount, onRestore}: {
    chainId: string
    selected: boolean
    locked: boolean
    procCount: number
    onRestore: () => void
}) {
    const contentRef = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        const content = contentRef.current
        if (!content) return

        const card = document.getElementById(`chain-card-${chainId}`)
        if (!card) return

        const procs = card.querySelectorAll('.chain-module.proc')
        if (procs.length === 0) return

        // Read header height from CSS variable (defined in chain.css)
        const overlay = content.parentElement
        const headerHeight = overlay
            ? parseInt(getComputedStyle(overlay).getPropertyValue('--chain-header-height'), 10) || 76
            : 76

        const cardRect = card.getBoundingClientRect()
        let minTop = Infinity
        let maxBottom = -Infinity

        procs.forEach(proc => {
            const rect = proc.getBoundingClientRect()
            minTop = Math.min(minTop, rect.top - cardRect.top)
            maxBottom = Math.max(maxBottom, rect.bottom - cardRect.top)
        })

        // Center of processors relative to card, minus overlay offset
        const centerY = (minTop + maxBottom) / 2 - headerHeight
        content.style.top = `${centerY}px`
    }, [chainId, procCount])

    return (
        <Div k="overlay" onClick={e => {
            if (locked) {
                toast(<Div k='toast'>Chain is locked</Div>, { id: 'toast', style: TOAST_STYLE })
            } else {
                onRestore()
            }
            e.stopPropagation()
            e.preventDefault()
        }}>
            <div ref={contentRef} className="content">
                <h1>Processors Bypassed</h1>
                <h2>({selected ? <><em>b</em> or </> : ''}click to restore)</h2>
            </div>
        </Div>
    )
}

/**
 * "chain-space" wrapper of editable chain.
 * Provides needed plumbing (e.g. DnD) for UI.
 * @returns
 */
function ChainCard ({chain,midi,dragged,dragSeer,controllers,overlay,arch,replacer}
    : {
        chain:IChain
        midi?: IMidiLearn
        dragged?:IDragged
        dragSeer:IDragObserver
        controllers: {
            chain: IChainCardController
            module: (node:IModule) => IModuleController
            midi: IMidiController
        }
        overlay?:boolean
        arch: IArch
        replacer?: {
            catalog: IModule[]
            inuse: Record<string,string>
        }
    })
{
    const control = controllers.chain
    // IS DRAGGED ITEM valid for CHAIN???
    const $canDrop = (drop:IDragged) : boolean => {
        // LOCKED chains reject ALL drops
        if (chain.state.locked) return false
        // identify of dropped cell's source chain
        const dropChain:string|undefined = drop.item?.item.chain?.id
        // INTRA-CHAIN CELL DRAG
        if (dropChain && dropChain === chain.id) {
            return true
        }
        // CROSS-CHAIN CELL DRAG
        else if (dropChain) {
            if (chain.cond.bypass) return false
            const {L,R} = drop.item!.item.modules
            // EMPTY cell (stub) — nothing to drop
            if (!L && !R) return false
            // SPLIT cell with distinct L/R modules — not supported for cross-chain (would lose one)
            if (L && R && L.id !== R.id) return false
            // NODE already in target chain?
            const mods = [L,R].filter(Boolean) as IModule[]
            if (mods.some(m => Chain.hasNode({chain, module: m}))) return false
            return true
        }
        else if (chain && chain.cond.bypass) {
            return false
        }
        // MODULE DRAG
        else if (drop.module) {
            const node = drop.module
            // INTRA-CHAIN drop
            if (drop.chain === chain.id) {
                return true
            }
            // BROWSER->CHAIN drop
            else {
                return !Chain.hasNode({chain, module: node})
            }
        }
        // ???
        else {
            return false
        }
    }
    const canDrop = dragged && $canDrop(dragged)
    const ref = useRef<HTMLDivElement>(null)
    const {id,cond} = chain
    const {selected} = cond
    const SPLIT_LANE_W = 592
    const UNSPLIT_LANE_W = 314
    // CALC full width of chain for sizing previews/containers
    const width = chain.lanes.reduce((p,lane) => {
        return p + (ChainLane.isSplit(lane) ? SPLIT_LANE_W : UNSPLIT_LANE_W)
    },24)

    // DRAG SOURCE: makes this chain card draggable
    const [{isDragging}, drag, dragPreview] = useDrag(() => ({
        type: 'chain-card',
        item: {
            id: chain.id,
            name: chain.state.name,
            color: chain.state.color,
            width,
            type: 'chain-card',
        },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    }), [chain.id, chain.state.name, chain.state.color, width])

    // Suppress native drag preview to prevent "snap back" animation
    useEffect(() => {
        dragPreview(getEmptyImage())
    }, [dragPreview])

    // Connect drag source to ref (drop is handled by ChainSpace container)
    drag(ref)

    // SCROLL into VIEW if selected
    useEffect(() => {selected && ref.current?.scrollIntoView({ behavior: 'smooth' })}, [selected])

    const dropInvalid = dragged && !canDrop

    function isNewChain (chain:IChain) : boolean {
        if (NEW_CHAIN === chain.id) {
            NEW_CHAIN = undefined
            return true
        }
        else {
            return false
        }
    }

    const split = Chain.isSplit(chain)
    const hasM2S = Chain.hasM2S(chain)
    // XXX employ caching?
    const chainView = ModelXform({chain,arch})
    return (
        <Div
                ref={ref}
                k={[
                    'chain-card',
                    cond.selected && 'highlight',
                    dropInvalid && 'drop-invalid',
                    midi && 'midi-learning',
                    cond.bypass && 'bypassed',
                ]}
                data-chain-id={id}
                id={`chain-card-${id}`}
                onClick={() => !chain.cond.selected && control.select(id)}
                s={{width: `${width}px`, opacity: isDragging ? 0 : 1}}>
            <ChainHeader {...{
                arch,
                chain,
                midi,
                _new: isNewChain(chain),
                control: {
                    set: state => control.modify({id,state}),
                    name: _ => control.name(id,_),
                    color: _ => control.color(id,_),
                    remove: () => control.remove(id),
                    active: ()  => control.active(id),
                    focus: () => control.focus(id,!cond.focus),
                    hide: () => control.hide(id),
                    show: () => control.show(id,false),
                    fave: () => control.fave(id),
                    lock: () => control.lock(id),
                    normal: () => control.normal(id),
                    copy: () => control.copy(id,Identity.create),
                    tags: control.tags(chain.id),
                    split: !split ? () => control.split(id) : undefined,
                    unsplit: !hasM2S && split ? () => control.unsplit(id) : undefined,
                    alert: args => control.alert(args),
                    bypass: 
                        (chain.cond.bypass || Chain.activeNodes(chain).some(_ => _.proc))
                        ? () => control.bypass(id)
                        : undefined,
                    midi: {
                        assignment: chain.state.midi?.activate,
                        choices: () =>
                            controllers.midi.choices({
                                assign: note => control.modify({id,state:{midi:{activate:note}}}),
                                assigned: chain.state.midi?.activate
                                    ? {
                                        note: chain.state.midi?.activate,
                                        label: chain.state.name,
                                    } : undefined
                            })
                    }
                },
            }}/>
            <MatrixChain {...{
                id,
                midi,
                chain: chainView,
                    controllers: {
                    matrix: ChainMatrixControl({
                        chain,
                        set: (chain,mutate,crossChainRemove) => {
                            // Do not auto-restore bypass snapshot here; many editor actions
                            // mutate activation state and would be reverted by a restore.
                            control.modify({id:chain.id,chain,mutate,cond:{active:chain.cond.active},crossChainRemove})
                        },
                        midi: controllers.midi,
                        idgen: Identity.create,
                        arch,
                        onLocked: () => toast(<Div k='toast'>Chain is locked</Div>, { id: 'toast', style: TOAST_STYLE, position: 'top-center' }),
                        // ALT+CLICK ISOLATE: copy the clicked node into a brand-new chain.
                        // addChain yields an empty mono chain; insertItem places by role.
                        onIsolate: (module) => {
                            const created = control.addChain({replace:false})
                            control.insertItem(created.id, module)
                        },
                    }),
                    ...controllers
                },
                dragSeer,
                dragging: canDrop ? dragged : undefined,
                onRender: ChainListeners[chain.id],
                overlay: overlay ? {arch} : undefined,
                replacer,
            }}/>
            {OP_PARAMS.DEBUG && <dbg.CollapsingJson chain={chain} arch={OP_PARAMS.ARCH}/>}
            {
                // PROCESSOR BYPASS OVERLAY
            }
            {chain.cond.bypass &&
                <BypassOverlay
                    chainId={id}
                    selected={!!selected}
                    locked={!!chain.state.locked}
                    procCount={Chain.activeNodes(chain).filter(_ => _.proc).length}
                    onRestore={() => control.bypass(id)}
                />
            }
        </Div>
    )
}

/**
 * DISPLAY build time
 */
function TimeDisplay () {
    return (
        <div id="time-display">
            {APP.version} {APP.build} ({APP.channel})
        </div>
    )
}

/**
 * CONTAINS state of a chain "transaction"
 */
interface ITxAction {
    chains: IChain[]
    then: IChain[]
    // RECORDS ID of chain whose module contents have been modified
    // XXX more details please!
    mutated?: string
    // CROSS-CHAIN DROP flag — controls dismiss behavior in conflict dialog (full revert vs cancel activation)
    crossChainDrop?: boolean
    find (id:string) : {index:number,chain:IChain}
}

/**
 * CONTROLLER logic for "chain-space" chain visualization
 */
interface IChainCardController extends IChainController {
    txn () : ITxAction
    find (id:string) : IChain
    commit (txn: ITxAction)
    all () : TRecChain
    modify ({
        id,
        state,
        cond,
        chain,
        mutate,
        txn,
        crossChainRemove,
    } : {
        id:string
        state?:IChainControllerState
        cond?:IChainCondition
        chain?:IChain
        mutate?: boolean
        txn?:ITxAction
        crossChainRemove?: {chainId:string,cellId:string,item:number}
    }) : void
    addChain ({replace}:{replace:boolean})
    focus (id:string,focus:boolean,txn?:ITxAction): void
    select (id:string,txn?:ITxAction): void
    hide (id:string,txn?:ITxAction): void
    listen (id:string,onRender:MatrixRenderCB): void
    unlisten (id:string,onRender:MatrixRenderCB): void
    insertItem: (id:string,module:IModule) => {chain:string,cell:string,item:number,channel?:'L'|'R'} | undefined
    reorder: (args: { id: string, targetId?: string, after?: boolean }) => void
    alert (args:TAlertArgs) : void
    bypass (id:string, nonmodal?:boolean): void
}

// BROWSER module view state
interface ModuleState {
    expanded?: boolean
}

// Wires a browser-palette module as a react-dnd drag source. This is a HOOK, not a child
// component, so useDrag lives in BrowserNode — the component that owns `nodeRef`. That's
// what makes the connection reliable: react-dnd attaches `draggable` from a layout effect,
// and a component's own layout effect runs only AFTER its descendants' refs are attached.
// (When this was a sibling component, its effect could fire while nodeRef.current was still
// null, so `draggable` never got set — the intermittent "text selects instead of drags"
// regression.) Call it unconditionally per node (mono = 1, stereo = 3) to keep hook order
// stable; an unrendered L/R node just never connects.
function useBrowseDrag (
    nodeRef: React.RefObject<HTMLDivElement>,
    module: IModule | null | undefined,
    channel: 'L'|'R'|undefined,
    dragSeer: IDragObserver,
    onFlyBack: (fly: {module: IModule, to: XYCoord, from: XYCoord, channel?: 'L'|'R'}) => void,
) : void {
    const dropRes = useRef<{res?:{nodrop?:boolean}}|undefined>()
    const [{dragging},$drag,preview] = useDrag({
        type: 'module',
        item: {module},
        end: (_,monitor) => {
            const res:any = monitor.getDropResult()
            dropRes.current = { res }
            dragSeer.stop()
        },
        collect: monitor => ({
            dragging: monitor.isDragging(),
        }),
    },[nodeRef,module])
    const dragState = useRef<{dragging:boolean}>({dragging})
    const rect = nodeRef.current?.getBoundingClientRect()
    useEffect(() => {
        if (dragging && !dragState.current.dragging && rect && module) {
            // FUDGE the fact that the drag version is 30px shorter
            const xFudge = -30
            dragItem = {
                role: module.role,
                module: module,
                to: {
                    x: rect.x + xFudge,
                    y: rect.y,
                },
            }
            dragSeer.start(dragItem)
        }
        else if (!dragging && dragState.current.dragging) {
            const res = dropRes.current?.res
            // INVALID DROP TARGET? "fly" back the dragged module to its source
            if ((res?.nodrop || !res) && dragItem?.from && dragItem?.to && module) {
                onFlyBack({module, to:dragItem.to, from:dragItem.from, channel})
            }
        }
        dragState.current = { dragging }
    }, [dragging])
    // INVISIBLE DRAG SOURCE: hides default drag preview (i.e. ghost)
    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true })
    }, [preview])
    // CONNECT the source to its node — a no-op until the node mounts, reconnected once it does.
    $drag(nodeRef)
}

/**
 * Node visualization used by node browser
 */
function BrowserNode ({module,control,state,active,dragSeer,onExpand,onChoose,onIsolate,setDevice,arch}
    : {
        module:IModuleDef
        control:{ module: IModuleDefController }
        state:ModuleState
        active:Record<string,{id:string,color:string}>
        setDevice: (id:string) => void
        dragSeer: IDragObserver
        onChoose?: ({module,onRender}:{module:IModule,onRender:(pos:XYCoord)=>void})=>void
        // ALT+CLICK: copy this palette node into a fresh chain
        onIsolate?: (module:IModule)=>void
        onExpand?: ()=>void
        arch: IArch
    }) : any
{
    // INIT DND
    const ref = useRef<HTMLDivElement>(null)
    const refL = useRef<HTMLDivElement>(null)
    const refR = useRef<HTMLDivElement>(null)
    const refFly = useRef<HTMLDivElement>(null)

    type Fly = {
        from:XYCoord
        to:XYCoord
        channel?:'L'|'R'
        module: IModule
    }

    const moduleRef = Module.fromDef(module,arch)

    // USED for "fly to/back" node-chain animations
    const [flyStart,setFlyStart] = useState<Fly|null>(null)
    const [flyTrans,setFlyTrans] = useState<Fly|null>(null)

    const stereo = !!module.phonia.stereo

    // expanded "sub" modules - broken out left/right channels
    const {L:left,R:right} = stereo && moduleRef && SubModule.split(moduleRef) || {}

    // DRAG SOURCES: one per node this row can render (mono = main only; stereo also splits
    // into L/R). Called unconditionally so hook order is stable across mono/stereo renders.
    useBrowseDrag(ref,  moduleRef, undefined, dragSeer, setFlyStart)
    useBrowseDrag(refL, left,      'L',       dragSeer, setFlyStart)
    useBrowseDrag(refR, right,     'R',       dragSeer, setFlyStart)

    const inuse = {
        L: left?.id && active[left.id]?.color || active[module.id]?.color,
        R: right?.id && active[right.id]?.color || active[module.id]?.color,
    }

    const view = (module:IModule,state:ModuleState) => ({...MatrixModuleCellView({module}),...state})

    useEffect(() => {
        if (refFly.current) {
            // TRANSITION time
            const transition = 0.3
            if (flyTrans) {
                refFly.current.style.opacity = '50%'
                refFly.current.style.transform = `translate(${flyTrans.to.x - flyTrans.from.x}px,${flyTrans.to.y - flyTrans.from.y}px)`
                refFly.current.style.transition = `opacity ${transition}s ease-out, transform ${transition}s ease-out`
            }
            else if (flyStart) {
                setFlyTrans(flyStart)
                // REMOVE "fly" element from DOM after CSS transition complete
                setTimeout(() => {
                        setFlyStart(null)
                        setFlyTrans(null)
                    },
                    transition * 1000
                )
            }
        }
    })

    function onRender(module:IModule,ref:React.RefObject<HTMLDivElement>,channel?:'L'|'R') : (pos:XYCoord)=>void {
        return (to:XYCoord) => {
            const rect = ref.current?.getBoundingClientRect()
            if (rect) {
                const from = {x:rect.x,y:rect.y}
                const fly:Fly = {module: module,to,from,channel}
                setFlyStart(fly)
            }
        }
    }

    const expandable = left && right

    // DOUBLE-CLICK an L/R chip on a (collapsed) stereo node to add just that channel.
    // Source the fly animation from the main node ref — refL/refR are display:none when
    // collapsed and would yield a zero-rect origin.
    const chooseChannel = (channel:'L'|'R') => {
        const sub = channel === 'L' ? left : right
        if (sub && onChoose) onChoose({module:sub,onRender:onRender(sub,ref,channel)})
    }

    // TOP-level rendered module
    return (
        moduleRef ?
        <>
            <Div k={['item',state?.expanded && 'expanded']}>
                <Div
                        ref={ref}
                        k={onIsolate ? 'isolatable' : undefined}
                        onClickCapture={onIsolate ? (e:React.MouseEvent) => {
                            if (e.button === 0 && Events.alt(e)) { onIsolate(moduleRef); e.stopPropagation(); e.preventDefault() }
                        } : undefined}
                        onDoubleClick={onChoose ? () => onChoose({module:moduleRef,onRender:onRender(moduleRef,ref)}) : undefined}>
                    <mod.BrowserModule
                        control={control.module}
                        inuse={inuse}
                        onExpand={onExpand}
                        onChooseChannel={onChoose && expandable ? chooseChannel : undefined}
                        m2s={module.phonia.m2s}
                        stereo={module.phonia.stereo}
                        setDevice={setDevice}
                        {...view(moduleRef,state)}
                        overlay={($PortOverlay || undefined) && {
                            module: moduleRef,
                            arch: $Arch,
                        }}
                    />
                </Div>
                {expandable &&
                    <>
                        <div className={klass(['subitem','top',state?.expanded && 'expanded',onIsolate && 'isolatable'])}
                                ref={refL}
                                onClickCapture={onIsolate ? (e:React.MouseEvent) => {
                                    if (e.button === 0 && Events.alt(e)) { onIsolate(left!); e.stopPropagation(); e.preventDefault() }
                                } : undefined}
                                onDoubleClick={onChoose ? () => onChoose({module:left,onRender:onRender(left,refL,'L')}) : undefined}>
                            <mod.BrowserModule
                                control={control.module}
                                inuse={inuse}
                                L={true}
                                setDevice={setDevice}
                                {...view(left,state)}
                                overlay={($PortOverlay || undefined) && {
                                    module: left,
                                    arch: $Arch,
                                }}
                            />
                        </div>
                        <div className={klass(['subitem',state?.expanded && 'expanded',onIsolate && 'isolatable'])}
                                ref={refR}
                                onClickCapture={onIsolate ? (e:React.MouseEvent) => {
                                    if (e.button === 0 && Events.alt(e)) { onIsolate(right!); e.stopPropagation(); e.preventDefault() }
                                } : undefined}
                                onDoubleClick={onChoose ? () => onChoose({module:right,onRender:onRender(right,refR,'R')}) : undefined}>
                            <mod.BrowserModule
                                control={control.module}
                                inuse={inuse}
                                R={true}
                                setDevice={setDevice}
                                {...view(right,state)}
                                overlay={($PortOverlay || undefined) && {
                                    module: right,
                                    arch: $Arch,
                                }}
                            />
                        </div>
                    </>
                }
            </Div>
            {flyStart &&
                <div 
                    ref={refFly} 
                    style={{
                        zIndex: 999,
                        opacity: '70%',
                        position: 'absolute',
                        left: `${flyStart.from.x}px`,
                        top: `${flyStart.from.y}px`,
                    }}
                >
                    <mod.ChainModule {...{
                        cell:MatrixModuleCellView({module:flyStart.module,dragging:true}),
                        bypassable: false,
                        split: false,
                        active: {},
                        bypassed: {},
                        stereo: flyStart.module.stereo,
                        stub: false,
                    }}/>
                </div>
            }
        </>
        :
            <Div k={['item']}>
                    <mod.BrowserModule
                        invalid={true}
                        control={control.module}
                        inuse={inuse}
                        onExpand={onExpand}
                        m2s={module.phonia.m2s}
                        stereo={module.phonia.stereo}
                        setDevice={setDevice}
                        name={module.name}
                        device={module.device}
                    />
            </Div>
    )
}

class SearchIndex<T> {
    index: Record<string,T>
    constructor (toker,items) {
        this.index = items
            .reduce((p,c) => {
                // Normalize tokens to lowercase for case-insensitive search
                const index = toker(c)
                    .map((t:string) => (t ?? '').toString().toLowerCase())
                    .join(' ')
                p[index] = p[index] ? [...p[index],c] : [c]
                return p
            },{})
    }

    find (query:string): Array<T> {
        // ESCAPE regex
        const esc = _ => _.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

        // TOKENIZE query text
        const tokenize = v => {
            const tokens:string[] = []
            v.replaceAll(/"([^"]+)"|\S+/g,(g1:string,g2:string|undefined) => tokens.push(g2 || g1))
            return tokens
        }
        const tokens = tokenize(query.toLowerCase())
        return Object
            .entries(this.index)
            // SUBSTRING match
            .filter(([k]) => tokens.every(_ => k.match(new RegExp(esc(_)))))
            .map(([_,v]) => v)
    }
}

// TRACK tag filter states
interface ITagState {
    tag: string
    selected?:boolean
}
type StatedTags = Record<string,ITagState>
type TypedTags = Record<string,StatedTags>

const FilterTags = ({ tags, onSelect } : {tags: StatedTags, onSelect?: (k:string)=>void}) => {
    return (
        <Div k={['filters','tags']}>
            {Object.entries(tags)
                .sort(([a],[b]) => a.toLowerCase() < b.toLowerCase() ? -1 : a.toLowerCase() > b.toLowerCase() ? 1 : 0)
                .map(([k,{tag,selected}],i) => (
                    <Div key={i} onClick={() => onSelect && onSelect(k)}>
                        <Tag
                            k={[selected && 'selected']}
                            tag={tag}
                        />
                    </Div>
            ))}
        </Div>
    )
}

export class ModuleDefController {
    static create (
        { setDevice }
        :
        { setDevice: (id: string) => void })
    : IModuleDefController 
    {
        return {
            gotoDevice (id: string) {
                const device = $Devices[id]
                device && setDevice?.(device.id)
            },
        }
    }
}

/**
 * COLLECT MIDI event sinks
 */
function collectSinks (
    {indexer, chains, app, sessions} :
    {indexer: (note?:IMidiNote) => string|undefined, chains: TRecChain, app: IAppState, sessions: TRecSession}
)
    : Record<string,TMidiSink[]>
{
    const midiSinks : Record<string,TMidiSink[]> = {}
    chains.forEach(chain => {
        const note = indexer(chain.state.midi?.activate)
        // INDEX chain actviation
        if (note) {
            midiSinks[note] = midiSinks[note] ?? []
            midiSinks[note].push({chain: chain.id})
        }
        // INDEX chain node activations
        Object
            .values(Chain.indexNodes(chain))
            .forEach(node => {
                const note = indexer(node.midi?.activate?.L)
                if (note) {
                    midiSinks[note] = midiSinks[note] ?? []
                    midiSinks[note].push({chain: chain.id, node: node.id})
                }
            })
        // INDEX chain group activations
        Chain.cells(chain)
            .filter(cell => cell.ab)
            .forEach(cell => {
                const note = indexer(cell.midi)
                if (note) {
                    midiSinks[note] = midiSinks[note] ?? []
                    midiSinks[note].push({chain: chain.id, group: cell.id})
                }
        })
    })
    if (app.settings.mute.midi) {
        const note = indexer(app.settings.mute.midi)
        if (note) {
            midiSinks[note] = midiSinks[note] ?? []
            midiSinks[note].push('mute')
        }
    }
    Object.values(sessions)
        .filter(({meta:{midi}}) => midi)
        .forEach(session => {
            const note = indexer(session.meta.midi)!
            midiSinks[note] = midiSinks[note] ?? []
            midiSinks[note].push({session: session.id})
        })

    return midiSinks
}

class MidiController implements IMidiController {
    private $alert: (args: TAlertArgs) => void
    private $prompt : (args: TPopArgs) => void
    private $refreshState: () => void

    constructor ({
        alert,
        prompt,
        refreshState,
    } : {
        alert (args: TAlertArgs) : void
        prompt (args: TPopArgs) : void
        refreshState () : void
    }) {
        this.$refreshState = refreshState
        this.$alert = alert
        this.$prompt = prompt
    }

    clearAll () {
        this.$prompt({
            title: 'CLEAR ALL?',
            prompt: 'Clear all MIDI settings for this session?',
            yes: () => {
                // CLEAR chain modules
                $Chains
                    .map(chain => Object.values(Chain.indexNodes(chain)))
                    .forEach(nodes => {
                        nodes
                        .filter(({midi}) => midi?.activate?.L)
                        .forEach(({midi}) => midi!.activate!.L = undefined)
                    })
                // CLEAR chains
                $Chains
                    .filter(({state:{midi}}) => midi?.activate)
                    .forEach(({state:{midi}}) => midi!.activate = undefined)
                // CLEAR AB groups
                $Chains
                    .forEach(chain => {
                        Chain.cells(chain)
                            .filter(cell => cell.ab && cell.midi)
                            .forEach(cell => cell.midi = undefined)
                    },{})
                // CLEAR sessions
                Object.values($Sessions)
                    .forEach(session => session.meta.midi = undefined)

                $AppState.settings.mute.midi = undefined
                this.$refreshState()
            }
        })
    }

    clear (clear: IMidiNote) {
        // CLEAR chain modules
        $Chains
            .map(chain => Object.values(Chain.indexNodes(chain)))
            .forEach(nodes => {
                nodes
                .filter(({midi}) => MidiNote.equal(clear,midi?.activate?.L))
                .forEach(({midi}) => midi!.activate!.L = undefined)
            })
        // CLEAR chains
        $Chains
            .filter(({state:{midi}}) => MidiNote.equal(clear,midi?.activate))
            .forEach(({state:{midi}}) => midi!.activate = undefined)
        // CLEAR AB-groups
        $Chains
            .forEach(chain => {
                Chain.cells(chain)
                    .filter(cell => cell.ab && MidiNote.equal(clear,cell.midi))
                    .forEach(cell => cell.midi = undefined)
            })
        // CLEAR sessions
        Object.values($Sessions)
            .filter(({meta:{midi}}) => MidiNote.equal(clear,midi))
            .forEach(session => session.meta.midi = undefined)

        const muteNote = $AppState.settings.mute.midi
        if (MidiNote.equal(clear,muteNote)) {
            $AppState.settings.mute.midi = undefined
        }
        this.$refreshState()
    }

    choices (
        {assigned,assign,clear:$clear} : 
        {
            assigned?: {
                note: IMidiNote
                label: string
            }
            assign (note?:IMidiNote) : void
            clear?: boolean
        }
    ) {
        const clear = $clear == null || $clear
        // INDEX CHAIN NODES by MIDI NOTE
        const reserved: Record<string,Record<string,any>> = 
            $Chains
                .map(chain => {
                    const nodes = Chain.indexNodes(chain)
                    return {
                        nodes: Object.values(nodes),
                        chain,
                    }
                })
                .reduce((p,c) => {
                    c.nodes
                        .filter(({midi}) => midi?.activate?.L)
                        .forEach(node => {
                            // NOTATE CHANNEL SIDE of SPLIT processor
                            const place = SubModule.getPlace(node) ? ` (${SubModule.getPlace(node)})` : ''
                            const index = MidiNote.toString(node.midi!.activate!.L!)
                            p[index] = p[index] ?? {}
                            p[index][node.id] = 
                                <Div k='menu-item midi'>
                                    <NodeIcon size={16}/>&nbsp;{`${c.chain.state.name} → ${node.device.name} ${node.name}${place}`}
                                </Div>
                        })
                    return p
                },{})
        // INDEX CHAINS by MIDI NOTE
        $Chains
            .filter(({state:{midi}}) => midi?.activate)
            .forEach(chain => {
                const index = MidiNote.toString(chain.state.midi!.activate!)
                reserved[index] = reserved[index] ?? {}
                reserved[index][chain.id] = <Div k='menu-item midi'><Span k='symbols'>flowchart</Span>&nbsp;{chain.state.name}</Div>
            },{})
        // INDEX chain group activations
        const abgroups: Record<string,number> = {}
        $Chains
            .forEach(chain => {
                Chain.cells(chain)
                    .filter(cell => cell.ab)
                    .forEach(cell => {
                        if (!abgroups[chain.id]) abgroups[chain.id] = 1
                        if (cell.midi) {
                            const index = MidiNote.toString(cell.midi)
                            reserved[index] = reserved[index] ?? {}
                            reserved[index][cell.id] =
                                <Div k='menu-item midi'>
                                    <Span k='symbols'>mediation</Span>&nbsp;{`${chain.state.name} → AB-GROUP #${abgroups[chain.id]}`}
                                </Div>
                        }
                        abgroups[chain.id]++
                    })
            },{})
        // INDEX SESSION by MIDI NOTE
        Object.values($Sessions)
            .filter(({meta:{midi}}) => midi)
            .forEach(session => {
                const index = MidiNote.toString(session.meta.midi!)
                reserved[index] = reserved[index] ?? {}
                reserved[index][session.id] = <Div k='menu-item midi'><Span k='symbols'>dataset</Span>&nbsp;{session.name}</Div>
            },{})

        const muteNote = $AppState.settings.mute.midi
        if (muteNote) {
            const index = MidiNote.toString(muteNote)
            reserved[index] = reserved[index] ?? {}
            reserved[index]['mute'] = <Div k='menu-item midi'><Span k='symbols'>power_settings_new</Span>&nbsp;MUTE</Div>
        }

        const choices = [
            assigned && clear &&
            {
                divider: 'top',
                label: 'Clear',
                choose: () => assign(undefined),
            },
            clear &&
            {
                divider: !assigned ? 'top' : undefined,
                label: 'Clear All..',
                choose: () => this.clearAll(),
            },
        ].filter(_ => _) as IMenuChoice[]

        function channelChoices (channel: number)  {
            let seenSome = false
            return {
                choices: MIDI_OCTAVES
                    .reduce((p:IMenuChoice[],octave) => {
                        // SEEN a assigned note? w/in octave?
                        let seen = false
                        const notes = 
                            MIDI_NOTES
                                .map((_,note) => note)
                                .map(note => note + (octave + 1) * 12)
                                // FILTER out invalid top notes for OCTAVE 9
                                .filter(note => note <= 127)
                                .map(note => MidiNote.fromNumber({note,channel}))
                                .filter(note => note != null)
                        const choices = p[p.length] = {
                            label: `${octave}`,
                            k:['octave'],
                            choices:
                                notes.map(note => {
                                    const $note = MidiNote.toString(note)
                                    // ENTITIES reserving MIDI note
                                    const allReserved = reserved[$note] || {}
                                    const names = Object.values(allReserved).map(node => node)
                                    if (names.length > 0) {
                                        seenSome = true
                                        seen = true
                                    }
                                    const label = `${note.pitch.note}${note.pitch.octave} (${note.note})`
                                    const assignment = assigned && MidiNote.equal(assigned.note,note) ? assigned.label : undefined
                                    const choice: IMenuChoice = {
                                        k: 'note',
                                        label,
                                        choose: () => {
                                            assign(note)
                                            return true
                                        },
                                        selected: assignment ? { k: 'reserved', label: assignment } : undefined,
                                        // DISPLAY other MIDI TARGETS in submenu
                                        choices: names.length === 0 || names.length === 1 && assignment // HIDE if only self
                                            ? undefined
                                            : names.map(name => ({
                                                k: 'midi-name',
                                                label: name,
                                            })),
                                    }
                                    return choice
                                })
                        }
                        if (!seen) {
                            choices.k = ['disabled',...choices.k]
                        }
                        return p
                    },[]),
                seen: seenSome,
            }
        }
        return [...MIDI_CHANS.reduce((p:IMenuChoice[],channel) => {
            const choices = channelChoices(channel)
            p[p.length] = {
                k: !choices.seen && 'disabled',
                label: `${channel}`,
                choices: choices.choices,
            }
            return p
        },[]),...choices]
    }
}

function MidiBrowser (
    {
        chains,
        midiActive,
        sessions,
        app,
        control,
        close,
        embed,
    } :
    {
        chains: TRecChain
        sessions: TRecSession
        app: IAppState
        midiActive: boolean  // True if any MIDI ports are selected
        control: {
            midi: {
                menu: MidiController
            }
            chains: {
                update: (chains:TRecChain) => void
            },
            sessions: {
                update: (sessions: TRecSession) => void
            },
            settings:{
                update: (settings) => void
            }
        }
        close?: () => void
        // EMBED: render just the content (no modal/titlebar/close), for the config dialog tab
        embed?: boolean
    }
) {
    interface TMidiEntity { note?: IMidiNote, label: string, assign: (note?: IMidiNote) => void }
    interface TMidiEntityChain extends TMidiEntity { chain: IChain }
    interface TMidiEntityCell extends TMidiEntity { chain: IChain, cell: IChainCell }
    interface TMidiEntityNode extends TMidiEntity { chain: IChain, node: IModule }
    interface TMidiEntitySess extends TMidiEntity { session: ISession }
    interface TMidiEntitySys extends TMidiEntity { system: 'mute' }

    type TSortChoice = 'name' | 'note' | 'cat'
    type TFilter = 'all' | 'assigned' | 'unassigned'
    type TCat = 'node' | 'cell' | 'chain' | 'session' | 'system'
    type TSort = {
        type: TSortChoice
        descend?: boolean
    }
    const [query,setQuery] = useState<string>()
    const [search,setSearch] = useState<boolean>()
    const [filterNote,setFilterNote] = useState<IMidiNote>()
    const [sort,setSort] = useState<TSort>({type:'name'})
    const [learning,setLearning] = useState<string>()
    const [filter,setFilter] = useState<TFilter>('all')
    // CATEGORY filter — empty set = show all; otherwise only the selected categories
    const [catFilter,setCatFilter] = useState<Set<TCat>>(new Set())
    const ref = useRef<HTMLDialogElement>(null)

    const descend = !!sort.descend

    // INDEX all ENTITIES by MIDI NOTE
    const entities : TMidiEntity[] = []
    const noteIndex : Record<string,number> = {}
    
    function indexNote (note?: IMidiNote) {
        if (note) {
            noteIndex[MidiNote.toString(note)] = noteIndex[MidiNote.toString(note)] == null ? 0 : 1
        }
    }

    useEffect(() => {
        if (!learning) return
        const handleESC = (e:KeyboardEvent) => {
            if (e.key === 'Escape') {
                setLearning(undefined)
                e.stopPropagation()
                e.preventDefault()
            }
        }
        document.addEventListener('keydown', handleESC)
        return () => document.removeEventListener('keydown', handleESC)
    }, [learning])

    widget.useDismissOnEvent({ref,clear:() => close?.()})

    chains.forEach(chain => {
        const note = chain.state.midi?.activate
        indexNote(note)
        // INDEX chain actviation
        entities
            .push({chain,note,
                label: chain.state.name,
                assign: note => {
                    // NO-OP
                    if (!note && chain.state.midi?.activate) {
                        delete chain.state.midi.activate
                        control.chains.update(chains)
                    }
                    else if (note) {
                        if (!chain.state.midi) {
                            chain.state.midi = { activate: note}
                        }
                        else {
                            chain.state.midi.activate = note
                        }
                        control.chains.update(chains)
                    }
                }} as TMidiEntityChain)
        // INDEX chain node activations
        Object
            .values(Chain.indexNodes(chain))
            .forEach(node => {
                const note = node.midi?.activate?.L
                indexNote(note)
                // NOTATE CHANNEL SIDE of SPLIT processor
                const place = SubModule.getPlace(node) ? ` (${SubModule.getPlace(node)})` : ''
                entities
                    .push({chain,node,note,
                        label: `${chain.state.name} → ${node.device.name} ${node.name}${place}`,
                        assign: note => {
                            if (!note && node.midi?.activate?.L) {
                                delete node.midi.activate.L
                                control.chains.update(chains)
                            }
                            else if (note) {
                                if (!node.midi) {
                                    node.midi = { activate: {L : note} }
                                }
                                else {
                                    node.midi.activate = { L: note }
                                }
                                control.chains.update(chains)
                            }
                        }
                    } as TMidiEntityNode)
            })
        // INDEX chain group activations
        Chain.cells(chain)
            .filter(cell => cell.ab)
            .forEach((cell,i) => {
                const note = cell.midi
                indexNote(note)
                entities
                    .push({chain,cell,note,
                        assign: note => {
                            cell.midi = note
                            control.chains.update(chains)
                        },
                        label: `${chain.state.name} → AB-Group ${i+1}`
                    } as TMidiEntityCell)
            })
    })

    entities
        .push({system: 'mute',
            note: app.settings.mute.midi,
            assign: note => {
                control.settings.update({
                    mute: {
                        ...app.settings.mute,
                        midi: note
                    }
                })
            },
            label: 'MUTE',
        } as TMidiEntitySys)

    indexNote(app.settings.mute.midi)

    Object.values(sessions)
        .forEach(session => {
            const note = session.meta.midi
            indexNote(note)
            entities
                .push({session,note,
                    assign: note => {
                        session.meta.midi = note
                        control.sessions.update(sessions)
                    },
                    label: session.name,
                } as TMidiEntitySess)
        })

    function tokenize (s: string) : string[] {
        return s.split(/\W+/).map(_ => _.toLowerCase())
    }

    const index = new SearchIndex<TMidiEntity>(
        ({note,node,chain,system,session}) => [
                node && Module.tokenize(node),
                chain && Chain.tokenize(chain),
                system && tokenize(system),
                session && tokenize(session.name),
                note && [
                    note.pitch.spn.toLowerCase(),
                    `${note.number}`,
                    `${note.channel}`,
                ],
            ]
            .flat()
            .filter(_ => _),
        entities)

    const found = query
        ? index
            .find(query)
            .flat()
            .reduce((p,entity) => {
                p.add(entity)
                return p
            },new Set<TMidiEntity>())
        : null

    // APPLY NAME sort w/in INTER-TYPE
    function nameSort (a,b) {
        return descend
            ? Names.sorter(b.label,a.label)
            : Names.sorter(a.label,b.label)
    }

    /**
     * FILTER and SORT system entities
     */
    const hits = (found ? [...found] : entities)
        .filter(({note}) => !filterNote || filter === 'unassigned' || MidiNote.equal(note,filterNote))
        .filter(({note}) => filter === 'all' || note && filter === 'assigned' || !note && filter === 'unassigned')
        .filter(entity => !catFilter.size || catFilter.has(entityCat(entity)))
        .sort(sort.type === 'name'
            ? (a,b) => Names.sorter(a.label,b.label)
            : sort.type === 'note'
            ? (a,b) => {
                const r = MidiNote.compare(a.note,b.note)
                // SORT by NAME w/in SAME NOTE
                return r ? r : nameSort(a,b)
            }
            // CATEGORY
            : (a:any,b:any) => {
                if (a.node || b.node) {
                    if (!b.node) return 1
                    else if (!a.node) return -1
                }
                else if (a.cell || b.cell) {
                    if (!b.cell) return 1
                    else if (!a.cell) return -1
                }
                else if (a.chain || b.chain) {
                    if (!b.chain) return 1
                    else if (!a.chain) return -1
                }
                else if (a.session || b.session) {
                    if (!b.session) return 1
                    else if (!a.session) return -1
                }
                else if (a.system || b.system) {
                    if (!b.system) return 1
                    else if (!a.system) return -1
                }
                // SORT by NAME
                return nameSort(a,b)
            }
        )
    
    if (descend) hits.reverse()

    function entityCat (entity: TMidiEntity) : TCat {
        const {node,cell,chain,session} = entity as any
        return node ? 'node' : cell ? 'cell' : chain ? 'chain' : session ? 'session' : 'system'
    }

    function entityId (entity: TMidiEntity) : string {
        const {node,cell,chain,session,system} = entity as any
        return cell ? cell.id
            : node ? node.id
            : chain ? chain.id
            : session ? session.id
            : system ? system
            : 'UNKNOWN'
    }

    function makeRow (entity:TMidiEntity) {
        const {note,assign,label} = entity
        const {node,chain,system,cell,session} = entity as any
        const icon = 
            node
            ? <NodeIcon size={14}/>
            : cell
            ? <Div k='symbols'>mediation</Div>
            : chain
            ? <Div k='symbols'>flowchart</Div>
            : session
            ? <Div k='symbols'>dataset</Div>
            : system
            ? <Div k='symbols'>settings</Div>
            : '???'

        const id = entityId(entity)
        const $note = note ? MidiNote.toString(note) : '-'
        return (
            <Div k={['entity',learning === id && ['learning']]}>
                <Div k={['note',noteIndex[$note] && 'shared']}>{$note}</Div>
                <Div k='cat'>{icon}</Div>
                <Div k='name'>{label}</Div>
                <TriPop
                    k='actions'
                    items={[
                        { label: 'Learn...',
                            disabled: !midiActive,
                            choose: () => {
                                setLearning(id)
                                $midiOneShot = (note: IMidiNote) => {
                                    D('ASSIGNED',note)
                                    assign(note)
                                    setLearning(undefined)
                                }
                            }
                        },
                        { label: 'Assign',
                            choices: () => control.midi.menu.choices({
                                assigned: note ? { note, label } : undefined,
                                assign,
                                clear: false,
                            })
                        },
                        { label: 'Clear', disabled: !note, choose: () => assign(undefined) },
                        { label: 'Clear All...', choose: () => control.midi.menu.clearAll() },
                        { label: 'Filter by note', disabled: !note, choose: () => note && setFilterNote(note) },
                    ]}
                />
            </Div>)
    }

    function makeHeader () {
        function order (type: string) {
            const active = type === sort.type
            return <Span k={['order',active && sort.descend ? 'north' : 'south']}/>
        }
        function choose (type: TSortChoice) {
            if (sort.type !== type) {
                setSort({type})
            }
            else {
                setSort({type: sort.type, descend: !sort.descend})
            }
        }

        function ks (type:TSortChoice) {
            return ['header',type,type === sort.type && 'active']
        }

        return (
            <Div k='headers'>
                <Div k={ks('note')} {...onClick(() => choose('note'))}>
                    {order('note')}
                    <Span k='symbols'>music_note</Span>
                </Div>
                <Div k={ks('cat')} {...onClick(() => choose('cat'))}>
                    {order('cat')}
                    <Span k='symbols'>category</Span>
                </Div>
                <Div k={ks('name')} {...onClick(() => choose('name'))}>
                    {order('name')}
                    NAME
                </Div>
            </Div>
        )
    }

    /**
     * CHOOSE next filter
     */
    function nextFilter () : TFilter {
        return filter === 'all'
            ? 'assigned'
            : filter === 'assigned'
            ? 'unassigned'
            : 'all'
    }

    const content = (
        <>
            <Div k='search'>
                <Div k={['icon','search']}>
                    <Span k='symbols'>search</Span>
                </Div>
                <SearchBox
                    k={['midi-search','searchbox']}
                    value={query}
                    onBlur={() => setSearch(false)}
                    onEdit={query => {
                        setSearch(true)
                        setQuery(query)
                    }}
                />
                {!search && query &&
                    <Div k={['icon','reset']}>
                        <Span k='symbols' {...onClick(() => setQuery(undefined))}>close</Span>
                    </Div>
                }
            </Div>
            <Div k={['header']}>
                {/* VIEW FILTERS/OPTIONS */}
                <Div k='left'>
                    <Div k={['filter']} onClick={() => setFilter(nextFilter())}>
                        {filter === 'all' ? 'ALL' : filter === 'assigned' ? 'ASSIGNED' : 'UNASSIGNED'}
                    </Div>
                    {embed &&
                        <Div k='cat-filters'>
                            {([
                                ['node', <NodeIcon size={13}/>],
                                ['cell', <Span k='symbols'>mediation</Span>],
                                ['chain', <Span k='symbols'>flowchart</Span>],
                                ['session', <Span k='symbols'>dataset</Span>],
                                ['system', <Span k='symbols'>settings</Span>],
                            ] as [TCat, React.ReactNode][]).map(([cat,icon]) =>
                                <Div
                                    key={cat}
                                    k={['cat-filter', catFilter.has(cat) && 'active']}
                                    {...onClick(() => {
                                        const next = new Set(catFilter)
                                        next.has(cat) ? next.delete(cat) : next.add(cat)
                                        setCatFilter(next)
                                    })}
                                >
                                    {icon}
                                </Div>
                            )}
                        </Div>
                    }
                    {filterNote &&
                        <Div k={['note',filter === 'unassigned' && 'disabled']} {...onClick(() => setFilterNote(undefined))}>
                            {MidiNote.toString(filterNote)}
                        </Div>
                    }
                </Div>
                <Div k='right'>
                </Div>
            </Div>
            <Div>
                {makeHeader()}
                <Div k='entities'>
                    {hits.length
                        ? hits.map(makeRow)
                        : <Div k='noresults'>No results for chosen query/filter</Div>
                    }
                </Div>
            </Div>
        </>
    )

    // EMBEDDED (config dialog MIDI tab): content only, no modal/titlebar/close
    if (embed) {
        return (
            <Div k={['midi-browser','embed']} s={learning && {pointerEvents:'none'}}>
                {content}
            </Div>
        )
    }

    return (
        <Div k='modal'>
            <Div k={['midi-browser','dialog']}>
                <Div k='titlebar'>MIDI Manager
                    <Div k='dismiss' {...onClick(() => close?.())}>close</Div>
                </Div>
                <Div k={['body','midi-browser']} s={learning && {pointerEvents:'none'}}>
                    {content}
                    <Div k='actions' s={{pointerEvents: 'all'}}>
                            <Div k='cta primary' {...onClick(() => close?.())}>
                                CLOSE
                            </Div>
                    </Div>
                </Div>
            </Div>
    </Div>)
}

/**
 * MIDI PORT selector — chip row for the config dialog MIDI tab.
 * Mirrors the 3-dot menu "MIDI ▸ Port" logic: 'ANY' clears the rest,
 * selecting a real port clears 'ANY', and a selected-but-disconnected
 * port is flagged in the 'bad' state.
 */
function MidiPortSelector (
    {
        ports,
        selected,
        setSelected,
    } :
    {
        ports: TMidiPortRec
        selected: Map<string,TMidiPort>
        setSelected: (ports: Map<string,TMidiPort>) => void
    }
) {
    const anyPort = ports['ANY'] ?? { id: 'ANY', name: '<any>' }
    const realConnected = Object.values(ports).filter(p => p.id !== 'ANY')
    const disconnectedSelected = Array.from(selected.values())
        .filter(p => p.id !== 'ANY' && !ports[p.id])
    const ordered: TMidiPort[] = [anyPort, ...realConnected, ...disconnectedSelected]

    function toggle (port: TMidiPort) {
        const isSelected = selected.has(port.id)
        const isAny = port.id === 'ANY'
        const next = new Map(selected)
        if (isSelected) {
            next.delete(port.id)
        } else if (isAny) {
            next.clear()
            next.set('ANY', port)
        } else {
            next.delete('ANY')
            next.set(port.id, port)
        }
        setSelected(next)
    }

    return (
        <Div k='midi-ports'>
            <Div k='section-label'>MIDI PORTS</Div>
            <Div k='ports'>
                {ordered.map(port => {
                    const isSelected = selected.has(port.id)
                    const isAny = port.id === 'ANY'
                    const isConnected = isAny || !!ports[port.id]
                    return (
                        <Div
                            key={port.id}
                            k={['port', isSelected && isConnected && 'on', isSelected && !isConnected && 'bad']}
                            {...onClick(() => toggle(port))}
                        >
                            <Span k={['check','symbols']}>check</Span>
                            {isAny ? 'Any port' : port.name}
                            {isSelected && !isConnected && <Span k='tag'>(disconnected)</Span>}
                        </Div>
                    )
                })}
                {selected.size > 0 &&
                    <Div k='clear' {...onClick(() => setSelected(new Map()))}>CLEAR</Div>
                }
            </Div>
        </Div>
    )
}

class ModuleController {
    static create (
        {midi,setDevice,chain,node,controller}
        :
        {
            chain: IChain
            node: IModule
            midi: {
                activate: ({chain,node,note}:{chain:string,node:string,note?:IMidiNote}) => void
            }
            controller: {
                midi: IMidiController
            }
            setDevice? (id: string)
        })
    : IModuleController 
    {
        const note = node.midi?.activate?.L
        const assignment = note
        return {
            gotoDevice () {
                const device = $Devices[node.device.id]
                if (device) {
                    setDevice && setDevice(device.id)
                }
            },
            midi: {
                assignment,
                choices () {
                    return controller.midi.choices({
                        assign: (note?:IMidiNote) => midi.activate({chain:chain.id,node:node.id,note}),
                        assigned: assignment
                        ?  {
                            label: `${chain.state.name} → ${node.device.name} ${node.name}`,
                            note: assignment
                        }
                        : undefined,
                    })
                },
            }
        }
    }
}

function ChainBrowser (
    {
        chains:$$chains=[],
        searchState,
        controllers,
        chainFilter,
        arch,
    }
    : {
        chains: IChain[]
        searchState?: boolean
        controllers: {
            chain: IChainCardController
            midi: IMidiController
            browser: {
                chainSearch: (query:string) => void
                setSearch: (state:boolean) => void
            }
        }
        chainFilter?: SearchHits
        arch: IArch
    })
{
    // ACTIVE/INACTIVE filter state
    type TChainActiveFilter = 'active'|'inactive'|undefined

    class ChainActiveFilter {
        // NEXT filter state
        // ( null -> active -> inactive -> null )
        static nextState (state: TChainActiveFilter) : TChainActiveFilter {
            return !state
            ? 'active'
            : state  === 'active'
            ? 'inactive'
            : undefined
        }
    }
    interface AttrFilter {
        active?:TChainActiveFilter
        sort?:boolean
        fave?:boolean
        stereo?:boolean
        normal?:boolean
    }

    const [attrFilter,setAttrFilter] = useState<AttrFilter>({})
    const [$keywords,setKeywords] = useState<StatedTags>({})

    // FILTERED chains (first level filter - tags is next)
    const $chains = $$chains.filter(({id}) => chainFilter?.hits && chainFilter.hits[id] || !chainFilter?.hits)

    // XXX this could be made more efficient using indexes
    const chains = $chains
        .filter(chain =>
            (!attrFilter.fave && !attrFilter.stereo && !attrFilter.normal && !attrFilter.active)
            || (attrFilter.fave && chain.state.fave)
            || (attrFilter.active === 'active' && chain.cond.active)
            || (attrFilter.active === 'inactive' && !chain.cond.active)
            || (attrFilter.normal && chain.state.normal)
        )

    const control = controllers.chain
    const {browser} = controllers

    // COLLECT all tag names
    const keywords:StatedTags = {
        ...chains
        .reduce((p,{state:{tags}}) => {
            tags.forEach(tag => {
                p[tag.name] = {tag:tag.name,selected: false}
                // FOLD-IN existing filter tag states (if found)
                if ($keywords[tag.name]) {
                    p[tag.name] = $keywords[tag.name]
                }
            })
            return p
        },{}),
    }

    // GATHER all TAG FILTERS
    const tagFilter = Object
        .entries(keywords)
        // DISABLE tag filter if no respective keywords selected
        .every(([_,{selected}]) => !selected) ? undefined : keywords

    // FILTER chains
    const filtered = 
        chains
            .sort((a,b) => Names.sorter(a.state.name,b.state.name))
            .filter(({state:{tags}}) => !tagFilter || tags.some(tag => tagFilter[tag.name]?.selected))
            // REVERSE sort?
            .sort(() => attrFilter.sort ? -1 : 0)

    return (
        <Div id="browser" k={['browser','chain']}>
            <Div k="search">
                <Div k="icon">
                    <span className="symbols">search</span>
                </Div>
                <SearchBox 
                    k='searchbox'
                    // FORCE edit mode (focus) for current item type if container set search active
                    editMode={searchState}
                    // NOTIFY controller when search is active (as set by container) and focus was lost
                    onBlur={()=>searchState && browser.setSearch(false)}
                    onEdit={browser.chainSearch}
                />
            </Div>
            <FilterTags
                tags={keywords} 
                onSelect={
                    kw => {
                        if (keywords && keywords[kw]) {
                            keywords[kw].selected = !keywords[kw].selected
                            setKeywords(keywords)
                        }
                    }
                }
            />
            <Div k="tag-hr"/>
            <Div k={["header",'chain']}>
                {/* VIEW FILTERS/OPTIONS */}
                <Div k='left'>
                    <Div k={['sort']}
                        onClick={() => {
                            attrFilter.sort = !attrFilter.sort
                            setAttrFilter({...attrFilter})
                        }}>
                        A-Z
                        {attrFilter.sort
                            ? <span className="symbols">north</span>
                            : <span className="symbols">south</span>
                        }
                    </Div>
                </Div>
                <Div k='right'>
                    <Div k={['active',attrFilter.active && 'selected']}
                        onClick={() => {
                            attrFilter.active = ChainActiveFilter.nextState(attrFilter.active)
                            setAttrFilter({...attrFilter})
                        }}>
                        {
                            attrFilter.active === 'inactive'
                            ? 'INACTIVE'
                            : attrFilter.active === 'active'
                            ? 'ACTIVE'
                            : 'ALL'
                        }
                    </Div>
                    <Div k={['normal',attrFilter.normal && 'selected','acticon']}
                        onClick={() => {
                            attrFilter.normal = !attrFilter.normal
                            setAttrFilter({...attrFilter})
                        }}>N
                    </Div>
                    <Div k={['fave',attrFilter.fave && 'selected','acticon']}
                        onClick={() => {
                            attrFilter.fave = !attrFilter.fave
                            setAttrFilter({...attrFilter})
                        }}>
                        {attrFilter.fave
                            ? <span className="mui-icons">bookmark</span>
                            : <span className="mui-icons">bookmark_border</span>
                        }
                    </Div>
                </Div>
            </Div>
            <Div k="results">
                <Div k="items">
                    {
                        filtered.length
                        ? filtered
                                .map(chain => {
                                    return <Div
                                        key={chain.id}
                                        k='item'
                                        onClick={e => {
                                            // HIDDEN - show in chain view
                                            if (chain.cond.hidden) {
                                                // LEAVE any targeted chain visible if shift+click
                                                control.show(chain.id,!!e.shiftKey)
                                            }
                                            // VISIBLE - select
                                            else if (!chain.cond.selected) {
                                                control.select(chain.id)
                                            }
                                            e.stopPropagation()
                                            e.preventDefault()
                                        }}
                                    >
                                        <BrowserChain
                                            arch={arch}
                                            chain={chain}
                                            control={{
                                                set: (state,cond) => control.modify({id:chain.id,state,cond}),
                                                name: name => control.name(chain.id,name),
                                                color: color => control.color(chain.id,color),
                                                hide: () => control.hide(chain.id),
                                                show: replace => control.show(chain.id,replace),
                                                remove: () => control.remove(chain.id),
                                                active: ()  => control.active(chain.id),
                                                focus: () => control.focus(chain.id,!chain.cond.focus),
                                                fave: () => control.fave(chain.id),
                                                lock: () => control.lock(chain.id),
                                                normal: () => control.normal(chain.id),
                                                copy: () => control.copy(chain.id,Identity.create),
                                                alert: args => control.alert(args),
                                                bypass: 
                                                    (chain.cond.bypass || Chain.activeNodes(chain).some(_ => _.proc))
                                                    ? () => control.bypass(chain.id)
                                                    : undefined,
                                                midi: {
                                                    assignment: chain.state.midi?.activate,
                                                    choices: () =>
                                                        controllers.midi.choices({
                                                            assign: note => control.modify({id:chain.id,state:{midi:{activate:note}}}),
                                                            assigned: chain.state.midi?.activate
                                                                ? {
                                                                    note: chain.state.midi?.activate,
                                                                    label: chain.state.name,
                                                                } : undefined
                                                        })
                                                },
                                            } as IChainBoxController }
                                        />
                                    </Div>
                                }
                                )
                        : $$chains.length
                        ? <Div k="item empty">No matches found</Div>
                        : <Div k="item empty">No chains defined</Div>
                    }
                    <Div k='item'>&nbsp;</Div>
                </Div>
            </Div>
        </Div>
    )
}

/**
 * LEFT-HAND pane device browser
 */
function DeviceBrowser (
    {
        devices:$devices,
        selected,
        searchState,
        onChoose,
        onDelete,
        controllers,
    }
    : {
        devices: TRecDeviceDef
        selected?: IDeviceDef
        searchState?: boolean
        onChoose: (device:IDeviceDef) => void
        onDelete: (device:IDeviceDef) => void
        controllers: {
            browser: {
                setSearch: (state:boolean) => void
            }
        }
    })
{
    const [deviceFilter,setDeviceFilter] = useState<Record<string,IDeviceDef>|undefined>()
    const [$keywords,setKeywords] = useState<StatedTags>({})
    // ACTIVE DEVICE result formats (e.g. sort order)
    const [itemStates,setItemStates] = useState<DeviceItemsState>( { sort:false })

    interface DeviceItemsState { sort: boolean }

    const {browser} = controllers

    const devices = Object.values($devices)

    /**
     * COLLECT all "effective" tags from Device instance
     * (gathers tags of child modules)
     */
    function extractTags (device:IDeviceDef) : string[] {
        const tags = Object.values(device.nodes)
            .filter(({device:{id}}) => id === device.id)
            .reduce((p,mod) => {
                mod.tags.forEach(tag => p[tag.name] = tag.name)
                return p
            },{})
        return Object.values(tags)
    }

    // GENERATE "stateful" tags from ALL devices
    const keywords:StatedTags = {
        ...devices
        .map(extractTags)
        .reduce((p,tags) => {
            tags.forEach(tag => {
                p[tag] = {tag:tag,selected: false}
                // FOLD-IN existing filter tag states (if found)
                if ($keywords[tag]) {
                    p[tag] = $keywords[tag]
                }
            })
            return p
        },{}),
    }

    // GATHER all TAG FILTERS
    const tagFilter = Object
        .entries(keywords)
        // DISABLE tag filter if no respective keywords selected
        .every(([_,{selected}]) => !selected) ? undefined : keywords

    // CREATE list of searchable tokens for device
    function tokenize (device:IDeviceDef) : string[] {
        return [
            ...device.name.split(/\W+/),
            ...extractTags(device),
        ].map(_ => _.toLowerCase())
    }

    const deviceIndex = new SearchIndex<IDeviceDef>(tokenize,devices)
    const deviceSearch = (query:string) => {
        const hits = deviceIndex
            .find(query)
            .reduce((p:Record<string,IDeviceDef>|undefined,c:any) => {
                p = p||{}
                c.forEach((device:IDeviceDef) => p[device.id] = device)
                return p
            },{})

        setDeviceFilter(hits)
        return query
    }
    
    // FILTER by search query and tags
    const filtered:IDeviceDef[] =
        // FILTERED by current search query
        DeviceDef
        // FILTERED by current search query
        .sortByName(devices.filter(({id}) => !deviceFilter || deviceFilter[id]))
        // FILTER by active tag(s)
        .filter(device => !tagFilter || extractTags(device).some(tag => tagFilter[tag]?.selected))
        // REVERSE sort?
        .sort(() => itemStates.sort ? -1 : 0)

    const $selected = selected ?? filtered[0]
    if (!selected && $selected) {
        setTimeout(() => onChoose($selected))
    }

    return (
        <Div k={['browser','device']} id="browser">
            <Div k="search">
                <Div k="icon">
                    <span className="symbols">search</span>
                </Div>
                <SearchBox 
                    k='searchbox'
                    // FORCE edit mode (focus) for current item type if container set search active
                    editMode={searchState}
                    // NOTIFY controller when search is active (as set by container) and focus was lost
                    onBlur={()=>searchState && browser.setSearch(false)}
                    onEdit={deviceSearch}
                >
                </SearchBox>
            </Div>
            <FilterTags
                tags={keywords} 
                onSelect={
                    kw => {
                        if (keywords && keywords[kw]) {
                            keywords[kw].selected = !keywords[kw].selected
                            setKeywords(keywords)
                        }
                    }
                }
            />
            <Div k="tag-hr"/>
            <Div k={["header",'device']}>
                {/* VIEW FILTERS/OPTIONS */}
                <Div k={['sort']}
                    onClick={() => {
                        itemStates.sort = !itemStates.sort
                        setItemStates({...itemStates})
                    }}>
                    A-Z
                    {itemStates.sort
                        ? <span className="mui-icons">north</span>
                        : <span className="mui-icons">south</span>
                    }
                </Div>
            </Div>
            <Div k="results">
                <Div k="items">
                    {
                        // DEVICES partitioned by TAG name
                        filtered.length
                        ?  filtered
                            .map(device=> 
                                <Div k='item' key={device.id}>
                                    <BrowserDevice
                                        selected={$selected?.id === device.id}
                                        onChoose={() => {
                                            onChoose($Devices[device.id])
                                        }}
                                        onDelete={() => onDelete(device)}
                                        key={device.id}
                                        device={device}
                                    />
                                </Div>
                            )
                        : devices.length
                        ? <Div k="empty">No matches found</Div>
                        : <Div k="empty">No devices defined</Div>
                    }
                    <Div k='item'>&nbsp;</Div>
                </Div>
            </Div>
        </Div>
    )
}

const $Arch = OP_PARAMS.ARCH

/**
 * LEFT-HAND pane node browser
 */
function NodeBrowser (
    {
        devices,
        chains,
        modType='proc',
        searchState,
        controllers,
        arch,
    }
    : {
        devices: TRecDeviceDef
        chains: IChain[]
        modType: TRoleType
        searchState?: boolean
        controllers: {
            chain: IChainCardController
            module: IModuleDefController
            browser: {
                dragSeer: IDragObserver
                setItemType: (type:TRoleType) => void
                setSearch: (state:boolean) => void
                setDevice: (id:string) => void
            }
        },
        arch: IArch,
    })
{
    // ACTIVE/INACTIVE filter state
    type TModuleItemsActiveState = 'active'|'inactive'|undefined

    interface IModuleItemsState {
        sort: boolean
        active: TModuleItemsActiveState 
    }

    class ModuleItemsState {
        // NEXT filter state
        // ( null -> active -> inactive -> null )
        static nextActiveState (state: TModuleItemsActiveState) : TModuleItemsActiveState {
            return !state
            ? 'active'
            : state  === 'active'
            ? 'inactive'
            : undefined
        }
    }

    const modules = DeviceDef.extractNodes(devices)

    const {browser} = controllers

    const ModuleItemTypes: TRoleType[]  = ['proc','source','dest']

    const [moduleFilter,setModuleFilter] = useState<Record<string,Record<string,IModule>>>({})
    const [$keywords,setKeywords] = useState<TypedTags>({})
    const [moduleStates,setModuleStates] = useState<Record<string,ModuleState>>({})
    // MODULE result states (e.g. sort order)
    const [itemStates,setItemStates] = useState<Record<string,IModuleItemsState>>(
        ModuleItemTypes.reduce((p,c) => {
            p[c] = {
                sort:false,
                active:false,
            }
            return p
        },{})
    )

    const roleFilter = (v:IModuleDef) : boolean => ModuleRole.toType(v.role) === modType

    // COLLECT all tag for role
    const tags: Record<string,ITag> = modules
        .filter(roleFilter)
        .reduce((p,{tags}) => {
            tags.forEach(tag => p[tag.name] = tag)
            return p
        },{})

    // REMOVE all missing keywords (i.e. active filtered tag has gone missing)
    if ($keywords[modType]) {
        // REMOVE any tags not present in current keyword set
        Object.keys($keywords[modType]).forEach(tag => {
            if (!tags[tag]) {
                delete $keywords[tag]
            }
        })
    }

    // COLLECT all tag names from modules, for current item type
    // XXX this should be `tagFilter`, really. Not sure it helps to partition by item type
    const keywords:TypedTags = Object.assign({
        [modType]:
            Object.values(tags)
                .reduce((p,tag) => {
                    p[tag.name] = { tag:tag.name, selected: 0 }
                    return p
                },{})
        },
        $keywords)

    // GATHER all TAG FILTERS for current item type
    const tagFilter = Object
        .entries(keywords[modType])
        // DISABLE tag filter if no respective keywords selected
        .every(([_,{selected}]) => !selected) ? undefined : keywords[modType]

    const searchClass:Record<string,string[]> = [...ModuleItemTypes]
        .reduce((p,c) => {
            p[c] = c === modType ? ['searchbox'] : ['searchbox','hidden']
            return p
        },{})

    const moduleIndex = new SearchIndex<IModule>(Module.tokenize,Module.fromDefs(modules,arch))
    const moduleSearch = (query:string) => {
        const hits =
            moduleIndex
            .find(query)
            .reduce((p:Record<string,IModule>|undefined,c:any) => {
                p = p||{}
                c.forEach((module:IModule) => p[module.id] = module)
                return p
            },{})
        setModuleFilter({...Object.assign(moduleFilter,{[modType]: hits})})
        return query
    }
    
    const BrowseChoice = ({id,name}) => {
        const size = '18px'
        return <Div k={['choice', modType === id && 'selected']}
            onMouseDown={_ => browser.setItemType(id)}>
            <Div k='item'>{name}</Div>
        </Div>
    }

    // INDEX all in-use modules
    // utilize the "visual" model for this as it reflects the logical status of any module
    // effectively bypassed because its upstream source was explicity bypassed
    const activeModules : Record<string,{id:string,color:string}> = 
        chains
        .filter(({cond:{active}}) => active)
        .reduce((p,chain) => {
            const {id,state:{color='red'}} = chain
            getActiveModules(chain,$Arch)
                // INDEX chain ID (and its assigned color) by active module ID
                .forEach(module => p[module.id] = {id,color})
            return p
        },{})

    // BROWSER ITEM TYPES
    const items = {
        source: 'SOURCES',
        proc: 'PROCESSORS',
        dest: 'DESTINATIONS',
    }

    // FILTER modules by role and tags
    const filteredModules:IModuleDef[] =
        ModuleDef
        // FILTERED by current search query
        .sortByName(modules.filter(({id}) => !moduleFilter[modType] || moduleFilter[modType][id]))
        // FILTER by IN-USE status
        .filter(roleFilter)
        // FILTER by active tag(s)
        .filter(({tags}) => {
            return !tagFilter || tags.some(tag => tagFilter[tag.name]?.selected)
        })
        .filter(module => {
            // ACTIVE/INACTIVE filter is DISABLED 
            if (!itemStates[modType].active) return 1
            // SPLIT stereo module, in L or R side in-use independently
            const {L:left,R:right} = module.phonia.stereo && SubModule.split(Module.fromDef(module,arch)!) || {}
            const active = left?.id && activeModules[left.id]
                || right?.id && activeModules[right.id]
                || activeModules[module.id]
            return active
                ? itemStates[modType].active === 'active'
                : itemStates[modType].active === 'inactive'
        })
        // REVERSE sort?
        .sort(() => itemStates[modType].sort ? -1 : 0)

    return (
        <Div k={['browser','module']} id="browser">
            <Div k="search">
                <Div k="icon">
                    <span className="symbols">search</span>
                </Div>
                {Object.keys(items)
                    .map(type => 
                        <SearchBox 
                            key={type}
                            k={searchClass[type]}
                            // FORCE edit mode (focus) for current item type if container set search active
                            editMode={searchState && modType === type}
                            // NOTIFY controller when search is active (as set by container) and focus was lost
                            onBlur={()=>searchState && modType === type && browser.setSearch(false)}
                            onEdit={moduleSearch}
                        />
                    )
                }
            </Div>
            <Div id="types" k="types">
                {Object
                    .entries(items)
                    .map(([id,name]) => <BrowseChoice name={name} key={id} id={id}/>)}
            </Div>
            <FilterTags
                tags={keywords[modType]} 
                onSelect={
                    kw => {
                        if (keywords[modType] && keywords[modType][kw]) {
                            keywords[modType][kw].selected = !keywords[modType][kw].selected
                            setKeywords(keywords)
                        }
                    }
                }
            />
            <Div k="tag-hr"/>
            <Div k={["header",'module']}>
                {/* VIEW FILTERS/OPTIONS */}
                {
                    ModuleItemTypes.includes(modType)
                    ? <>
                        <Div k='left'>
                            <Div k={['sort']}
                                onClick={() => {
                                    itemStates[modType].sort = !itemStates[modType].sort
                                    setItemStates({...itemStates})
                                }}>
                                A-Z
                                {itemStates[modType].sort
                                    ? <span className="symbols">north</span>
                                    : <span className="symbols">south</span>
                                }
                            </Div>
                        </Div>
                        <Div k='right'>
                            <Div k={['active',itemStates[modType].active && 'selected']}
                                onClick={() => {
                                    itemStates[modType].active = ModuleItemsState.nextActiveState(itemStates[modType].active)
                                    setItemStates({...itemStates})
                                }}>
                                {
                                    itemStates[modType].active === 'inactive'
                                    ? 'INACTIVE'
                                    : itemStates[modType].active === 'active'
                                    ? 'ACTIVE'
                                    : 'ALL'
                                }
                            </Div>
                        </Div>
                    </>
                    : null
                }
            </Div>
            <Div k="results">
                <Div k="items">
                    {
                        // MODULES partitioned by TAG name
                        filteredModules.length
                        ?  filteredModules
                            .map(module => 
                                <Div key={module.id}>
                                    <BrowserNode 
                                        arch={$Arch}
                                        control={{ module: controllers.module }}
                                        setDevice={controllers.browser.setDevice}
                                        // DOUBLE-CLICK-TO-ADD
                                        onChoose={({module,onRender:$onRender}) => {
                                            const selected = chains.filter(({cond:{selected}}) => selected)[0]
                                            if (selected) {
                                                const control = controllers.chain
                                                const inserted = control.insertItem(selected.id,module)
                                                if (inserted) {
                                                    // ENFORCE single invocation of render hook
                                                    let fired = false
                                                    const {chain,cell,item,channel} = inserted
                                                    const onRender = !$onRender
                                                        ? undefined
                                                        : (event:IMatrixRenderEvent) => {
                                                            if (!fired
                                                                    && event.chain === chain
                                                                    && event.cell === cell
                                                                    && event.item === item
                                                                    && event.channel === channel)
                                                            {
                                                                fired = true
                                                                $onRender(event.pos)
                                                                // UNREGISTER "render" event listener
                                                                control.unlisten(chain,onRender!)
                                                            }
                                                        }
                                                    // REGISTER "render" event listener
                                                    if (onRender) control.listen(chain,onRender)
                                                }
                                            }
                                        }}
                                        // ALT+CLICK-TO-ISOLATE: copy this palette node into a fresh chain
                                        onIsolate={(module) => {
                                            const control = controllers.chain
                                            const created = control.addChain({replace:false})
                                            control.insertItem(created.id,module)
                                        }}
                                        key={module.id}
                                        module={module}
                                        dragSeer={browser.dragSeer}
                                        onExpand={
                                            module.phonia.stereo
                                            ? () => {
                                                const state = moduleStates[module.id] || {}
                                                state.expanded = !state.expanded
                                                moduleStates[module.id] = state
                                                setModuleStates({...moduleStates})
                                            }
                                            : undefined
                                        }
                                        state={moduleStates[module.id]}
                                        active={activeModules}
                                    />
                                </Div>
                            )
                        : modules.length
                        ? <Div k="empty item">No matches found</Div>
                        : <Div k="empty item">No valid device modules defined</Div>
                    }
                </Div>
            </Div>
        </Div>
    )
}

/**
 * VISUALIZE incoming MIDI EVENT
 */
function MidiEventPopup ({event}:{event:IMidiNoteEvent|undefined}) {
    return event && <Div k='midi-event' key={`${event.time}`}>
        <Div k='note'>{event.pitch.spn}</Div>
        <Div k='channel'>CHAN {event.channel}</Div>
        <Div k='state'>{event.on ? `ON (${event.velocity})` : 'OFF'}</Div>
        <Div k='ts'>{event.time}</Div>
    </Div>
}

/**
 * For given name prefix GENERATE a unique SESSION name (e.g. `default copy 1`)
 * @param sessions 
 * @param prefix 
 * @returns 
 */
function findUniqueSessionName (sessions:TRecSession, name:string) {
    // INDEX all DEVICE names
    const names = Object.values(sessions).reduce((p,{name}) => {
        p[name] = name
        return p
    },{})
    const $suffix = name.match(/^(.+) (\d+)$/)
    const baseName = $suffix ? $suffix[1] : name
    let suffix = $suffix ? +$suffix[2] : 0
    // DERIVE unique title
    do {
        name = `${baseName} ${++suffix}`
    } while (names[name])
    return name
}


// INITIALIZE REMOTE STORE
const remoteStore: RemoteStore | undefined = (() => {
    try {
        const unit = OP_PARAMS.UNIT
        if (unit && OP_PARAMS.API_STORE && URL.canParse(OP_PARAMS.API_STORE)) {
            return new RemoteStore({
                basepath:`propatch/${unit}`,
                url: new URL(OP_PARAMS.API_STORE),
            })
        }
    }
    catch (e) {
        W('ERROR initializing RemoteStore',e)
    }
})()

// INITIALIZE LOCAL STORE
const localStore: store.LocalStore | undefined = (() => {
    try {
        const unit = OP_PARAMS.UNIT ?? 'default'
        return new store.LocalStore({
            basepath:`${unit}`
        })
    }
    catch (e) {
        W('ERROR initializing LocalStore',e)
    }
})()

/**
 * LOAD all known sessions, remote first, local as fallback
 */
function loadSessions () : Promise<TRecSession> {
    // ATTEMPT REMOTE (API) session LOAD
    const sessions = remoteStore && remoteStore.loadSessions()
    // FALLBACK to local store
    if (!sessions) {
        const stored = LocalStore.loadSessions()
        if (stored) {
            try {
                return Promise.resolve(stored)
            } catch (e) {
                E('FATAL ERROR LOADING SNAPSHOTS',e)
                return Promise.resolve({})
            }
        }
        else {
            // NO LOCALLY STORED SNAPSHOTS
            return Promise.resolve({})
        }
    }
    else {
        return sessions
    }
}

// Gap-based drop positioning for chain cards
interface DropGap { x: number, y: number, height: number, index: number }

// Find drop position based on cursor location over cards
// Hovering left half of a card = insert before, right half = insert after
function findDropGap(container: HTMLElement, draggedId: string, cursorX: number, cursorY: number): DropGap | null {
    const cards = Array.from(container.querySelectorAll('.chain-card')) as HTMLElement[]
    const remaining = cards.filter(el => el.dataset.chainId !== draggedId)

    if (!remaining.length) {
        const rect = container.getBoundingClientRect()
        return { x: rect.left + 20, y: rect.top + 20, height: 100, index: 0 }
    }

    // First, find all cards in the same row (matching Y bounds)
    const cardsInRow: { el: HTMLElement, rect: DOMRect, idx: number }[] = []
    for (let i = 0; i < remaining.length; i++) {
        const r = remaining[i].getBoundingClientRect()
        if (cursorY >= r.top - 10 && cursorY <= r.bottom + 10) {
            cardsInRow.push({ el: remaining[i], rect: r, idx: i })
        }
    }

    if (cardsInRow.length > 0) {
        // Sort cards in this row by X position (left to right)
        cardsInRow.sort((a, b) => a.rect.left - b.rect.left)

        // Find which card/gap the cursor is over
        for (let i = 0; i < cardsInRow.length; i++) {
            const { rect: r, idx } = cardsInRow[i]
            const nextCard = cardsInRow[i + 1]

            // Check if cursor is within this card's X bounds
            if (cursorX >= r.left && cursorX <= r.right) {
                const midX = r.left + r.width / 2
                if (cursorX < midX) {
                    return { x: r.left, y: r.top + r.height / 2, height: r.height, index: idx }
                } else {
                    return { x: r.right, y: r.top + r.height / 2, height: r.height, index: idx + 1 }
                }
            }

            // Check if cursor is in the gap between this card and the next
            if (nextCard && cursorX > r.right && cursorX < nextCard.rect.left) {
                // Cursor is in the gap - decide based on which card it's closer to
                const gapMidX = (r.right + nextCard.rect.left) / 2
                if (cursorX < gapMidX) {
                    return { x: r.right, y: r.top + r.height / 2, height: r.height, index: idx + 1 }
                } else {
                    return { x: nextCard.rect.left, y: nextCard.rect.top + nextCard.rect.height / 2, height: nextCard.rect.height, index: nextCard.idx }
                }
            }
        }

        // Cursor is outside all cards in the row - use nearest edge
        const first = cardsInRow[0]
        const last = cardsInRow[cardsInRow.length - 1]
        if (cursorX < first.rect.left) {
            return { x: first.rect.left, y: first.rect.top + first.rect.height / 2, height: first.rect.height, index: first.idx }
        } else {
            return { x: last.rect.right, y: last.rect.top + last.rect.height / 2, height: last.rect.height, index: last.idx + 1 }
        }
    }

    // Fallback: find closest card by Y distance
    let closest = remaining[0]
    let closestIdx = 0
    let minDist = Infinity
    for (let i = 0; i < remaining.length; i++) {
        const r = remaining[i].getBoundingClientRect()
        const dist = Math.abs(cursorY - (r.top + r.height / 2))
        if (dist < minDist) {
            minDist = dist
            closest = remaining[i]
            closestIdx = i
        }
    }
    const r = closest.getBoundingClientRect()
    const midX = r.left + r.width / 2
    if (cursorX < midX) {
        return { x: r.left, y: r.top + r.height / 2, height: r.height, index: closestIdx }
    } else {
        return { x: r.right, y: r.top + r.height / 2, height: r.height, index: closestIdx + 1 }
    }
}

/**
 * Right-side chain display pane ("chain-space")
 * @returns
 */
function ChainSpace (
    {chains,arch,midi,dragSeer,dragged,controllers,overlay}
    : {
        chains:IChain[]
        arch:IArch
        midi?:IMidiLearn
        dragSeer:IDragObserver
        dragged?:IDragged
        controllers: {
            chain: IChainCardController
            module: (chain:IChain,node:IModule) => IModuleController
            midi: IMidiController
        }
        overlay?: boolean
    }) : any
{
    const empty = !chains.length
    const containerRef = useRef<HTMLDivElement>(null)
    const [dropIndicator, setDropIndicator] = useState<DropGap | null>(null)

    // Visible chains in render order
    const visibleChains = chains.filter(c => !c.cond.hidden)

    // RIGHT-CLICK replace: full device catalog + in-use map (moduleId -> owning-chain color)
    const replacer = useMemo(() => {
        const inuse: Record<string,string> = {}
        chains
            .filter(c => c.cond.active)
            .forEach(c => getActiveModules(c, arch).forEach(m => {
                inuse[m.id] = c.state.color || 'red'
            }))
        return { catalog: Module.fromDefs(DeviceDef.extractNodes($Devices), arch), inuse }
    }, [chains, arch])

    const [, drop] = useDrop(() => ({
        accept: 'chain-card',
        hover: (item: DraggedChain, monitor) => {
            const cursor = monitor.getClientOffset()
            if (!containerRef.current || !cursor) return

            const gap = findDropGap(containerRef.current, item.id, cursor.x, cursor.y)
            if (!gap) return

            setDropIndicator(prev =>
                prev?.index === gap.index ? prev : gap
            )
        },
        drop: (item: DraggedChain) => {
            if (!dropIndicator) return

            const remaining = visibleChains.filter(c => c.id !== item.id)
            if (!remaining.length) return

            const idx = dropIndicator.index
            const targetId = idx === 0 ? remaining[0].id : remaining[idx - 1].id
            const after = idx > 0

            controllers.chain.reorder({ id: item.id, targetId, after })
            setDropIndicator(null)
        },
        collect: monitor => !monitor.isOver() && setDropIndicator(null)
    }), [visibleChains, dropIndicator, controllers])

    // Connect drop target to container
    drop(containerRef)

    return (
        <Div
            k={['chain-space', empty && 'empty']}
            ref={containerRef}
            // DOUBLE-CLICK empty chain space (not a chain) creates a new chain — same as hotkey N
            onDoubleClick={e => {
                if ((e.target as HTMLElement).closest('.chain-card')) return
                const chain = controllers.chain.addChain({replace:false})
                NEW_CHAIN = chain.id
            }}>
            {
                !empty
                ? <>
                    {chains.map(chain =>
                        !chain.cond.hidden &&
                            <ChainCard key={chain.id} {...{
                                chain,
                                arch,
                                midi,
                                dragSeer,
                                dragged,
                                controllers: {
                                    ...controllers,
                                    module: node => controllers.module(chain,node),
                                },
                                overlay,
                                replacer,
                            }}/>
                    )}
                    {/* Drop position indicator */}
                    {dropIndicator && (
                        <div
                            style={{
                                position: 'fixed',
                                left: dropIndicator.x - 2,
                                top: dropIndicator.y - dropIndicator.height / 2,
                                width: 4,
                                height: dropIndicator.height,
                                backgroundColor: '#4a9eff',
                                borderRadius: 2,
                                pointerEvents: 'none',
                                zIndex: 1000,
                                boxShadow: '0 0 8px rgba(74, 158, 255, 0.6)',
                            }}
                        />
                    )}
                </>
                : <Div k='empty'>No chains defined</Div>
            }
        </Div>
    )
}

// TRACK LAST KNOWN GOOD pushed route set
let $$LKG_routes : string | undefined

/**
 * PUSH active routes to host API
 */
function routePush ({host,routes:$routes,setAPIState,arch}:{
        host:URL
        routes:Record<number,number>
        setAPIState: (state: boolean) => void
        arch: IArch
    }) {
    const routes = encode()
    // ENCODE route paths for delivery as a QUERY parameter
    function encode () {
        return Object.entries($routes)
            .map(([sink,source]) => {
                // LEGACY API (PP128 only)
                if (Arch.same(arch,ArchPP128A)) {
                    const $sink = +sink < 0 ? `${Math.abs(+sink) - 1}` : sink
                    const $source = source < 0 ? `${Math.abs(source) - 1}` : source
                    return `${$source}.${$sink}`
                }
                // V2+ API
                else {
                    const $sink = ChannelPort.toPortId(+sink,arch)
                    const $source = ChannelPort.toPortId(source,arch)
                    if (!$sink || !$source) return
                    // XXX hack for F/W version 25.09.25
                    return `${$source.replace(/^(FP|P)/,'T')}.${$sink.replace(/^(FP|P)/,'T')}`
                }
            })
            .join(',')
    }

    if (routes === $$LKG_routes) {
        D('routePush: route remains same as last (known good) pushed')
        return
    }

    const version:net.APIVersion = Arch.same(arch,ArchPP128A) ? 'V1' : 'V2'
    net.PPApi
        .pushRoutes({host,proxy:OP_PARAMS.API_PROXY,routes,timeout:DEFAULT_API_TIMEOUT,version})
        .then(response => {
            if (response.ok) {
                // LKG route
                $$LKG_routes = routes
                setAPIState(true)
                D('ROUTE PUSH SUCCESS!')
            }
            else {
                D('ROUTE PUSH FAIL!','STATUS',response.status)
            }
        })
        .catch((e) => {
            E('PUSH ROUTE',e)
        })
}

/**
 * Calculate and push routes reflecting chains' state
 */
function routeChains ({chains,arch,setAPIState,state,host}:{chains:IChain[],arch:IArch,setAPIState:any,host?:URL,state:boolean}) {
    const activeChains = chains
        .filter(_ => !Chain.invalid(_,arch))
        .filter(({cond:{active}}) => active)

    // TREE-based calculation
    const routes = activeChains
        .map(chain => Chain.decompose(chain,arch))
        .reduce((p:Record<number,number>,c) => {
            c.forEach(({from,to}) => p[to] = from)
            return p
        },{})

    D('ENCODED ROUTES',dbgencode() ?? '<none>')
    // ENCODE routes, including
    function dbgencode () {
        const route = Object
            .entries(routes)
            .map(([sink,source]) => {
                const $sink = ChannelPort.toPortId(+sink,arch)
                const $source = ChannelPort.toPortId(source,arch)
                return `${$source}.${$sink}`
            })
            .join(',')
        return route.length ? route : null
    }

    if (host && state) {
        routePush({host,routes,setAPIState,arch})
    }
}

/**
 * Represents point-in-time capture of application state
 */
interface ICheckpoint {
    origin?: string
    chains: string
    devices: string
    timestamp: string
}

// HISTORY of application state; used for undo/redo
let checkpoints: ICheckpoint[] = []

// INDEX of CURRENT APP STATE as stored in history
let checkpointIndex = 0

type SearchHits = {query:string,hits?:Record<string,IChain>}

type TPopArgs = {
    title: string,
    prompt: any
    yes: () => void
    no?: () => void
}

// AUGMENT global Window interface to include showSaveFilePicker
declare global {
    interface Window {
        showSaveFilePicker(options?: ShowSaveFilePickerOptions): Promise<FileSystemFileHandle>
    }
}

const saveDump = async () => {
    // request file handle
    const fh = await window.showSaveFilePicker({
        suggestedName: 'propatch-dump',
        types: [
            {
                description: 'DUMP file',
                accept: { 'text/plain': ['.dump'] }
            }
        ],
        excludeAcceptAllOption: true
    })
    // create writable stream
    const out = await fh.createWritable()
    out.write('PROPATCH DUMP V1\n')
    out.write('\ndevices\n')
    out.write(localStore?.get('devices') || '')
    out.write('\nchains\n')
    out.write(localStore?.get('chains') || '')
    out.write('\nsessions\n')
    out.write(localStore?.get('sessions') || '')
    out.write('\nactive\n')
    // ACTIVE session
    out.write(localStore?.get('active') || '')
    out.write('\n')
    await out.close()
}

const CenteredDialog = ({ width = 300, children }) => {
    const dialogRef = useRef<HTMLDivElement>(null)
    const [iconSize, setIconSize] = useState(0)
    
    useEffect(() => {
      if (dialogRef.current) {
        const height = dialogRef.current.clientHeight
        setIconSize(Math.min(width, height) * 0.8)
      }
    })

    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#000',
          backgroundColor: '#600'
        }}
      >
        <div 
         ref={dialogRef}
          style={{
            width,
            backgroundColor: '#fff',
            borderRadius: '5px',
            padding: '20px',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 0 10px rgba(0,0,0,0.2)'
          }}
        >
            <div style={{
                position: 'absolute',
                fontSize: iconSize,
                opacity: 0.2,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
                }}>
            ⚠️
            </div>
            <div style={{position: 'relative', zIndex: 1}}>
            {children}
            </div>
        </div>
      </div>
    )
}

////////////////////////////////////////////////////////////////////////////////
// LOAD INITIAL app state
////////////////////////////////////////////////////////////////////////////////
let $ActiveSession : IActiveSession | undefined = LocalStore.loadActive()
let $Devices : TRecDeviceDef = LocalStore.loadDevices() || {}
let $Chains: TRecChain = LocalStore.loadChains($Arch) || []
let $AppState: IAppState = LocalStore.loadApp() || AppState.create()
let $Sessions : TRecSession = {}
let $SessionsLoaded = false

let $MuteAll : Record<string,boolean> | undefined

let Pop: TPopArgs | undefined

type TBrowserTypes = 'chain'|'module'|'device'
let $browserType: TBrowserTypes

// USED by browsers to decide when filter/view states should be cleared
let DEVICE_EPOCH = 0
let CHAIN_EPOCH = 0

interface IMidiLearnMgr extends IMidiLearn {
    all(): TMidiSink[]
}
let $midiLearn: IMidiLearnMgr | undefined
let $midiOneShot: ((event: IMidiNoteEvent) => void) | undefined
let $space: 'chain' | 'device' = 'chain'
let $midiPortConnected: boolean = false

const $MidiEvents: Record<string,IMidiNoteEvent> = {}

type TMidiToastSink = 'node' | 'chain' | 'abgroup' | 'mute' | 'session'


type TEventMonEvent = {
    name: string,
    session: {
        name: string | null,
        id: string | null,
    }
    ts: string,
    args: (string|null)[],
}

interface IVersionData {
    version: string
    build: string
    commit: string
    channel: string
}

// Compare version strings like "25.12.07" numerically
// Returns: 1 if a > b, -1 if a < b, 0 if equal
function compareVersions(a: string, b: string): number {
    const partsA = a.split('.').map(Number)
    const partsB = b.split('.').map(Number)
    const len = Math.max(partsA.length, partsB.length)
    for (let i = 0; i < len; i++) {
        const numA = partsA[i] ?? 0
        const numB = partsB[i] ?? 0
        if (numA > numB) return 1
        if (numA < numB) return -1
    }
    return 0
}

class VersionMon {
    static INTERVAL_MS = 1 * 60 * 1000 // 1 minute
    private active: boolean
    private version: string
    private channel: string
    private build: string
    private announce: (version?:string,build?:string) => void

    constructor (
        { version, build, channel, announce}
    :{
        version: string
        build: string
        channel: string
        announce: (version?:string,build?:string) => void
    }) {
        D('VersionMon: START',version,build,channel)
        this.version = version
        this.build = build
        this.channel = channel
        this.active = true
        this.announce = announce
        setTimeout(() => this.detect(), 10000)
    }

    private async detect () {
        // DESTROYED?
        if (!this.active) {
            D('VersionMon.detect: ABORTING')
            return
        }
        // SCHEDULE next fetch
        setTimeout(() => this.detect(), VersionMon.INTERVAL_MS)
        D('VersionMon: FETCHING LATEST...')
        try {
            const response = await fetch(
                './version.json',
                {
                    headers: {
                        'cache-control': 'no-cache',
                        pragma: 'no-cache',
                    }
                }
            )
            if (response.ok) {
                const resp = await response.blob
                const data:IVersionData = await response.json()
                L('VersionMon: FETCHED',data)
                if (data?.version && data?.build) {
                    const {version,build,channel} = data
                    // CHANNEL must match!
                    const versionCmp = compareVersions(version, this.version)
                    if (channel === this.channel
                            // VERSION > current or VERSION == current and BUILD > current
                            && (versionCmp > 0 || (versionCmp === 0 && build > this.build))) {
                        L('VersionMon: PAW UPDATE DETECTED')
                        this.announce(version,build)
                    }
                    else {
                        L('VersionMon: NO update detected')
                        this.announce()
                    }
                }
            }
            else {
                E(`VersionMon: FETCH ATEMPT REJECTED - status: ${response.status}`)
            }
        } catch (e) {
            E('VersionMon: FETCH FAILED:', e)
        }
    }

    destroy () {
        D('VersionMon: DESTROY')
        this.active = false
    }
}

class EventMon {
    static INTERVAL_MS = 10 * 60 * 1000
    private events: TEventMonEvent[] = []
    private worker?: Worker

    constructor () {
        D('EVENT MON: START')
        this.worker = new Worker(new URL('./workers/telem.ts', import.meta.url), { type: 'module' })
        setTimeout(() => this.shipEvents(), EventMon.INTERVAL_MS)
    }

    private async shipEvents () {
        // WORKER terminated?
        if (!this.worker) {
            D('shipEvents: ABORTING')
            return
        }

        setTimeout(() => this.shipEvents(), EventMon.INTERVAL_MS)
        // NOTHING TO DO
        if (!this.events.length) return
        D('EVENT MON: SHIPPING...')
        // COLLECT event-referenced sessions
        const sessions = Object
            .keys(this.events
                .reduce((p,c) => {
                    if (c.session.id) {
                        p[c.session.id] = true
                    }
                    return p
                },{}))
            // LOCATE by ID (unfortunately indexed by name)
            .map(id => Object.values($Sessions).find(s => s.id === id))
            .filter(_ => _) as ISession[]
        const info = {
            version: '1.1',
            ts: nowTimestamp(),
            wolffhound: APP_ENV.wolffhound ?? '',
            document: {
                url: document.URL,
                params: DOC_PARAMS,
            },
            app: {
                version: VERSION,
                built: BUILT_AT,
                channel: BUILT_FOR,
            },
            system: await AppEnv.system() ?? {},
        }
        const payload = {
            ...info,
            events: this.events,
            sessions,
            session: $ActiveSession?.name
            ? {
                name: $ActiveSession.name,
                id: $ActiveSession?.id ?? null,
                chains: $Chains,
                devices: $Devices,
            }
            : {
                name: null,
                id: null,
                chains: [],
                devices: {},
            }
        }
        // RESET event queue
        this.events = []
        // POST payload
        D('POSTING PAYLOAD',info)
        const data = new TextEncoder().encode(JSON.stringify(payload)).buffer
        const url = `https://${BUILT_FOR}-pp.telem.wolffaud.io/post`
        this.worker.postMessage( { data, url, }, [data] )
    }

    post (name:string, args: any[]) {
        if (!this.worker) return
        const event = {
            name,
            ts: nowTimestamp(),
            session: {
                name: $ActiveSession?.name ?? null,
                id: $ActiveSession?.id ?? null,
            },
            args: args.map(_ => _ == null ? null : `${_}`),
        }
        //E('POST EVENT',event)
        this.events.push(event)
    }

    destroy () {
        this.worker?.terminate()
        this.worker = undefined
    }
}

let $EventMon: EventMon | undefined

const EVENT_TYPES = [
    'CHAIN-CREATE',
    'CHAIN-DELETE',
    'CHAIN-HIDE',
    'CHAIN-SHOW',
    'CHAIN-ACT',
    'CHAIN-COPY',
    'CHAIN-FAVE',
    'CHAIN-NAME',
    'CHAIN-COLOR',
    'CHAIN-NORMAL',
    'CHAIN-NODE-INS',
    'CHAIN-NODE-DROP',
    'CHAIN-TAG-CREATE',
    'CHAIN-TAG-DELETE',
    'CHAIN-TAG-ASSIGN',
    'CHAIN-TAG-UNASSIGN',
    'CHAIN-SPLIT',
    'CHAIN-UNSPLIT',
    'CHAIN-CELL-MULT',
    'CHAIN-CELL-ACT',
    'CHAIN-CELL-AB',
    'CHAIN-CELL-UNGROUP',
    'CHAIN-CELL-DELETE',
    'CHAIN-CELL-BRIDGE',
    'CHAIN-CELL-UNBRIDGE',
    'CHAIN-CELL-SPLIT',
    'CHAIN-LANE-DELETE',
    'DEVICE-DELETE',
    'DEVICE-COPY',
    'DEVICE-CREATE',
    'DEVICE-MODIFY',
    'NODE-TAG-CREATE',
    'NODE-TAG-DELETE',
    'NODE-TAG-ASSIGN',
    'NODE-TAG-UNASSIGN',
    'NODE-CREATE',
    'NODE-DELETE',
    'NODE-MODIFY',
    'NODE-COPY',
    'APP-HOTKEY',
    'APP-MUTE',
    'APP-UNMUTE',
    'APP-UNDO',
    'APP-REDO',
    'MIDI-LEARN',
    'SESSION-CREATE',
    'SESSION-DELETE',
    'SESSION-SAVE',
    'SESSION-LOAD',
    'SESSION-COPY',
    'SESSION-REVERT',
    'SESSION-LOCK',
    'SESSION-NAME',
    'SESSIONS-CLEAR',
]

/**
 * MONITOR console log for EVENTS
 * (proxy log.console)
 */
const $ConsoleMonitor = new Proxy(console, {
    get(target, prop) {
        const original = target[prop]
        // PROXY 'console.log'
        if (typeof original === 'function' && (prop as string) === 'log') {
            return function(...args: any[]) {
                const event = args[0]
                if ($EventMon && typeof event === 'string' && EVENT_TYPES.includes(event)) {
                    //E('CONSOLE EVENT',event,args.slice(1))
                    $EventMon.post(event,args.slice(1))
                }
                // CONTINUE console logging
                return original.apply(target, args)
            }
        }
        else {
            return original
        }
    }
});

// INSTALL console.log proxy
(window as any).console = $ConsoleMonitor

let $LastForceOff : string | undefined

let $ShowShortcuts: hotkeys.ITarget[] | undefined
let $HotkeyTargets: hotkeys.ITarget[] | undefined
let $PortOverlay: boolean = false

// Main component
// XXX break this up!
export default function Upstage ({fallback}:{fallback?:boolean}) {
    const ref = useRef<HTMLElement>(null)
    // Capture demo storage state before effects mutate localStorage. Session
    // loading can happen during render, so resetSessions() needs the boot values.
    const demoBootRef = useRef<{
        currentModel: string
        firstVisit: boolean
        resetApplied: boolean
        storedModel: string | null
    }>()

    if (!demoBootRef.current) {
        demoBootRef.current = {
            currentModel: $Arch.model.name,
            firstVisit: DEMO && !localStorage.getItem(KEY_DEMO_FIRST),
            resetApplied: false,
            storedModel: DEMO ? localStorage.getItem(KEY_DEMO_HW_MODEL) : null,
        }
    }

    const [demoDialog, setDemoDialog] = useState<'demo' | 'notes' | 'update' | null>(null)

    useEffect(() => {
        // DISPLAY DEMO info dialog?
        if (DEMO) {
            const demoBoot = demoBootRef.current
            setTimeout(() => {
                setDemoDialog('demo')
            })
            localStorage.setItem(KEY_DEMO_FIRST,'0')
            localStorage.setItem(KEY_DEMO_VER,APP.version)
            // The arch marker is updated on every demo boot so a future URL
            // model change can distinguish stale Demo state from normal reloads.
            localStorage.setItem(KEY_DEMO_HW_MODEL,demoBoot?.currentModel ?? $Arch.model.name)
        }
        // DISPLAY UPDATE modal on version change? (non-demo only)
        if (!DEMO) {
            const lastSeen = localStorage.getItem(KEY_UPDATE_LAST_VER)
            if (lastSeen && compareVersions(APP.version, lastSeen) > 0) {
                setTimeout(() => setDemoDialog('update'))
            }
            localStorage.setItem(KEY_UPDATE_LAST_VER, APP.version)
        }
    },[])

    const [importErrors,setImportErrors] = useState<string>()

    // "DISPLAY ONLY" copies of app state
    const [Devices,setDevices] = useState<TRecDeviceDef>(clone($Devices))
    const [Chains,setChains] = useState<TRecChain>(clone($Chains))
    const [appState,setAppState] = useState<IAppState>(clone($AppState))
    const [Sessions,setSessions] = useState<TRecSession>(clone($Sessions))

    // CURRENT filter options of chain browser
    // XXX why are these maintained externally?
    const [chainFilter,setChainFilter] = useState<SearchHits>()

    // CURRENT node type displayed in node browser
    const [browserModType,setBrowserModType] = useState<TRoleType>('proc') // DEFAULT node browser type

    // SEARCH input field active?
    const [browserSearch,setBrowserSearch] = useState<boolean>(false)

    // CURRENT device selected for display in right-side pane (if "device space" active)
    const [browserDevice,setBrowserDevice] = useState<string>()

    // LEFT-SIDE pane (browser) content
    const [browserType,$setBrowserType] = useState<TBrowserTypes>('module') // DEFAULT browser tab

    // ITEM current being DRAGGED
    const [dragItem,setDragItem] = useState<IDragged>()

    // MODAL OK/CANCEL prompt
    const [pop,$setPop] = useState<TPopArgs>()

    // MODAL (CRITICAL) ALERT (OK) prompt
    const [popAlert,setPopAlert] = useState<TAlertArgs>()

    // MODAL (INFO) NOTIFICATION (OK) prompt
    const [popInfo,setPopInfo] = useState<TAlertArgs>()
    // Non-blocking offline warning
    const [offlineNotice,setOfflineNotice] = useState<boolean>(false)

    const [updatePending,setUpdatePending] = useState<boolean>(DOC_PARAMS.updatepend ?? false)

    const [muteAll,$setMuteAll] = useState<Record<string,boolean>>()

    function setMuteAll (ids: Record<string,boolean> | undefined) {
        $MuteAll = ids
        $setMuteAll(ids)
    }
    // Selected MIDI ports (multi-select, 'ANY' means listen to all ports)
    // Map from port ID to port object (preserves names for disconnected ports)
    const [selectedPorts, $setSelectedPorts] = useState<Map<string, TMidiPort>>(() => {
        const midi = $AppState.settings.midi
        const map = new Map<string, TMidiPort>()
        // Migrate from old single-port format
        if (midi?.port && !midi?.ports) {
            map.set(midi.port.id, midi.port)
        } else if (midi?.ports) {
            midi.ports.forEach(p => map.set(p.id, p))
        }
        return map
    })
    // Available MIDI ports (discovered by MidiMonitor)
    const [midiPorts, setMidiPorts] = useState<TMidiPortRec>({})

    // Update selected ports and persist to storage
    function setSelectedPorts(ports: Map<string, TMidiPort>): void {
        $AppState.settings.midi = { ports: Array.from(ports.values()) }
        $setSelectedPorts(ports)
        setAppState({...$AppState})
        LocalStore.saveApp($AppState)
    }

    const [conflict,setConflict] = useState<TConflictDialogArgs>()
    const [conflictInfo,setConflictInfo] = useState<TConflictInfoArgs>()

    // MODAL INPUT prompt
    const [prompt,setPrompt] = useState<TPromptArgs>()

    // CREATE SESSION dialog
    const [createSessionDialog, setCreateSessionDialog] = useState<boolean>(false)

    // SUPPORT dialog
    const [supportDialog, setSupportDialog] = useState<TSupportDialogArgs>()

    // API HOST status
    const [apiState,setAPIState] = useState<boolean>(false)
    // Ref to track current API state for async callbacks (avoids stale closures in MIDI handlers)
    const apiStateRef = useRef(apiState)
    apiStateRef.current = apiState

    // Auto-dismiss offline warning when connection is restored
    useEffect(() => {
        if (apiState && offlineNotice) {
            setOfflineNotice(false)
        }
    }, [apiState, offlineNotice])

    // CURRENT (validly formatted) API HOST
    const [apiHost,setAPIHost] = useState<URL|undefined>(OP_PARAMS.API_HOST)

    // LAST incoming MIDI note
    const [midiEvent,setMidiEvent] = useState<IMidiNoteEvent>()

    // Keyboard shortcuts modal
    const [,$setShowShortcuts] = useState<hotkeys.ITarget[]>()

    // PORT overlays
    const [,$setPortOverlay] = useState<boolean>()

    // MIDI MANAGER
    const [midiManager,setMidiManager] = useState<boolean>()

    // DESKTOP BACKGROUND IMAGE
    const [bgDialog,setBgDialog] = useState<boolean>()
    // Apply the persisted background once #space is mounted.
    useEffect(() => { widget.applyBackgroundToSpace($AppState.settings.background) }, [])

    // SYSTEM CONFIGURATION
    const [sysConfig,setSysConfig] = useState<boolean>()
    const [sysConfigTab,setSysConfigTab] = useState<TSysConfigTab>('preferences')

    // APP INFO MODAL
    const [appInfo,setAppInfo] = useState<boolean>()

    // CURRENT ACTIVE session
    const [,$setActive] = useState<IActiveSession|undefined>($ActiveSession)

    // WHAT is being displayed in right-side pane
    const [space,setSpace] = useState<'chain'|'device'>('chain')
    useEffect(() => { $space = space }, [space])

    // ESTABLISHED MIDI learning mode
    const [midiLearn,$setMidiLearn] = useState<IMidiLearnMgr>()

    function setMidiLearn (learn?: MidiLearn) {
        D('MIDI-LEARN',learn ? 'ON' : 'OFF')
        $setMidiLearn(learn)
        $midiLearn = learn
    }

    // INPUT elements for 'load' file dialog
    const restoreRef = useRef<HTMLInputElement>(null)
    const importSessRef = useRef<HTMLInputElement>(null)

    // MAX API PING fail count before considered "bad" state
    const MAX_PING_FAILS = 3
    // POLLING interval (ms)
    const API_POLL_INTERVAL = 5000

    /**
     * SCRUB disruptive query parameters from document URL
     */
    function scrubDocURL () : string {
        const url = new URL(document.URL)
        url.searchParams.forEach((_,k) => {
            if (['dump','reset'].includes(k.toLowerCase())) {
                url.searchParams.delete(k)
            }
        })
        return String(url)
    }

    function setShowShortcuts (targets?: hotkeys.ITarget[]) {
        $setShowShortcuts(targets)
        $ShowShortcuts = targets
    }

    function setPortOverlay (show: boolean) {
        $setPortOverlay(show)
        $PortOverlay = show
    }

    // RESET APP STATE
    if (OP_PARAMS.RESET) {
        return <a 
                onClick={
                    () => {
                        LocalStore.resetAll()
                        localStorage.removeItem(KEY_DEMO_FIRST)
                        localStorage.removeItem(KEY_DEMO_VER)
                        localStorage.removeItem(KEY_DEMO_HW_MODEL)
                        localStorage.removeItem(KEY_UPDATE_LAST_VER)
                    }
                }
                style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    backgroundColor: 'red',
                    color: 'white',
                    textDecoration: 'underline'}}
                href={scrubDocURL()}
            >RESET STATE</a>
    }
    // DUMP APP STATE
    else if (OP_PARAMS.DUMP) {
        return <a 
                onClick={
                    async () => {
                        try {
                            await saveDump()
                        } catch (err) {
                            // user cancelled operation
                            const cancelled = err instanceof DOMException &&  err.message.match(/aborted/)
                            if (!cancelled) {
                                D('Error saving dump:', err)
                                setError('Failed to save dump file',err)
                            }
                        }
                        window.location.href = scrubDocURL()
                    }
                }
                style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    backgroundColor: 'red',
                    color: 'white',
                    textDecoration: 'underline'}}
            >DUMP STATE</a>
    }
    else if (fallback) {
        const hrefNext = scrubDocURL() 
        const Choice = ({title,text,cb}) => {
            const [hover, setHover] = useState<boolean>(false)
            return (
                <div
                    style={{
                        padding: '10px'
                    }}>
                    <div
                        style={{
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                            color: hover ? '#f0f' : '#00f'
                        }}
                        onMouseOver={() => setHover(true)}
                        onMouseOut={() => setHover(false)}
                        onClick={() => {
                            try {
                                if (!cb()) {
                                    window.location.href = hrefNext
                                }
                            }
                            catch (e) {
                                window.alert(e)
                            }
                        }}
                    >
                        {title}
                    </div>
                    <div>
                    {text}
                    </div>
                </div>
            )
        }

        return (
            <CenteredDialog width={500} >
                <h2>A critical error occurred!</h2>
                {hasState() && 
                    <Choice 
                        title="OK"
                        text="Acknowledge and continue. You will only lose data from last several operations."
                        cb={() => {
                            undoState()
                        }}
                    />
                }
                <Choice
                    title="REVERT"
                    text="Revert application state to last saved session."
                    cb={() => {
                        // RESTORE SAVED state of (persistent) ACTIVE session
                        if ($ActiveSession?.name) {
                            loadSession($ActiveSession?.name)
                        }
                        // RESET active (ephemeral) session
                        else {
                            setActive(undefined)
                        }
                    }}
                />
                <Choice
                    title="DUMP"
                    text="Dump application state (for later analysis)"
                    cb={() => {
                        saveDump().then(() => {
                            window.alert('Application state dumped successfully')
                        })
                        .catch(err => {
                            // operation CANCELLED by user
                            const cancelled = err instanceof DOMException &&  err.message.match(/aborted/)
                            if (!cancelled) {
                                D('Error saving dump:', err)
                                window.alert(`Failed to save dump file!\n\n${err?.message}`)
                            }
                            else {
                                window.alert('User cancelled application dump')
                            }
                        })
                        // OVERRIDE HREF redirect
                        return true
                    }}
                />
                <Choice
                    title="NUKE"
                    text="NUKE application state! (ERASES EVERYTHING! DUMP FIRST MAYBE?)"
                    cb={() => {
                        LocalStore.resetAll()
                    }}
                />
            </CenteredDialog>
        )
    }

    /**
     * HOTKEY handlers
     */
    useEffect(() => {
        // HOTKEY functional categories
        const cats = {
            browser: { desc: 'Navigation' },
            actions: { desc: 'Actions' },
            select: { desc: 'Selection' },
            search: { desc: 'Search' },
            history: { desc: 'History' },
            gen: { desc: 'General' },
        }
        // SELECT next or previos (visible) chain
        const selectChain = (prev:boolean) => {
            const visible = $Chains.filter(({cond:{hidden}}) => !hidden)
            if (visible.length) {
                const selected = getSelectedChain()
                // BE CAREFUL - DON'T UNSELECT sole visible chain
                if (visible.length > 1 || visible.length === 1 && !selected) {
                    const idx = visible.findIndex(({id}) => id === selected?.id)
                    // ID of next selected chain
                    const next = visible[idx < 0 
                        ? 0 // NONE (visible) SELECTED - select first
                        : !prev
                        ? (
                            idx === visible.length - 1
                            ? 0  // LAST SELECTED - rollover to first
                            : idx + 1 // SELECT next
                        )
                        : (
                            idx === 0
                            ? visible.length - 1  // FIRST SELECTED - rollover to last
                            : idx - 1 // SELECT previous
                        ) 
                    ]
                    control.select(next.id)
                }
            }
        }
        const targets: hotkeys.ITarget[] = [
            /////////////////////////////////////////////////////
            // BROWSER selection
            /////////////////////////////////////////////////////
            // SOURCE BROWSER
            {
                key: 'S',
                cmd: () => {
                    setBrowserModType('source')
                    setBrowserType('module')
                },
                desc: 'Browse Source Nodes',
                cat: cats.browser,
            },
            // PROC BROWSER
            {
                key: 'P',
                cmd: () => {
                    setBrowserModType('proc')
                    setBrowserType('module')
                },
                desc: 'Browse Processor Nodes',
                cat: cats.browser,
            },
            // DEST BROWSER
            {
                key: 'D',
                cmd: () => {
                    setBrowserModType('dest')
                    setBrowserType('module')
                },
                desc: 'Browse Destination Nodes',
                cat: cats.browser,
            },
            // CHAIN BROWSER
            {
                key: 'C',
                cmd: () => setBrowserType('chain'),
                desc: 'Browse Chains',
                cat: cats.browser,
            },
            // NODE BROWSER
            {
                key: 'N',
                cmd: () => setBrowserType('module'),
                desc: 'Browse Nodes',
                cat: cats.browser,
            },
            // DEVICE  BROWSER
            {
                key: 'G',
                cmd: () => setBrowserType('device'),
                desc: 'Browse Devices',
                cat: cats.browser,
            },
            /////////////////////////////////////////////////////
            // CHAIN actions
            /////////////////////////////////////////////////////
            // CREATE CHAIN/DEVICE
            {
                key: 'N', mod: 'S',
                cmd: () => {
                    if ($browserType === 'device') {
                        setTimeout(() => newDevModal?.current())
                    }
                    else {
                        // Create a brand-new chain (do not replace existing)
                        setTimeout(() => addChain(false),0)
                    }
                },
                desc: 'Create New (Device or Chain)',
                cat: cats.actions,
            },
            // TOGGLE CHAIN active state
            {
                key: 'A',
                cmd: () => {
                    const selected = getSelectedChain()
                    if (selected && !Chain.invalid(selected,$Arch)) {
                        control.active(selected.id)
                    }
                },
                desc: 'Toggle Active State of Selected Chain',
                cat: cats.actions,
            },
            // HIDE CHAIN
            {
                key: 'H',
                cmd: () => {
                    const selected = getSelectedChain()
                    if (selected) control.hide(selected.id)
                },
                desc: 'Hide Selected Chain',
                cat: cats.actions,
            },
            // TARGET CHAIN
            {
                key: 'T',
                cmd: () => {
                    const selected = getSelectedChain()
                    if (selected) control.focus(selected.id,!selected.cond.focus)
                },
                desc: 'Toggle Target/Focus for Selected Chain',
                cat: cats.actions,
            },
            // LOCK/UNLOCK CHAIN
            {
                key: 'L',
                cmd: () => {
                    const selected = getSelectedChain()
                    if (selected) control.lock(selected.id)
                },
                desc: 'Toggle Lock on Selected Chain',
                cat: cats.actions,
            },
            // BYPASS/RESTORE chain processors
            {
                key: 'B',
                cmd: () => {
                    const selected = getSelectedChain()
                    if (selected?.state.locked) {
                        toast(<Div k='toast'>Chain is locked</Div>, { id: 'toast', style: TOAST_STYLE })
                        return
                    }
                    if (selected
                        && ((
                                // ACTIVE
                                selected.cond.active
                                // at least one ACTIVE PROCESSOR
                                && Chain.activeNodes(selected).some(_ => _.proc)
                            )
                            // PATHOLOGICAL CASE: inactive + bypassed
                            || selected.cond.bypass))
                    {
                        control.bypass(selected.id)
                    }
                },
                desc: 'Bypass/Restore Processors of Selected Chain',
                cat: cats.actions,
            },
            // DEACTIVATE ALL processors in selected chain
            {
                key: 'B',
                mod: 'S',
                cmd: () => {
                    const selected = getSelectedChain()
                    if (selected?.state.locked) {
                        toast(<Div k='toast'>Chain is locked</Div>, { id: 'toast', style: TOAST_STYLE })
                        return
                    }
                    if (selected
                            // NOT currently BYPASSED
                            && !selected.cond.bypass) {
                        control.bypass(selected.id,true)
                    }
                },
                desc: 'Deactivate Processors of Selected Chain',
                cat: cats.actions,
            },
            /////////////////////////////////////////////////////
            // BROWSER search
            /////////////////////////////////////////////////////
            // ACTIVATE BROWSER SEARCH
            {
                key: '/',
                cmd: () => setBrowserSearch(true),
                desc: 'Focus Browser Search',
                cat: cats.search,
            },
            // CANCEL active browser search
            {
                key: 'Escape',
                cmd: () => setBrowserSearch(false),
                desc: 'Exit Browser Search',
                cat: cats.search,
            },
            /////////////////////////////////////////////////////
            // CHAIN navigation
            /////////////////////////////////////////////////////
            // SELECT next CHAIN
            {
                key: ['Tab'],
                cmd: () => selectChain(false),
                desc: 'Select Next Visible Chain',
                cat: cats.select,
            },
            // SELECT prev CHAIN
            {
                key: ['Tab'],
                mod: 'S',
                cmd: () => selectChain(true),
                desc: 'Select Previous Visible Chain',
                cat: cats.select,
            },
            /////////////////////////////////////////////////////
            // GENERAL
            /////////////////////////////////////////////////////
            // SHOW keyboard shortcuts
            {
                key: 'K',
                cmd: () => {
                    if ($ShowShortcuts) {
                        setShowShortcuts(undefined)
                    } else {
                        setShowShortcuts(targets)
                    }
                },
                desc: 'Display All Shortcuts',
                // IF ANY TARGET then CLOSE shortcut overlay
                mon: target => {
                    if ($ShowShortcuts && target.key !== 'K') {
                        setTimeout(() => setShowShortcuts(undefined),0)
                    }
                }
            },
            // SHOW port overlays
            {
                key: 'O',
                cmd: () => { if (!$PortOverlay) setPortOverlay(true) },
                up: () => { if ($PortOverlay) setPortOverlay(false) },
                desc: 'Display PortID Overlays (Hold)',
                cat: cats.gen,
            },
            // TOGGLE port overlays
            {
                key: 'O',
                mod: 'S',
                cmd: () => setPortOverlay(!$PortOverlay),
                desc: 'Toggle Port Overlays',
                cat: cats.gen,
            },
            // TOGGLE MIDI LEARN
            {
                key: 'M',
                cmd: () => {
                    if ($space === 'chain' && $midiPortConnected) {
                        setMidiLearn($midiLearn ? undefined : new MidiLearn())
                    }
                },
                desc: 'Toggle MIDI Learn mode',
                cat: cats.gen,
            },
            // OPEN MIDI MANAGER
            {
                key: 'M',
                mod: 'S',
                cmd: () => setMidiManager(true),
                desc: 'Open MIDI Manager',
                cat: cats.gen,
            },
            // UNDO
            {
                key: 'Z',
                mod: 'M',
                cmd: () => undoState(),
                desc: 'Undo',
                cat: cats.history,
            },
            // REDO: WINDOWS
            /win/i.test(navigator.platform)
            ? {
                key: 'Y',
                mod: 'C',
                cmd: () => redoState(),
                desc: 'Redo',
                cat: cats.history,
            }
            // REDO: MACOS
            : {
                key: 'Z',
                mod: ['M','S'],
                cmd: () => redoState(),
                desc: 'Redo',
                cat: cats.history,
            }
        ]
        $HotkeyTargets = targets
        function handler (e:KeyboardEvent,up: boolean) {
            /*
             * IGNORE input from active text control
             */
            const el = e.target as HTMLElement
            if (['INPUT','TEXTAREA'].includes(el.tagName) || el.isContentEditable) {
                return
            }
            const hots = hotkeys.Target.forEvent(targets,e)
            if (hots) {
                const target = hots[0]
                // TRIGGER any observers/monitors
                targets
                    .filter(_ => _.mon)
                    .forEach(_ => _.mon!(target))
                //D('HOTKEY',target.key,'MOD',target.mod ?? 'plain','DESC',target.desc)
                try {
                    if (up) {
                        if (target.up) {
                            e.stopPropagation()
                            e.preventDefault()
                            target.up()
                        }
                    }
                    else {
                        e.stopPropagation()
                        e.preventDefault()
                        target.cmd()
                    }
                } catch (e) {
                    E(e)
                }
            }
        }
        const keyDown = (e:KeyboardEvent) => { handler(e,false) }
        const keyUp = (e:KeyboardEvent) => { handler(e,true) }
        document.addEventListener('keydown', keyDown)
        document.addEventListener('keyup', keyUp)

        return () => {
            document.removeEventListener('keydown', keyDown)
            document.removeEventListener('keydown', keyUp)
        }
    },[ref])

    // INIT HOST API monitors
    useEffect(() => {
        // DEFAULT to BAD connection state, until proven otherwise
        setAPIState(false)
        if (apiHost) {
            const host = apiHost
            const proxy = OP_PARAMS.API_PROXY

            // MONITOR API status
            const apiMonitor = new net.HttpApiMonitor({
                host,
                proxy,
                timeout: DEFAULT_API_TIMEOUT * 2,
                maxFails: MAX_PING_FAILS,
                pollInt: API_POLL_INTERVAL,
                onChange: state => setAPIState(state),
            })
            // DESTRUCTOR
            return () => apiMonitor.terminate()
        }
    },[apiHost])

    // Derive port IDs string for stable dependency tracking
    const selectedPortIds = useMemo(() => Array.from(selectedPorts.keys()).sort().join(','), [selectedPorts])

    // Check if at least one selected port is actually connected
    // 'ANY' counts as connected if there's at least one real port available
    const hasConnectedPort = Array.from(selectedPorts.keys()).some(id =>
        id === 'ANY'
            ? Object.values(midiPorts).some(p => p.id !== 'ANY')
            : !!midiPorts[id]
    )
    useEffect(() => { $midiPortConnected = hasConnectedPort }, [hasConnectedPort])

    // INIT MIDI monitor
    useEffect(() => {
        // Determine which ports to monitor:
        // - undefined = listen to all ports (when ANY selected or no ports selected)
        // - array = listen only to these specific ports
        const specificPorts = Array.from(selectedPorts.keys()).filter(id => id !== 'ANY')
        const monitorPorts = selectedPorts.has('ANY') ? undefined :
                             specificPorts.length > 0 ? specificPorts : undefined

        const midiObserver = new MidiMonitor({
            onNote: selectedPorts.size > 0 ? onMidiNote : () => {},
            ports: monitorPorts,
            onPort: ports => {
                setMidiPorts({
                    ...ports,
                    'ANY': { id: 'ANY', name: '<any>' }
                })
            }
        })
        return () => midiObserver?.destroy()
    },[selectedPortIds])  // Re-run when selection changes

    // INIT TELEM EVENT collector/shipper
    useEffect(() => {
        D('CREATING EVENT MONITOR')
        const $ = new EventMon()
        $EventMon = $
        return () => {
            D('DESTROYING EVENTMON')
            $?.destroy()
            $EventMon = undefined
        }
    },[])

    // INIT VERSION MONITOR
    useEffect(() => {
        // Check if WH passed a newer bundled version via URL params
        if (DOC_PARAMS.bundled_version && DOC_PARAMS.bundled_channel === APP.channel) {
            const versionCmp = compareVersions(DOC_PARAMS.bundled_version, APP.version)
            const bundledNewer = versionCmp > 0 ||
                (versionCmp === 0 && (DOC_PARAMS.bundled_build ?? '') > APP.build)
            if (bundledNewer) {
                L('main: BUNDLED UPDATE AVAILABLE', DOC_PARAMS.bundled_version, DOC_PARAMS.bundled_build)
                setUpdatePending(true)
            }
        }

        D('CREATING VERSION MONITOR')
        const $ = new VersionMon({
            version: APP.version,
            build: APP.build,
            channel: APP.channel,
            announce: (version,build) => {
                if (version && build) {
                    L('main: UPDATE AVAILABLE',version,build)
                    setUpdatePending(true)
                }
                else {
                    setUpdatePending(false)
                }
            },
        })
        return () => {
            D('DESTROYING VERMON')
            $?.destroy()
        }
    },[])

    // LOAD sessions...
    // (if not already loaded or local sessions cannot be found,
    // as would happen with a "clear local storage")
    if (!$SessionsLoaded || !LocalStore.loadSessions()) {
        D('LOADING SESSIONS')
        $SessionsLoaded = true
        ;(async () => {
            let sessions: TRecSession = {}
            try {
                sessions = await loadSessions()
                D('LOADED SESSIONS')
            }
            catch (e) {
                E('FAILED TO LOAD sessions','REASON:',e)
                setError('Error loading remote sessions',e)
            }
            resetSessions(sessions)
        })()
        .catch(e =>  {
            W('FAILED loading sessions',e)
            return {}
        })
    }

    let $toastEntries: Array<{sink: TMidiToastSink, label: string, verb: string | boolean}> = []

    // Icon for each MIDI sink type
    function midiToastIcon (sink: TMidiToastSink): React.ReactNode {
        if (sink === 'node') return <NodeIcon size={18}/>
        const symbols: Record<string, string> = {
            chain: 'flowchart',
            abgroup: 'mediation',
            session: 'dataset',
            mute: 'settings',
        }
        return <Div k={['symbols', 'toast-icon']}>{symbols[sink]}</Div>
    }

    // Resolve verb to display string
    function midiToastVerb (verb: string | boolean): string {
        if (verb === true) return 'activated'
        if (verb === false) return 'deactivated'
        return verb
    }

    // Single entry row: icon + label + verb
    function MidiToastRow ({sink, label, verb}: {sink: TMidiToastSink, label: string, verb: string | boolean}): React.ReactElement {
        return <>
            {midiToastIcon(sink)}
            <Div k='toast-body'>
                <Span k='toast-label'>{label}</Span>
                &nbsp;
                <Span k='toast-verb'>{midiToastVerb(verb)}</Span>
            </Div>
        </>
    }

    function makeMidiToast (
        sink: TMidiToastSink,
        label: string,
        verb: string | boolean,
        note: IMidiNote,
    ) {
        $toastEntries.push({sink, label, verb})
        const midiNote = `[${note.channel}] ${note.pitch.spn} (${note.note})`

        // Single entry: compact inline layout
        const content = $toastEntries.length === 1
            ? <Div k='toast'>
                <MidiToastRow {...{sink, label, verb}}/>
                &nbsp;
                <Span k='toast-note'>MIDI {midiNote}</Span>
              </Div>
            : <Div k='toast' s={{flexDirection: 'column', alignItems: 'stretch'}}>
                <Div k={['toast-header', 'toast-divider']}>
                    <Span k='toast-header-label'>MIDI Trigger</Span>
                    <Span k='toast-header-note'>{midiNote}</Span>
                </Div>
                {$toastEntries.map((entry, i) =>
                    <Div key={i} k={['toast-row', i < $toastEntries.length - 1 && 'toast-divider']}>
                        <MidiToastRow {...entry}/>
                    </Div>
                )}
              </Div>

        toast(content, { id: 'toast', style: TOAST_STYLE })
    }

    class MidiLearn implements IMidiLearn {
        learners: TMidiSink[]
        constructor () {
            this.learners = []
        }

        private has (learner: TMidiSink) : boolean {
            return !!this.learners.find(_ => MidiSink.equal(learner,_))
        }

        /**
         * 
         * @param self 
         * @returns 
         */
        state (learner: TMidiSink) : TMidiLearnState {
            return this.has(learner)
            ? 'learning'
            : 'eligible'
        }

        /**
         * TOGGLE learning state of (candidate) sink
         * @param learner 
         */
        learn (learner: TMidiSink) {
            if (this.learners.find(_ => MidiSink.equal(learner,_))) {
                this.remove(learner)
            }
            else {
                this.add(learner)
            }
        }

        add (learner: TMidiSink) {
            // NOT EXISTS?
            if (!this.learners.find(_ => MidiSink.equal(learner,_))) {
                // MUTE and SESSIONs are EXCLUSIVE sinks
                if (MidiSink.exclusive(learner)) {
                    this.learners = [learner]
                }
                // NON-EXVCLUSIVE sinks
                else {
                    // FILTER out EXCLUSIVE sinks
                    this.learners = this.learners.filter(_ => !MidiSink.exclusive(_))
                    this.learners.push(learner)
                }
                const clone = new MidiLearn()
                clone.learners = [...this.learners]
                setMidiLearn(clone)
            }
        }

        remove (learner: TMidiSink) {
            // EXISTS?
            if (this.learners.find(_ => MidiSink.equal(learner,_))) {
                this.learners = this.learners.filter(_ => !MidiSink.equal(learner,_))
                const clone = new MidiLearn()
                clone.learners = [...this.learners]
                setMidiLearn(clone)
            }
        }

        all () : TMidiSink[] {
            return [...this.learners]
        }
    }

    /**
     * UPDATE application state
     * PRESERVE current state in history
     */
    function updateState ({ephemeral,chains,devices}:{
            ephemeral?:boolean,
            chains?:TRecChain,
            devices?:TRecDeviceDef}) 
    {
        const MAX_CHECKPOINTS = 100
        checkpoints = checkpoints.slice(0,checkpointIndex)

        // MARK that this checkpoint originated from last save of session
        const origin =  $ActiveSession && !$ActiveSession.dirty ? $ActiveSession.modified : undefined

        // IF NONE, SAVE CURRENT STATE to HISTORY if NONE
        if (!checkpoints.length) {
            checkpoints.push({
                origin,
                chains: DeSer.writeChains($Chains),
                devices: DeSer.writeDevices($Devices),
                timestamp: DateTime.now(),
            })
        }

        // NOTHING TO DO?! OK...
        if (!(chains || devices)) {
            W('updateState invoked w/o state')
            return
        }

        // UPDATE state
        refreshState({chains,devices,ephemeral})

        // COMMIT to HISTORY
        if (!ephemeral) {
            // AT CHECKPOINT (UNDO history) limit?
            // LOSE oldest state entry
            if (checkpoints.length === MAX_CHECKPOINTS) {
                checkpoints = checkpoints.slice(1)
                D('MAX CP',checkpoints.length)
            }
            checkpoints.push({
                origin,
                chains: DeSer.writeChains($Chains),
                devices: DeSer.writeDevices($Devices),
                timestamp: DateTime.now(),
            })
        }

        checkpointIndex = checkpoints.length
    }

    // SET current active session
    function setActive (active?: IActiveSession) {
        LocalStore.saveActive(active)
        $ActiveSession = active
        $setActive(active)
    }

    // RIGHT-side pane is determined by browser type
    function setBrowserType (type: TBrowserTypes) {
        $setBrowserType(type)
        $browserType = type
        if (type === 'chain' || type === 'module') {
            setSpace('chain')
        }
        else {
            setSpace('device')
        }
    }

    // TRIGGER modal alert
    function setAlert (message: any) {
        setPopAlert({
            title: 'ALERT',
            message,
            dismiss: 'OK',
        })
    }

    // TRIGGER modal error alert
    function setError (message:string,error:unknown) {
        setPopAlert({
            title: 'ERROR',
            message: <><p>{message}</p><b>Error</b><p>{inspect(error)}</p></>,
            dismiss: 'OK',
        })
    }

    // TRIGGER modal notice
    function setInfo (message:string) {
        setPopInfo ({
            title: 'INFO',
            message,
            dismiss: 'OK',
        })
    }

    // TRIGGER modal yes/no dialog
    function setPop (args: TPopArgs | undefined) {
        Pop = args
        $setPop(args)
    }

    // ATTEMPT remote save of session
    function saveSession({name,session}:{name:string,session:ISession}) {
        D('SESSION-SAVE',name)
        if (remoteStore) {
            remoteStore
                .saveSession({name,session})
                // XXX non-modal toast/banner notification
                .catch(e => setError('Failed to save session (remotely)',e))
        }
    }

    function getSelectedChain() : IChain | undefined {
        return $Chains
        .filter(({cond:{selected}}) => selected)[0]
    }

    /**
     * COLLECT chains whom have conflicting active ports with given chain
     * @param chain 
     * @param chains 
     * @returns 
     */
    function getConflicts (chain: IChain,chains:IChain[], arch:IArch) : Array<{chain: IChain, nodes: Array<{module: IModule, ports: string[]}>}> {
        const $chain = clone(chain)
        $chain.cond.active = true
        // INDEX all active ports of chain
        const ports = getActivePorts($chain,$Arch)
        if (!Object.entries(ports).length) return []

        // INDEX conflicting ports of other active chains
        return Object.values(
            chains
            .filter(({id}) => id !== chain.id) // only other chains
            .reduce((p,chain) => {
                const other = getActivePorts(chain,$Arch)
                // Track modules and their conflicting ports
                const conflicts: Record<string, {module: IModule, ports: string[]}> = {}
                Object
                    .keys(other.from)
                    // ONLY CONFLICTING ports
                    .filter(a => Object.keys(ports.from).some(b => arch.ports.isConflict(a,b)))
                    .forEach(port => {
                        const module = other.from[port]
                        if (!conflicts[module.id]) {
                            conflicts[module.id] = {module, ports: []}
                        }
                        conflicts[module.id].ports.push(port)
                    })
                Object
                    .keys(other.to)
                    // ONLY CONFLICTING ports
                    .filter(a => Object.keys(ports.to).some(b => arch.ports.isConflict(a,b)))
                    .forEach(port => {
                        const module = other.to[port]
                        if (!conflicts[module.id]) {
                            conflicts[module.id] = {module, ports: []}
                        }
                        conflicts[module.id].ports.push(port)
                    })
                // CONFLICTS FOUND?
                if (Object.keys(conflicts).length) {
                    p[chain.id] = {
                        chain,
                        nodes: Object.values(conflicts),
                    }
                }
                return p
            },{})
        )
    }

    /**
     * PROCESS incoming MIDI note
     * @param event 
     * @returns 
     */
    function onMidiNote (event:IMidiNoteEvent) {
        D('ONMIDINOTE',event)
        $toastEntries = []
        const $key = `${event.channel}-${event.note}`
        // DEBOUNCE???
        const $last = $MidiEvents[$key]
        // APPLY DEBOUNCE?
        // ON-OFF for same note/channel 
        if ($last && $last.on && event.off) {
            const DEBOUNCE_MS = 400
            if ((event.time - $last.time) < DEBOUNCE_MS) {
                // DEBOUNCE
                D('DEBOUNCED:',MidiNote.toString(event))
                return
            }
        }
        // BOOKKEEP "ON" event
        if (event.on) {
            $MidiEvents[$key] = event
        }
        if ($midiOneShot) {
            D('MIDIONESHOT',$midiOneShot)
            $midiOneShot(event)
            $midiOneShot = undefined
        }
        else if ($midiLearn?.all()) {
            if (!event.on) return
            const note = MidiNote.toString(event)
            $midiLearn.all().forEach(entity => {
                const $chain = MidiSink.chain(entity)?.chain
                // CHAIN or CHAIN MODULE
                if ($chain) {
                    const chain = $Chains.find(({id}) => id === $chain)
                    // XXX PANIC
                    if (!chain) return
                    // GROUP cycle
                    if (MidiSink.group(entity)) {
                        const learner = MidiSink.group(entity)!
                        const cells = Chain.cells(chain).filter(({ab}) => ab)
                        const index = cells.findIndex(({id}) => learner.group === id)
                        const group = cells[index]
                        if (group) {
                            group.midi = clone(event)
                            makeMidiToast('abgroup',`${chain.state.name} → AB-group #${index + 1}`,'assigned to',event)
                        }
                    }
                    // NODE activate
                    else if (MidiSink.node(entity)) {
                        const learner = MidiSink.node(entity)!
                        const node = Chain.nodes(chain).find(({id}) => learner.node === id)
                        if (node) {
                            node.midi = {
                                activate: {
                                    L: clone(event),
                                    R: clone(event),
                                }
                            }
                            makeMidiToast('node',`${chain.state.name} → ${Module.fullName(node)}`,'assigned to',event)
                        }
                    }
                    // CHAIN activate
                    else {
                        chain.state.midi = chain.state.midi || {}
                        chain.state.midi.activate = clone(event)
                        makeMidiToast('chain',chain.state.name,'assigned to',event)
                    }
                }
                // MUTE
                else if (MidiSink.mute(entity)) {
                    // MUTE is EXCLUSIVE for NOTE
                    midiController.clear(event)
                    $AppState.settings.mute.midi = clone(event)
                    setAppState({...$AppState})
                    LocalStore.saveApp($AppState)
                    makeMidiToast('mute',`Mute All`,'assigned to',event)
                }
                // SESSION
                else if (MidiSink.session(entity)) {
                    const session = Object.values($Sessions).find(({id}) => id === entity['session'])
                    if (session) {
                        // SESSION is EXCLUSIVE for NOTE
                        midiController.clear(event)
                        session.meta.midi = clone(event)
                        makeMidiToast('session',session.name,'assigned to',event)
                    }
                }
            })
            refreshState({chains:$Chains})
            setMidiLearn(undefined)
        }
        else {
            if (DOC_PARAMS.debug != null) {
                setMidiEvent(event)
            }
            /*
            else {
                const message = <Div s={{marginLeft: '5px'}}>
                    <Div>
                        <em>MIDI note: {MidiNote.toString(event)} (vel {event.velocity})</em>
                    </Div>
                    {event.port &&
                    <Div>
                        <em>{`Port: ${event.port.name ?? event.port.id}`}</em>
                    </Div>}
                </Div>
                toast.success(<Div k='toast'>{message}</Div>)
            }
                */

            const note = event

            let chains: TRecChain = []
            const indexer = (note?:IMidiNote) : string | undefined => note && MidiNote.toString(note)
            const allSinks = collectSinks({indexer,chains:$Chains,app:$AppState,sessions:$Sessions})
            // FIND all SINKS for incoming MIDI note/event
            const sinks = allSinks[MidiNote.toString(event)]
            if (!sinks) return

            // TRACK chains triggered, directly/indirectly by MIDI event/note
            const triggered : Record<string,boolean|null> = {}

            function findChain (id:string|undefined,$active?:boolean) : IChain | undefined {
                if (!id || !$Chains.find(chain => chain.id === id)) return
                if (!chains.length) {
                    chains = clone($Chains)
                }
                const chain = chains.find(chain => chain.id === id)!
                // RECORD initial state of a "triggered" chain (one that is affected by MIDI event
                if (triggered[chain.id] === undefined) {
                    triggered[chain.id] = null
                }
                // IGNORE inactive chain?
                //if ($active && !chain.cond.active) return
                return chain
            }

            if (sinks.some(MidiSink.mute)) {
                const mute = note.on === 'hard'
                    ? true
                    : note.on
                    ? !$AppState.cond.muteAll
                    : false

                if (mute && !$AppState.cond.muteAll) {
                    setTimeout(() => {
                        const muted = $Chains.filter(({cond:{active}}) => active)
                        muted.forEach(({cond}) => cond.active = false)
                        refreshState({chains:$Chains})
                        $AppState.cond.muteAll = true
                        setMuteAll(
                            muted
                            .reduce((p,c) => {
                                p[c.id] = true
                                return p
                            },{})
                        )
                    },0)
                }
                else if (!mute && $AppState.cond.muteAll) {
                    setTimeout(() => {
                        // RESTORE muted chains
                        $Chains
                            .filter(({id}) => $MuteAll![id])
                            .forEach(({cond}) => cond.active = true)
                        refreshState({chains:$Chains})
                        D('CLEAR MUTE ALL')
                        $AppState.cond.muteAll = false
                        setMuteAll(undefined)
                    },0)
                }
                if (mute !== $AppState.cond.muteAll) {
                    makeMidiToast('mute','MUTE ALL',mute,event)
                }
            }
            
            if ($AppState.cond.muteAll) return

            // SESSION loads ARE HIGHEST PRIORITY
            if (sinks.some(MidiSink.session) && note.on) {
                const $session = MidiSink.session(sinks.find(MidiSink.session)!)!
                const session = Object.values($Sessions).find(({id}) => id === $session.session)
                if (!session) return
                // SWITCH session?
                if (session.name !== $ActiveSession?.name) {
                    setTimeout(() => loadSession(session.name), 0)
                    makeMidiToast('session',session.name,'loaded by',event)
                    return
                }
            }

            // CHAIN GROUP activations
            sinks
                .filter(MidiSink.group)
                .filter(_ => findChain(MidiSink.chain(_)!.chain))
                .map(sink => ({
                    target: MidiSink.group(sink)!.group,
                    chain: findChain(MidiSink.chain(sink)!.chain!)!
                }))
                .forEach(({target,chain}) => {
                    const cells = Chain.cells(chain).filter(({ab}) => ab)
                    const index = cells.findIndex(({id}) => id === target)
                    const cell = cells[index]
                    // GROUP NOT FOUND?! NOT AB GROUP?!
                    if (!cell) return
                    const {L,R} = cell.state.active
                    if (note.on) {
                        D('MIDI: AB-GROUP ROLL',chain.id,cell.id)
                        let item = 0
                        // R-side active, only
                        if (L == null && R != null) {
                            item = R + 1
                        }
                        // L-side and (maybe) R-side active (SYNC R to L and increment)
                        else if (L != null) {
                            item = L + 1
                        }
                        // ROLLOVER
                        if (item >= cell.items.length) {
                            item = 0
                        }
                        // FIND non-empty item
                        while (item > 0 && ChainCellItem.isEmpty(cell.items[item])) {
                            item--
                        }
                        // ACTIVATE ONLY existing NODES
                        cell.state.active = {
                            L: cell.items[item]?.L ? item : undefined,
                            R: cell.items[item]?.R ? item : undefined,
                        }
                        triggered[chain.id] = true
                        makeMidiToast('abgroup',`${chain.state.name} → AB-group #${index + 1}`,'trigged by',event)
                    }
                    // DEACTIVATE AB-GROUP
                    else {
                        D('MIDI: AB-GROUP OFF',chain.id,cell.id)
                        cell.state.active = {
                            L: undefined,
                            R: undefined,
                        }
                        makeMidiToast('abgroup',`${chain.state.name} → AB-group #${index + 1}`,false,event)
                    }
                })
            // CHAIN NODE activations
            sinks
                .filter(MidiSink.node)
                .filter(_ => findChain(MidiSink.chain(_)!.chain))
                .map(sink => ({
                    target: MidiSink.node(sink)!.node,
                    chain: findChain(MidiSink.chain(sink)!.chain!)!
                }))
                .forEach(({target,chain}) => {
                    // CHAIN NODE
                    const node = Chain.nodes(chain).find(({id}) => id === target)
                    // NODE NOT FOUND?!
                    if (!node) return
                    const cell = Chain.cells(chain).find(cell => cell.items.some(({L,R}) => L?.id === node.id || R?.id === node.id))
                    // CELL NOT FOUND?!
                    if (!cell) return
                    const itemL = cell.items.findIndex(({L}) => L?.id === node.id)
                    const itemR = cell.items.findIndex(({R}) => R?.id === node.id)
                    // NODE NOT FOUND?!
                    if (itemL === -1 && itemR === -1) return
                    const chan = itemL >= 0 ? 'L' : 'R'
                    const item = chan === 'L' ? itemL : itemR
                    const {state} = cell
                    const active = state.active[chan] != null
                    // ACTIVATE
                    if (note.on) {
                        // FORCE ON
                        if (note.on === 'hard') {
                            D('MIDI: NODE (chain) ON',chan,item,node)
                            state.active[chan] = item
                            if (node.stereo) state.active.R = item
                        }
                        // TOGGLE
                        else {
                            D('MIDI: NODE (chain) TOGGLE',chan,item,node)
                            state.active[chan] = state.active[chan] === item ? undefined : item
                            if (node.stereo) state.active.R = state.active.L
                        }
                    }
                    // FORCE OFF
                    else {
                        D('MIDI: CHAIN NODE OFF',node)
                        state.active[chan] = undefined
                        if (node.stereo) state.active.R = undefined
                    }

                    if (active !== (state.active[chan] != null)) {
                        makeMidiToast('node',`${chain.state.name} → ${Module.fullName(node)}`,!active,event)
                    }
                })
            // CHAIN activations
            sinks
                .filter(_ => MidiSink.chain(_) && !MidiSink.node(_) && !MidiSink.group(_))
                .map(_ => findChain(MidiSink.chain(_)?.chain!))
                .filter(_ => _).map(_ => _!)
                .forEach(chain => {
                    const {cond} = chain
                    // ACTIVATE CHAIN
                    if (note.on) {
                        // FORCE ON
                        if (note.on === 'hard') {
                            D('MIDI: CHAIN ON',chain.id)
                            triggered[chain.id] = true
                        }
                        // TOGGLE
                        else {
                            D('MIDI: CHAIN TOGGLE',chain.id)
                            triggered[chain.id] = !cond.active
                        }
                    }
                    else {
                        D('MIDI: CHAIN OFF',chain.id)
                        triggered[chain.id] = false
                    }
                })
            
            // TRACK (ultimately) activated chains
            const activated : Record<string,IChain> = {}
            const deactivated : Record<string,{chain: IChain, indirect?: boolean}> = {}
            // ADJUST CHAIN state for any node activations/deactivations
            Object
                .entries(triggered) 
                .forEach(([id, active])=> {
                    const chain = chains.find(_ => _.id === id)
                    // NOT FOUND?!?!
                    if (!chain) return
                    // SKIP invalid chains
                    if (Chain.invalid(chain,$Arch)) return
                    const {cond} = chain
                    // ATTEMPT activation of NON-CONTINUOUS chain
                    if (active && !Chain.hasContinuity(chain,$Arch)) {
                        // MODIFY chain state to activate at least one signal path
                        const $chain = Chain.continue(chain,$Arch)
                        if ($chain) {
                            $chain.cond.active = true
                            // REPLACE with presumably "fixed up" chain
                            chains[chains.findIndex(_ => _.id === id)] = $chain
                            activated[id] = $chain
                        }
                        // FAILED?!?!
                        else {
                            if (cond.active) {
                                cond.active = false
                                deactivated[id] = { chain }
                            }
                        }
                    }
                    // ACTIVATED
                    else if (active && !cond.active) {
                        cond.active = true
                        activated[id] = chain
                    }
                    // DEACTIVATED
                    else if (active === false && cond.active) {
                        cond.active = false
                        deactivated[id] = { chain }
                    }
                    // INDIRECT chain DEACTIVATION
                    // (node deactivation has invalidated chain)
                    else if (active === null && cond.active && !Chain.hasContinuity(chain,$Arch)) {
                        cond.active = false
                        deactivated[id] = { chain, indirect: true }
                    }
                })
            // DISCOVER and RESOLVE CONFLICTS
            // XXX FIRST WINS
            Object
                .values(activated)
                // IGNORE NORMALS
                .filter(chain => !chain.state.normal)
                .forEach(chain => {
                    if (chain.cond.active) {
                        Chain
                            .conflicts(chain, chains.filter((chain => chain.cond.active && Chain.hasContinuity(chain,$Arch))),$Arch)
                            .forEach(({chain}) => {
                                delete activated[chain.id]
                                chain.cond.active = false
                            })
                    }
                })
            // ANNOUNCE CHAIN activations
            Object
                .values(activated)
                .forEach(chain => {
                    makeMidiToast('chain',chain.state.name,true,event)
                })
            // ANNOUNCE CHAIN DE-activations
            Object
                .values(deactivated)
                .forEach(({chain,indirect}) => {
                    if (indirect) {
                        makeMidiToast('chain',chain.state.name,'indirectly deactivated by',event)
                    }
                    else {
                        makeMidiToast('chain',chain.state.name,false,event)
                    }
                })

            // REACTIVATE NORMALS
            chains
                .filter(chain => chain.state.normal && Chain.hasContinuity(chain,$Arch))
                .forEach(chain => {
                    const conflicts = Chain.conflicts(
                        chain,
                        // ACTIVE chains
                        chains.filter(({cond:{active}}) => active),
                        $Arch,
                    )
                    // ACTIVATE eligible normal - conflict free
                    if (!conflicts.length) {
                        chain.cond.active = true
                    }
                    // DEACTIVATE ineligible normal
                    else {
                        chain.cond.active = false
                    }
            })
            if (chains.length) {
                refreshState({chains})
            }
        }
    }

    // STORE (locally) all known sessions
    function resetSessions (sessions: TRecSession) {
        const demoBoot = demoBootRef.current
        let demoReset: ISession | undefined

        // Demo endpoints can be revisited under a different ?hw.model= on the
        // same origin. Rebuild only the canned Demo session/state; keep any
        // user-created sessions intact.
        if (DEMO && demoBoot && !demoBoot.resetApplied && demoSessionNeedsReset({
            currentModel: demoBoot.currentModel,
            demoSession: sessions[SESSION_DEMO],
            firstVisit: demoBoot.firstVisit,
            storedModel: demoBoot.storedModel,
        })) {
            const refreshed = refreshDemoSessionForArch({arch: $Arch, sessions})
            sessions = refreshed.sessions
            demoReset = refreshed.demo
            demoBoot.resetApplied = true
            demoBoot.storedModel = demoBoot.currentModel
            localStorage.setItem(KEY_DEMO_HW_MODEL,demoBoot.currentModel)
        }

        const nodef = !sessions[SESSION_DEFAULT]
        // CREATE Demo session
        if (!sessions[SESSION_DEMO]) {
            sessions[SESSION_DEMO] = Snapshots($Arch)[SESSION_DEMO]
        }
        // CREATE default session
        if (!sessions[SESSION_DEFAULT]) {
            sessions[SESSION_DEFAULT] = Snapshots($Arch)[SESSION_DEFAULT]
        }
        const name =
            // After an arch reset, surface the freshly-rebuilt Demo as the active session.
            demoReset
            ? SESSION_DEMO :
            // NO ACTIVE session or UNKNOWN session name
            ($ActiveSession == null || !sessions[$ActiveSession.name])
            ? (DEMO ? SESSION_DEMO : SESSION_DEFAULT)
            : $ActiveSession.name
        const session = sessions[name]
        if (demoReset) {
            // resetState() refreshes chains/devices immediately; setActive()
            // below then restores Demo to a clean, non-dirty active session.
            resetState({chains: demoReset.chains, devices: demoReset.devices})
        }
        setActive({
            name,
            id: sessions[name]?.id,
            modified: session.meta.modified,
            // XXX not always correct
            dirty: demoReset ? false : !nodef,
        })
        $Sessions = clone(sessions)
        LocalStore.saveSessions($Sessions)
        setSessions($Sessions)
    }

    /**
     * RESET application state (exception sessions)
     */
    function resetState ({chains=[],devices={}} : {
            chains?:TRecChain
            devices?:TRecDeviceDef
        }) 
    {
        D('RESET STATE')
        CHAIN_EPOCH++
        DEVICE_EPOCH++
        checkpointIndex = 0
        checkpoints = []
        refreshState({chains,devices})
    }

    /**
     * CLEAR application state (exception sessions)
     */
    function clearState () {
        resetState({})
    }

    /**
     * CLEAR all sessions, locally and remotely
     */
    function clearSessions () {
        D('SESSIONS-CLEAR');
        (remoteStore ? remoteStore.removeSessions() : Promise.resolve())
        .then(() => {
            setActive()
            resetSessions({})
        })
        .catch(e => {
            setError('UNABLE to remove all remote sessions',e)
        })
    }

    /**
     * REFRESH application state
     */
    function refreshState ({chains:$chains,devices,ephemeral}:{
            chains?:TRecChain,
            devices?:TRecDeviceDef
            ephemeral?:boolean
        }) 
    {
        D('REFRESH STATE')
        // Show offline warning when user makes changes while disconnected
        // (skip on Demo Device which has no hardware)
        // Use apiStateRef.current to avoid stale closure in async MIDI callbacks
        if ($AppState.settings.offlineWarn && !apiStateRef.current && apiHost && !DEMO_DEVICE) {
            setOfflineNotice(true)
        }
        // POSSIBLE only devices are being refreshed
        // ...but we always refresh chains
        const chains:TRecChain = clone($chains ?? $Chains)

        // REFRESH devices
        if (devices) {
            $Devices = clone(devices)
            LocalStore.saveDevices($Devices)
            setDevices(clone($Devices))
        
            // REFRESH "view objects"
            const nodes = Module
                .fromDefs(DeviceDef.extractNodes($Devices),$Arch)
                .reduce((p,c) => {
                    p[c.id] = c
                    return p
                },{})
            chains.forEach(chain => Chain.refreshNodes(chain,nodes))
        }
        
        // REFRESH chain state
        $Chains.length = 0
        chains.forEach((chain,i) => $Chains[i] = chain)
        LocalStore.saveChains($Chains)
        setChains(clone($Chains))

        // REFRESH CURRENT browser device that may be disturbed by device refresh
        // ASSIGN default/replacement device
        if (browserDevice && !$Devices[browserDevice]) {
            const device = Object
                .values($Devices)
                .sort((a,b) => a.name.localeCompare(b.name))[0]
            setBrowserDevice(device?.id)
        }

        // PUSH refreshed route state
        // Use apiStateRef.current to get fresh state value in async MIDI callbacks
        routeChains({
            chains: $Chains,
            arch: $Arch,
            setAPIState,
            host: apiHost,
            state: apiStateRef.current,
        })

        // MARK current session as "dirty"
        if ($ActiveSession && !ephemeral) {
            setActive({
                ...$ActiveSession,
                dirty: true,
            })
        }
    }

    /**
     * LOAD app state history from designated checkpoint
     * @param index
     */
    function loadCheckpoint (index:number) {
        D('LOAD CHECKPOINT',index)
        // XXX why decrementing index??!?!?!
        const cp = checkpoints[index-1]

        if (!cp) {
            E('UNABLE to load checkpoint at index',index-1)
        }

        const devices = DeSer.readDevices(cp.devices)
        const chains = DeSer.readChains(cp.chains)
        const {origin} = cp

        if (!devices) {
            E('UNABLE to read devices from checkpoint')
            return
        }
        else if (!chains) {
            E('UNABLE to read chains from checkpoint')
            return
        }

        refreshState({chains,devices})
        if ($ActiveSession) {
            const dirty = !origin || $ActiveSession.modified !== origin
            // MARK session as "dirty"
            setActive({
                ...$ActiveSession,
                dirty,
            })
        }
    }

    // REDO app state (load next state in state history)
    const redoState = () => {
        if (checkpointIndex < checkpoints.length) {
            D('REDO',checkpointIndex)
            loadCheckpoint(++checkpointIndex)
        }
    }
    
    // REPORTS if application has any previous state thay may reverted to (i.e. undo)
    function hasState () : boolean {
        return checkpointIndex > 1
    }

    // UNDO app state (load previous state in state history)
    function undoState () : boolean {
        if (checkpointIndex > 1) {
            D('UNDO',checkpointIndex )
            loadCheckpoint(--checkpointIndex)
            return true
        }
        else {
            return false
        }
    }

    const chainIndex = new SearchIndex<IChain>(Chain.tokenize,$Chains)

    function chainSearch (query:string) {
        // CREATE list of unique tokens found in chain
        const hits =
            // FIND matching chains for each token, reduce to set
            chainIndex
            .find(query)
            .reduce((p:Record<string,IChain>,c:any) => {
                c.forEach((chain:IChain) => p[chain.id] = chain)
                return p
            },{})
        setChainFilter({query,hits})
        return query
    }

    /**
     * IMPORT full state backup
     */
    function restoreBackup (el:React.ChangeEvent<HTMLInputElement>) {
        const {files} = el.target
        if (files) {
            const file = files[0]
            const reader = new FileReader()
            reader.onload = (event: ProgressEvent<FileReader>) => {
                // RESET file input element
                // https://claude.ai/share/632d0006-4b13-42f6-8ad4-5dd632960756
                el.target.value = ''
                if (event.target?.result) {
                    D('RESTORING BACKUP')
                    // XXX some sort of "waiting..." modal dialog?
                    const $data = event.target.result as string
                    try {
                        const data = JSON.parse($data)
                        const devices = DeSer.readDevices(data)
                        if (!devices) throw Error('`devices` missing from backup JSON (or error loading)')
                        const chains = DeSer.readChains(data)
                        if (!chains) throw Error('`chains` missing from backup JSON (or error loading)')
                        const sessions : TRecSession =
                            !data.sessions
                            ? {}
                            : DeSer.readSessions(data.sessions) || {}
                        resetState({chains,devices})
                        resetSessions(sessions)
                        // PUSH imported SESSSIONS to REMOTE STORE (if available)
                        Promise.all(
                            remoteStore 
                                ? Object.entries(sessions)
                                    .map(([name,session]) => remoteStore.saveSession({name,session}))
                                : []
                        )
                        .then(() => setInfo(`Successfully restored backup`))
                        .catch(e => setError('Backup was restored but UNABLE to remotely persist imported sessions',e))
                    } catch (err) {
                        setImportErrors(`Unknown error has occurred!\n${(err as Error).stack}`)
                        throw err
                    }
                }
            }
            reader.readAsText(file)
        }
    }

    /**
     * EXPORT full app-state backup (chain+devices+sessions)
     */
    const saveBackup = async () => {
        try {
            // request file handle
            const fh = await window['showSaveFilePicker']({
                suggestedName: 'propatch-backup',
                types: [
                    {
                        description: 'ProPatch backup',
                        accept: { 
                            'application/propatch-backup': ['.propatchb'],
                        }
                    }
                ],
                excludeAcceptAllOption: true
            })
            // create writable stream
            const out = await fh.createWritable()
            // SERIALIZE active session + saved sessions
            await out.write(JSON.stringify({
                schema: SCHEMA_VER,
                session: $ActiveSession,
                devices: DeSer.packDevices($Devices),
                chains: DeSer.packChains($Chains),
                sessions: DeSer.packSessions($Sessions),
            },null,2))
            await out.close()
        } catch (err) {
            // CANCELLED operation?
            const cancelled = err instanceof DOMException && err.message.match(/aborted/)
            if (!cancelled) {
                D('Error saving backup:', err)
                setAlert('Failed to save backup file')
            }
        }
    }

    /**
     * EXPORT active session
     * @param name 
     */
    const exportActiveSession = async (active:IActiveSession) => {
        const session = activeToSession(active)
        if (!session) return
        exportSession(session)
    }

    /**
     * EXPORT session
     * @param name 
     */
    async function exportSession (session: ISession) {
        try {
            // request file handle
            const fh = await window['showSaveFilePicker']({
                suggestedName: `propatch-session-${Names.makeFileSafe(session.name,'session')}`,
                types: [
                    {
                        description: 'ProPatch session',
                        accept: { 
                            'application/propatch-session': ['.propatchs'],
                        }
                    }
                ],
                excludeAcceptAllOption: true,
            })
            // create writable stream
            const out = await fh.createWritable()
            await out.write(JSON.stringify(DeSer.packSession(session),null,2))
            await out.close()
        } catch (err) {
            // CANCELLED operation?
            const cancelled = err instanceof DOMException && err.message.match(/aborted/)
            if (!cancelled) {
                D('Error exporting session:', err)
                setAlert('Failed to export session')
            }
        }
    }

    /**
     * IMPORT session
     * @param el element
     */
    function importSession (el:React.ChangeEvent<HTMLInputElement>) {
        const {files} = el.target
        if (files) {
            const file = files[0]
            const reader = new FileReader()
            reader.onload = (event) => {
                // RESET file input element
                // https://claude.ai/share/632d0006-4b13-42f6-8ad4-5dd632960756
                el.target.value = ''
                if (!event.target?.result) {
                    setError('IMPORT SESSION', 'event.target undefined')
                    return
                }
                const $data = event.target.result.toString()
                try {
                    // XXX migration
                    const data = JSON.parse($data)
                    const {id,name,meta,chains,modules,devices} = data
                    // OLDER schema
                    if (!devices && !modules) {
                        setAlert('Session is obsolete format')
                        return
                    }
                    if (!id || !name || !meta || !chains || !devices) {
                        setAlert('Session is missing required properties')
                        return
                    }
                    const session: ISession = DeSer.unpackSession(data)
                    setPrompt({
                        title: 'ENTER NAME',
                        prompt: 'Enter session name for import',
                        // DEFAULT to imported session name
                        value: name,
                        onClose: name => {
                            setPrompt(undefined)
                            if (name) {
                                // NAME CONFLICT
                                if ($Sessions[name]) {
                                    setPop({
                                        title: 'OVERWRITE?',
                                        prompt: `Session [${name}] already exists. Overwrite?`,
                                        yes: () => {
                                            // ADD new session
                                            resetSessions({
                                                ...$Sessions,
                                                [name]: session,
                                            })
                                            saveSession({name,session})
                                            loadSession(name)
                                            setInfo(`Imported session [${name}]`)
                                        },
                                        no: () => setAlert('Session import canceled'),
                                    })
                                }
                                // RENAME session import (maybe)
                                else {
                                    session.name = name
                                    // ADD new session
                                    resetSessions({
                                        ...$Sessions,
                                        [name]: session
                                    })
                                    saveSession({name,session})
                                    loadSession(name)
                                    setInfo(`Imported session [${name}]`)
                                }
                            }
                            // XXX how did we get here really/
                            else {
                                setAlert('Session import canceled')
                            }
                        }
                    })
                } catch (err) {
                    E('IMPORT SESSION',el)
                    setError('Error importing session',err)
                }
            }
            reader.readAsText(file)
        }
    }

    /**
     * CREATE session from current app state
     * @returns 
     */
    function activeToSession (active: IActiveSession) : ISession {
        const {name,modified} = active
        const $session = $Sessions[name]
        const now = DateTime.now()
        // GENERATE new session if needed
        // XXX kind of risky to not trap this an exception condition?
        return {
            id: $session?.id || Identity.create(),
            name,
            created: $session?.created || modified,
            meta: {
                locked: false,
                ...$session?.meta,
                schema: SCHEMA_VER,
                modified: now,
                accessed: now,
            },
            devices: clone($Devices),
            chains: clone($Chains),
        }
    }

    /**
     * PERSIST current app state to current session (if any)
     */
    function saveActiveSession (active: IActiveSession) {
        let session = activeToSession(active)
        // UPDATE active session
        $Sessions[session.name] = session
        saveSession({name:session.name,session})
        resetSessions($Sessions)
        session = $Sessions[session.name]
        // UPDATE active session
        setActive({
            name: session.name,
            id: session.id,
            modified: session.meta.modified,
            dirty: false,
        })
        if (checkpoints[checkpointIndex-1]) {
            checkpoints[checkpointIndex-1].origin = session.meta.modified
        }
    }

    /**
     * CREATE new session
     */
    function createActiveSession (name:string) {
        D('SESSION-CREATE',name)
        const session = Session.create({name})
        $Sessions[session.name] = session
        saveSession({name:session.name,session})
        resetSessions($Sessions)
        // UPDATE active session
        setActive({
            name: session.name,
            id: session.id,
            modified: session.meta.modified,
            dirty: false,
        })
        if (checkpoints[checkpointIndex]) {
            checkpoints[checkpointIndex].origin = session.meta.modified
        }
    }

    /**
     * CREATE new session from an existing template session
     * Uses Session.copy for device/chain copying with proper ID remapping
     * @param name - Name for the new session
     * @param templateId - Name of the template session to copy from
     * @param importChains - Whether to import chains from the template
     * @returns true on success, false if template not found
     */
    function createFromTemplate(name: string, templateId: string, importChains: boolean): boolean {
        D('SESSION-FROM-TEMPLATE', name, templateId, importChains)

        const template = $Sessions[templateId]
        if (!template) {
            return false
        }

        // Create session copy with all devices and optionally chains
        const session = Session.copy({
            template,
            name,
            importChains,
            arch: $Arch
        })

        // Save session to store
        $Sessions[session.name] = session
        saveSession({ name: session.name, session })
        resetSessions($Sessions)

        // Update active session
        setActive({
            name: session.name,
            id: session.id,
            modified: session.meta.modified,
            dirty: false,
        })

        if (checkpoints[checkpointIndex]) {
            checkpoints[checkpointIndex].origin = session.meta.modified
        }

        return true
    }

    /**
     * COPY current app state to named session
     * @param name
     */
    function copyActiveSession (active: IActiveSession, name: string) {
        const session = activeToSession(active)
        D('SESSION-COPY',session.name,name)
        // MDDIFY active session
        session.name = name
        session.id = Identity.create()

        // CLEAR intransitive properties
        session.meta.midi = undefined
        session.meta.immutable = false

        // SAVE to sessions store
        $Sessions[name] = session
        saveSession({name,session})
        resetSessions($Sessions)
    }

    /**
     * REPLACE (load) active session, locally
     * NO ATTEMPT is made to load remote session because
     * we assume local store is synchronized to remote.
     * @param name
     */
    function loadSession (name:string) {
        D('SESSION-LOAD',name)
        const session = $Sessions[name] 
        if (!session) {
            D('NO session found', name)
        }
        const {chains,devices} = session
        session.meta.accessed = DateTime.now()
        resetState({chains,devices})
        // UPDATE current session
        setActive({
            name: session.name,
            id: session.id,
            modified: session.meta.modified,
            dirty: false,
        })
    }

    /**
     * REMOVE stored session, locally and remotely
     * @param name
     */
    function removeSession (name:string) {
        D('SESSION-DELETE',name)
        // SAVE to sessions store
        delete $Sessions[name]
        remoteStore?.removeSession({name})
        resetSessions($Sessions)
    }

    /**
     * EXPORT current device state in CSV form
     * @param devices 
     */
    const exportDevices = async (devices:IDeviceDef[]) => {
        try {
            // request file handle
            const fh = await window['showSaveFilePicker']({
                suggestedName: 'pp-device-export',
                types: [
                    {
                        description: 'CSV file',
                        accept: { 'text/csv': ['.csv'] }
                    }
                ],
                excludeAcceptAllOption: true
            })
            // create writable stream
            const out = await fh.createWritable()
            const data = excrete(devices, $Arch)
            out.write(data)
            out.write('\n')
            await out.close()
        } catch (e) {
            // user cancelled operation
            const cancelled = e instanceof DOMException &&  e.message.match(/aborted/)
            if (!cancelled) {
                E('Error exporting devices:', e)
                setError('Failed to export devices',e)
            }
        }
    }

    /**
     * IMPORT device state from CSV.
     * RESETS current app state.
     * @param options 
     */
    const importDevices = (e,options?) => {
        const destructive = options?.destructive

        const file = e.target.files[0]
        if (file) {
            const reader = new FileReader()
            reader.onload = (event) => {
                // RESET file input element
                // https://claude.ai/share/632d0006-4b13-42f6-8ad4-5dd632960756
                e.target.value = ''
                if (!event.target?.result) {
                    W('IMPORT DEVICES: event.target undefined')
                    return
                }
                const data = event.target.result?.toString()
                try {
                    const {devices} = ingest(Identity.create, data, $Arch)
                    // DESTRUCTIVE 
                    if (destructive) {
                        resetState({chains:[],devices})
                        setInfo(`Successfully imported devices`)
                    }
                    // NON-DESTRUCTIVE
                    else {
                        throw Error('UNIMPLEMENTED')
                    }
                } catch (err) {
                    if (err instanceof IngestError) {
                        const error = err.errors
                            .map(({row,errors}) => 
                                row == null
                                ? `Import error:\n  ${errors.join('\n  ')}`
                                : Array.isArray(row)
                                ? `Lines ${row.join(',')}:\n  ${errors.join(`\n  `)}`
                                : `Line ${row}:\n  ${errors.join('\n  ')}`
                            )
                            .join('\n')
                        setImportErrors(error)
                    }
                    else {
                        setImportErrors(`Unknown import error has occurred!\n${(err as Error).stack}`)
                    }
                }
            }
            reader.readAsText(file)
        }
    }

    /**
     * PRIMITIVE "transaction" manager for chains
     * HOLDS 2 deep copies of initial chain collection, mutable (chains) and immutable (then)
     * XXX concept of "mutation" needs better explanation
     */
    class TxAction {

        static createTxn ($chains: IChain[]) : ITxAction {
            const chains = clone($chains)
            return {
                chains,
                then: clone(chains),
                find: id => {
                    const index = chains.findIndex(chain => chain.id === id)
                    if (index >= 0) {
                        return {index,chain:chains[index]}
                    }
                    else {
                        throw Error(`Chain ID ${id} not found in transaction`)
                    }
                }
            }
        }

        static resolveConflicts (self: ITxAction,commit: () => void) {
            // INFORM the user when THIS action introduces a same-chain port conflict — two
            // Nodes in one Chain sharing a physical matrix path (e.g. FP15 vs D7 on PP64R).
            // The Chain is force-deactivated below as invalid; this explains why. Triggered
            // on the mutation (once), not on render, so pre-existing arrangements don't nag.
            if (self.mutated) {
                const prevChain = $Chains.find(c => c.id === self.mutated)
                const nextChain = self.chains.find(c => c.id === self.mutated)
                if (nextChain
                    && Chain.invalid(nextChain,$Arch) === Chain.Invalid.Conflict
                    && (!prevChain || Chain.invalid(prevChain,$Arch) !== Chain.Invalid.Conflict)) {
                    const items = sameChainConflictItems(nextChain,$Arch)
                    if (items.length) {
                        setConflictInfo({
                            message: <>This Chain has Nodes whose Ports share one physical matrix path — they can’t both be active, so the Chain was left inactive:</>,
                            items,
                            dismiss () { setConflictInfo(undefined) },
                        })
                    }
                }
            }

            // FIRSTLY deactivate all invalid chains
            self.chains
                .filter(chain => Chain.invalid(chain,$Arch))
                .forEach(chain => chain.cond.active = false)

            // INDEX current and transaction chains by ID
            const nowChains:Record<string,IChain> = $Chains.reduce((p,c) => {
                p[c.id] = c
                return p
            },{})
            // CHAIN states previous to transaction
            const nextChains:Record<string,IChain> = self.chains.reduce((p,c) => {
                p[c.id] = c
                return p
            },{})

            /**
             * XXX detect multiple chains mutated/activated within transaction and force ROLLBACK
             */
            // SUBJECT of conflict resolution (if needed)
            let subject: {chain: IChain, validated?:boolean } | undefined
            // FIND mutated chain (if any)
            if (self.mutated) {
                const prev = nowChains[self.mutated]
                const next = nextChains[self.mutated]
                // INVALID->VALID transition
                const validated = !Chain.invalid(next,$Arch) && !!Chain.invalid(prev,$Arch)
                // CONT->NON-CONT transition, capture chain was active at time of invalidation
                const discontinued = !Chain.hasContinuity(next,$Arch) && Chain.hasContinuity(prev,$Arch)
                // NON-CONT-CONT transition, capture chain was inactive at time of invalidation
                const continued = Chain.hasContinuity(next,$Arch) && !Chain.hasContinuity(prev,$Arch)
                    && !prev.cond.active
                // ACTIVATED due to INVALID->VALID transition
                if (validated) {
                    next.cond.active = true
                    $LastForceOff = undefined
                }
                else if (discontinued) {
                    $LastForceOff = undefined
                    // FORCE OFF? CAPTURE chain ID - used to restore chain activation later if chain continuity is restored
                    if (!!next.cond.active) {
                        $LastForceOff = next.id
                    }
                }
                else if (continued) {
                    if ($LastForceOff === next.id) {
                        next.cond.active = true
                    }
                    $LastForceOff = undefined
                }

                if (!discontinued) {
                    $LastForceOff = undefined
                }

                // SYNC active state to chain reality
                if (!Chain.hasContinuity(next,$Arch) || Chain.invalid(next,$Arch)) {
                    next.cond.active = false
                }

                if (next.cond.active) {
                    subject = {
                        chain: next,
                        validated,
                    }
                }
            }
            // IDENTIFY *single* chain that is newly *activated* or *mutated*
            // (we presume this chain was activated or *validated* within transaction)
            // XXX hacky - we should know activated chain explicitly
            else {
                self.chains
                // ONLY chains active in transaction
                .filter(chain => chain.cond.active)
                // NEWLY activated chain or NEW and ACTIVE chain
                // (first wins)
                .some(chain => {
                    if (nowChains[chain.id] && !nowChains[chain.id].cond.active || !nowChains[chain.id]) {
                        return subject = {chain}
                    }
                })
            }

            // RESOLVE conflicts?
            if (subject) {
                const next = subject.chain
                const prev = nowChains[next.id]
                // COLLECT conflicting chains
                const conflicts = getConflicts(
                    next,
                    // ACTIVE + NOT NORMALED chains
                    self.chains.filter(({cond:{active},state:{normal}}) => !normal && active),
                    $Arch,
                )
                // CONFLICTS
                if (conflicts.length && !subject.validated) {
                    setConflict({
                        message: <>One or more active Chains have a Node with a conflicting Port:</>,
                        conflicts,
                        deactivate () {
                            // DISABLE other chains
                            conflicts.forEach(({chain}) => chain.cond.active = false)
                            commit()
                            setConflict(undefined)
                        },
                        resolution: {
                            prompt: 'To resolve this conflict, you can either:',
                            choices: ['Deactivate conflicting chains','Cancel operation'],
                            primary: 'DEACTIVATE',
                        },
                        dismiss () {
                            if (self.crossChainDrop) {
                                // CROSS-CHAIN DROP: full revert (cancel the drop entirely)
                                refreshState({chains: clone(self.then)})
                            } else {
                                // CANCEL chain activation
                                next.cond.active = false
                                commit()
                            }
                            setConflict(undefined)
                        }
                    })
                }
                // NO CONFLICTS
                else {
                    // ACTIVATE a VALIDATED chain having ZERO conflicts
                    if (conflicts.length && subject.validated) {
                        next.cond.active = false
                    }
                    commit()
                }
            }
            else {
                commit()
            }
        }

        static commit (self: ITxAction) {
            D('CHAIN TXN COMMIT')
            const {chains,then: original} = self
            // RESOlVE any conflicts
            this.resolveConflicts(self,() => {
                this.activateNormals(self.chains)
                // ONE VISIBLE CHAIN ALWAYS SELECTED
                if (!chains.find(({cond:{selected}}) => selected)) {
                    // NEXT VISIBLE chain after LAST SELECTED (if any)
                    const last = original.findIndex(({cond:{selected}}) => selected)
                    const next = last > -1 && chains.find(({cond:{hidden}},index) => !hidden && index > last)
                    // FIRST VISIBLE chain (if any)
                    const first = chains.find(({cond:{hidden}}) => !hidden)
                    // MARK eligible visible as SELECTED (if any)
                    const select = next || first
                    if (select) {
                        select.cond.selected = true
                    }
                }
                updateState({chains,ephemeral:isEphemeral()})
            })

            /**
             * Is change "ephermal"? This implies we don't add to UNDO history
             */
            function isEphemeral () : boolean {
                // Purely selection state changes?
                // 1. Find selected chain in "now" chain-space
                // 2. Replicate "then" chain-space state
                // 3. Select "then" chain
                // 4. Compare (modified) "then" chain-space directly to "now" state
                // 5. If identical, only change is selection
                function selectOnly () {
                    const then:IChain[] = clone(original)
                    const chain = chains.find(({cond:{selected}}) => selected)
                    if (!chain) return false
                    const $then= then.find(({id}) => id === chain.id)
                    if (!$then) return false
                    // DESELECT all others in original set
                    then
                        .filter(({cond}) => cond.selected)
                        .forEach(({cond}) => cond.selected = false)
                    // SELECT "then" chain
                    $then.cond.selected = true
                    // COMPARE serialized state of chain collections. Identical? EPHEMERAL!
                    const same = JSON.stringify(chains) === JSON.stringify(then)
                    return same
                }

                // Purely activation state changes?
                // 1. Replicate "then" chain-space state
                // 2. Copy activations of "now" state over to "then" state
                // 3. Compare (modified) "then" chain-space directly to "now"
                // 4. If identical, only change is (one-or-more) activations
                function activateOnly () {
                    const then:IChain[] = clone(original)
                    const indexed = chains
                        .reduce((p,c) => {
                            p[c.id] = c
                            return p
                        },{})
                    // ASSIGN "now" activation states to "then"
                    then
                        .forEach(({id,cond}) => {
                            const now = indexed[id]
                            cond.active = now?.cond.active
                        })
                    // COMPARE serialized state of chain collections. Identical? EPHEMERAL!
                    const same = JSON.stringify(chains) === JSON.stringify(then)
                    return same
                }

                // DELTA is exclusively selection and/or activation? EPHEMERAL!
                const ephermal = selectOnly() || activateOnly()
                return ephermal
            }
        }

        // ACTIVATE normaled chain, if eligible
        static activateNormal (chain:IChain,chains:IChain[]) {
            const {cond} = chain
            // INDEX conflicting modules of other active chains
            const conflicts = getConflicts(
                chain,
                // ACTIVE chains
                chains.filter(({cond:{active}}) => active),
                $Arch,
            )
            // ACTIVATE eligible normal - conflict free
            if (!conflicts.length) {
                cond.active = true
            }
            // DEACTIVATE ineligible normal - conflicts exist
            else {
                cond.active = false
            }
        }

        // ACTIVATE any eligible normals
        static activateNormals (chains:IChain[]) {
            chains
            .filter(chain => chain.state.normal
                && !Chain.invalid(chain,$Arch) && Chain.hasContinuity(chain,$Arch)) // only valid,normaled chains
            .forEach(chain => this.activateNormal(chain,chains))
        }
    }

    // XXX 
    // Ths controller needs to be DRYed up some.
    // We emulate certain operations such as "deselect", "hide", etc. within other high level operations (ex: `focus`).
    // It is better than we add context to some high-level operations so they can participate within a larger transaction.
    const control:IChainCardController = {
        alert (args) { setPopAlert(args) },
        all () { return clone($Chains) },
        txn() { return TxAction.createTxn(this.all())},
        commit (txn: ITxAction) { TxAction.commit(txn) },
        find (id: string) : IChain {
            const chain = $Chains.find(chain => id === chain.id)
            if (!chain) {
                throw Error(`Chain ID ${id} not found`)
            }
            else {
                return clone(chain)
            }
        },
        // MODIFY chain state/composition
        modify ({
            id,
            state,
            cond,
            chain,
            mutate,
            txn:$txn,
            crossChainRemove,
        }) {
            const txn: ITxAction = $txn ?? this.txn()
            const {chain:next} = txn.find(id)
            if (next.cond.bypass && !cond?.bypass === false) {
                D('CHAIN MODIFY: IMMUTABLE (bypassed)')
                return
            }
            D('CHAIN MODIFY',{id,cond,state})

            // MARK chain as "mutated"
            // XXX explain purpose of this
            if (mutate) {
                txn.mutated = id
            }
            if (state) Object.assign(next.state,{...state})
            if (cond) Object.assign(next.cond,{...cond})
            if (chain) next.lanes = clone(chain.lanes)

            // CROSS-CHAIN MOVE: delete source cell in same txn (single undo entry)
            if (crossChainRemove) {
                txn.crossChainDrop = true
                try {
                    const {chain: srcChain, index: srcIndex} = txn.find(crossChainRemove.chainId)
                    ChainMatrixControl({
                        chain: srcChain,
                        set: (chain) => { txn.chains[srcIndex] = chain },
                        midi: midiController,
                        idgen: Identity.create,
                        arch: $Arch,
                    }).deleteItem({
                        cell: crossChainRemove.cellId,
                        item: crossChainRemove.item,
                    })
                } catch (e) {
                    E('cross-chain remove: failed to delete from source chain', crossChainRemove.chainId, e)
                }
            }

            /** COMMIT deltas */
            function commit () {
                const {index} = txn.find(next!.id)
                txn.chains[index] = next
                // COMMIT transaction if we originated it
                if (!$txn) {
                    control.commit(txn)
                }
            }
            // WHY is this code not just inline?
            commit()
        },
        listen (id,onRender) {
            ChainListeners[id] = onRender
        },
        unlisten (id) {
            delete ChainListeners[id]
        },
        bypass (id,nonmodal) {
            const chain = this.find(id)
            if (chain.state.locked) { D('CHAIN-BYPASS: blocked (locked)', id); return }
            const {bypass} = chain.cond
            // RESTORE bypassed processor states
            if (!nonmodal && bypass) {
                Chain
                    .cells(chain)
                    .filter(cell => cell.proc)
                    .forEach(cell => {
                        const prev = bypass[cell.id]
                        if (prev) {
                            cell.state.active = { ...prev }
                        }
                    })
                this.modify({ 
                    id,
                    chain,
                    cond: { bypass: false, },
                    mutate: true,
                })
            }
            // BYPASS: capture processor states and deactivate
            else {
                const bypass: Record<string, { L?: number; R?: number }> = {}
                Chain
                    .cells(chain)
                    .filter(cell => cell.proc)
                    .forEach(cell => {
                        bypass[cell.id] = { ...(cell.state.active || {}) }
                        cell.state.active = {}
                    })
                this.modify({ 
                    id,
                    chain,
                    cond: { bypass: nonmodal ? false : bypass },
                    mutate: true,
                })
            }
        },
        insertItem (id,module) {
            const txn = this.txn()
            const {chain} = txn.find(id)
            // IMMUTABLE
            if (chain.state.locked) { D('CHAIN-INSERT: blocked (locked)', id); return }
            if (chain.cond.bypass) return
            // GUARD: don't insert a node already present in this chain (double-click path,
            // which bypasses onDrop). Same-chain duplicate is never allowed.
            if (Chain.hasNode({chain, module})) { D('CHAIN-INSERT: blocked (duplicate)', module.id); return }
            const item = Chain.insertNode({chain,module,idgen:Identity.create,arch:$Arch})
            if (item) {
                D('CHAIN-NODE-INS',id,module.id,item.channel)
                txn.mutated = id
                // CHAIN contents have been modified
                this.commit(txn)
            }
            return item
        },
        name (id,name) {
            D('CHAIN-NAME',id,name)
            control.modify({id,state:{name}})
        },
        color (id,color) {
            D('CHAIN-COLOR',id,color)
            control.modify({id,state:{color}})
        },
        hide (id,$txn) {
            D('CHAIN-HIDE',id)
            const txn = $txn ?? this.txn()
            // UNFOCUS (if needed)
            control.focus(id,false,txn)
            control.modify({id,txn,cond:{hidden: true, selected: false}})
            // ONLY commit if callee originated transaction
            if (!$txn) this.commit(txn)
        },
        // TOGGLE fave state
        fave (id) {
            D('CHAIN-FAVE',id)
            const chain = this.find(id)
            const {state:{fave}} = chain
            control.modify({id,state:{fave:!fave}})
        },
        lock (id) {
            D('CHAIN-LOCK',id)
            const chain = this.find(id)
            control.modify({id,state:{locked:!chain.state.locked}})
        },
        show (id,replace) {
            D('CHAIN-SHOW',id)
            const txn = this.txn()
            // UNHIDE
            control.modify({id,txn,cond:{hidden: false}})
            const focusIdx = txn.chains.findIndex(chain => chain.cond.focus)
            const selectIdx = !replace ? -1 : txn.chains.findIndex(chain => chain.cond.selected)
            const replaceIdx = focusIdx > -1 ? focusIdx : selectIdx
            // HIDE and UNFOCUS currently TARGETED/SELECTED chain, if any
            if (replaceIdx > -1) {
                const hiddenIdx = txn.chains.findIndex(chain => chain.id === id)
                const replace = txn.chains[replaceIdx]
                control.hide(replace.id,txn)
                // SWITCHAROO current hidden <-> current focused/selected
                txn.chains[replaceIdx] = txn.chains[hiddenIdx]
                txn.chains[hiddenIdx] = replace
                if (focusIdx > -1) {
                    // FOCUS replacment
                    control.focus(id,true,txn)
                }
                else {
                    // SELECT replacment
                    control.select(id,txn)
                }
            }
            this.commit(txn)
        },
        // FOCUS state of chain has changed
        focus (id,focus,$txn) {
            D('CHAIN FOCUS')
            const txn = $txn ?? this.txn()
            if (focus) {
                // DEFOCUS any targeted or selected chains
               txn.chains 
                    .filter(_  => _.id !== id)
                    .filter(({cond}) => cond.focus || cond.selected)
                    .forEach(({id}) => control.modify({
                        id,
                        txn,
                        cond: {
                            focus:false,
                            selected: false,
                        },
                    }))
                // TARGET chain
                control.modify({
                    id,
                    txn,
                    cond: {
                        focus: true,
                        selected: true,
                        hidden: false,
                    }
                })
            }
            // REMOVE focus, do not alter highlight/selected status
            else {
                control.modify({
                    id,
                    txn,
                    cond: {
                        focus: false,
                    }
                })
            }
            // ONLY commit if callee originated transaction
            if (!$txn) this.commit(txn)
        },
        // SELECTION condition has changed
        select (id,$txn) {
            D('SELECT',id)
            const txn = $txn ?? this.txn()
            // SELECT chain
            txn.chains 
                .filter(_  => _.id !== id) // other chain
                .filter(({cond}) => cond.selected) // only active chains
                .forEach(({id}) => control.modify({
                    id,
                    txn,
                    cond:{
                        selected: false,
                    }
                }))
            control.modify({id,txn,cond:{selected: true}})
            // ONLY commit if callee originated transaction
            if (!$txn) this.commit(txn)
        },
        // TOGGLE NORMAL state
        normal (id) {
            const chain = this.find(id)
            D('CHAIN-NORMAL',id,!!chain.state.normal)
            if (chain.state.normal) {
                chain.state.normal = false
                chain.cond.active = false
                control.modify({id,state:{normal: false},cond: {active: false}})
            }
            else {
                let normal = !!chain.state.normal
                // INDEX any conflicting chains
                const conflicts = getConflicts(
                    chain,
                    // NORMALED chains
                    this.all().filter(({state:{normal}}) => normal),
                    $Arch,
                )
                // CONFLICTS with other normalled chains
                if (conflicts.length) {
                    setConflict({
                        message: <>One or more <b><i>normaled</i></b> Chains have a Node with a conflicting Port:</>,
                        conflicts,
                        deactivate () {
                            const txn = control.txn()
                            // DENORMAL other chains
                            conflicts.forEach(({chain:{id}})=> 
                                control.modify({id,txn,state:{normal: false},cond: {active: false}})
                            )
                            // NORMALIZE
                            control.modify({id,txn,state:{normal: true}})
                            control.commit(txn)
                            setConflict(undefined)
                        },
                        resolution: {
                            prompt: 'To resolve this conflict, you can either:',
                            choices: ['Denormal conflicting chains','Cancel operation'],
                            primary: 'DENORMAL',
                        },
                        dismiss () {setConflict(undefined)}
                    })
                }
                // ENABLE! (no conflicts)
                else {
                    normal = true
                }
                control.modify({id,state:{normal}})
            }
        },
        // TOGGLE ACTIVE chain state
        active (id,value) {
            const chain = this.find(id)
            // CANNOT toggle state of normaled chain
            if (chain.state.normal) return
            // ACTIVATE state - toggle or set explicitly
            const next = value != null
                ? value 
                : !chain.cond.active
            D('CHAIN-ACT',id,next)
            // DEACTIVATE
            if (!next) {
                control.modify({id,cond:{active:false}})
            }
            // ATTEMPTING to ACTIVATE
            else {
                // CANNOT activate invalid chain
                if (Chain.invalid(chain,$Arch)) return
                const cont = Chain.hasContinuity(chain,$Arch)
                if (!cont) {
                    // MODIFY chain state to activate at least one signal path
                    const $chain = Chain.continue(chain,$Arch)
                    if ($chain) {
                        control.modify({id,chain:$chain,cond:{active:true}})
                    }
                }
                else {
                    control.modify({id,cond:{active:true}})
                }
            }
        },
        // MANAGE chain tags
        tags (id) {
            const self = this
            return {
                // ALL tags of ALL chains
                all () {
                    return TagCache.chain
                    /*
                    return Object
                        .values($Chains)
                        .reduce((p,{state:{tags}}) => {
                            tags.forEach(tag => p[tag.name] = tag)
                            return p
                        },{})
                    */
                },
                // ASSIGN a tag
                assign (tag) {
                    D('CHAIN-TAG-ASSIGN',id,tag.name)
                    const chain = self.find(id)
                    if (chain) {
                        if (!chain.state.tags.some(_ => tag.name === _.name)) {
                            const tags = [...chain.state.tags,tag]
                            control.modify({id,state:{tags}})
                        }
                    }
                },
                // UNASSIGN a tag
                unassign (tag) {
                    D('CHAIN-TAG-UNASSIGN',id,tag.name)
                    const chain = self.find(id)
                    if (chain) {
                        const tags = chain.state.tags.filter(_ => tag.name !== _.name)
                        control.modify({id,state:{tags}})
                    }
                },
                add (tag) {
                    D('CHAIN-TAG-CREATE',tag.name)
                    TagCache.chain[tag.name] = tag
                },
                // MODIFY tag (across all chains)
                modify (tag) {
                    // LOCATE original tag by ID (in case of name change)
                    const $tag = Object.values(TagCache.chain).find(({id}) => tag.id === id)
                    if (!$tag) return
                    // NAME change
                    if ($tag.name !== tag.name) {
                        // REMOVE old from cache
                        delete TagCache.node[$tag.name]
                    }
                    // UPDATE cache
                    TagCache.node[tag.name] = tag
                    const txn = self.txn()
                    // SELECT chain
                    txn.chains 
                        .filter(({state:{tags}}) => tags.some(_ => _.id === tag.id))
                        .forEach(({id, state:{tags}}) => control.modify({
                            id,
                            txn,
                            state: { tags: tags.map(_ => _.id === tag.id ? tag : _)},
                        }))
                    self.commit(txn)
                },
                // REMOVE tag (across all chains)
                remove (tag) {
                    D('CHAIN-TAG-DELETE',tag.name)
                    delete TagCache.chain[tag.name]
                    const txn = self.txn()
                    // SELECT chain
                    txn.chains 
                        .filter(({state:{tags}}) => tags.some(_ => _.id === tag.id))
                        .forEach(({id, state:{tags}}) => control.modify({
                            id,
                            txn,
                            state: { tags: tags.filter(_ => _.id !== tag.id) }
                        }))
                    self.commit(txn)
                }
            }
        },
        copy (id) {
            const $chain = this.find(id)
            const chain = Chain.copy($chain,Identity.create)
            D('CHAIN-COPY',id,chain.id)
            chain.cond = {}
            // RESET MIDI state
            chain.state.midi = undefined
            chain.state.locked = false
            const chains = this.all()

            // DERIVE unique title
            const titles = chains.reduce((names,{state:{name}}) => {
                names[name] = name
                return names
            },{})
            let name = `${chain.state.name} copy`
            let idx = 0
            while (titles[name]) name = `${chain.state.name} copy ${++idx}`
            chain.state.name = name

            // INSERT into CHAIN collection
            const txn = this.txn()
            const index = chains.findIndex(({id})  => id === $chain.id)
            txn.chains.splice(index+1,0,chain)
            this.commit(txn)
        },
        remove (id) {
            const chain = this.find(id)
            if (chain.state.locked) { D('CHAIN-DELETE: blocked (locked)', id); return }
            D('CHAIN-DELETE',id)
            const index = this.all().findIndex(({id})  => id === chain.id)
            // REMOVE from CHAIN collection
            const txn = this.txn()
            txn.chains.splice(index,1)
            this.commit(txn)
        },
        // Move chain to new position in array
        reorder ({id,targetId,after}) {
            const txn = this.txn()
            // Find current position of chain being moved
            const from = txn.chains.findIndex(chain => chain.id === id)
            if (from < 0) return
            // Find target position (or end of array if no target)
            let to = targetId
                ? txn.chains.findIndex(chain => chain.id === targetId)
                : txn.chains.length
            if (to < 0) return
            if (from === to) return
            // Remove chain from current position
            const [moved] = txn.chains.splice(from,1)
            // Adjust target index since array shifted after removal
            if (from < to) {
                to -= 1
            }
            // Calculate final insertion point
            const insertAt = targetId ? (after ? to + 1 : to) : txn.chains.length
            const clamped = Math.max(0, Math.min(insertAt, txn.chains.length))
            // Insert chain at new position
            txn.chains.splice(clamped,0,moved)
            D('CHAIN-REORDER',id,targetId,after)
            this.commit(txn)
        },
        // XXX explain why there are 2 of these
        add () {
            return this.addChain({replace:false})
        },
        addChain ({replace=false}:{replace:boolean}) {
            const txn = this.txn()
            // DERIVE unique default title
            const titles = txn.chains.reduce((names,{state:{name}}) => {
                names[name] = name
                return names
            },{})
            let name = 'Untitled'
            let index = 1
            while (titles[name]) name = `Untitled ${index++}`

            const chain:IChain = {
                id: Identity.create(),
                state: {
                    name,
                    color: $COLORS[colorIdx % $COLORS.length],
                    tags: [],
                },
                cond: { new: true },
                lanes: [
                    {
                        id: Identity.create(),
                        cells: [
                            ChainCell.create({
                                id: Identity.create(), 
                                source: true,
                                items: [ChainCellItem.createStub(false)],
                                split: false,
                            }),
                            ChainCell.create({
                                id: Identity.create(),
                                dest: true,
                                items: [ChainCellItem.createStub(false)],
                                split: false,
                            }),
                        ],
                    },
                ],
            }
            D('CHAIN-CREATE',chain.id)
            // DISCOVER index of currently TARGETED chain (if any)
            const focusIdx = txn.chains.findIndex(chain => chain.cond.focus)
            // DISCOVER index of currently SELECTED chain (if any)
            const selectIdx = !replace ? -1 : txn.chains.findIndex(chain => chain.cond.selected)
            // INSERT new chain at this index
            const newIdx = 
                // REPLACE any targeted chain
                focusIdx > -1 
                ? focusIdx
                // REPLACE selected chain (if replace === true)
                : selectIdx > -1
                ? selectIdx
                // JUST APPEND
                : txn.chains.length

            // HIDE currently targeted/selected chain
            if (focusIdx > -1 || selectIdx > -1) {
                const id = txn.chains[newIdx].id
                // DETARGET
                control.focus(id,false,txn)
                control.hide(id,txn)
                // INSERT new chain
                txn.chains.splice(newIdx,0,chain)
            }
            else {
                txn.chains[newIdx] = chain
            }

            // TARGET new chain
            if (focusIdx > -1) {
                control.focus(chain.id,true,txn)
            }
            // SELECT new chain
            else {
                control.select(chain.id,txn)
            }

            this.commit(txn)
            // XXX why not just return `chain` here?
            return txn.chains[newIdx]
        },
        split (id) {
            const chain = this.find(id)
            if (chain.state.locked) { D('CHAIN-SPLIT: blocked (locked)', id); return }
            D('CHAIN-SPLIT',id)
            // CHAIN split
            if (Chain.isSplit(chain)) return
            Chain.split(chain)
            control.modify({id,chain,mutate:true})
        },
        unsplit (id) {
            const chain = this.find(id)
            if (chain.state.locked) { D('CHAIN-UNSPLIT: blocked (locked)', id); return }
            D('CHAIN-UNSPLIT',id)
            // CHAIN not split
            if (!Chain.isSplit(chain)) return
            Chain.unsplit(chain)
            control.modify({id,chain,mutate:true})
        },
    }

    function addChain (replace:boolean) {
        const chain = control.addChain({replace})
        NEW_CHAIN = chain.id
    }

    const importRef = useRef<HTMLInputElement>(null)
    const newDevModal = useRef<any>()

    const modDefController = ModuleDefController.create({
        setDevice (id:string) {
            if ($Devices[id]) {
                setBrowserDevice(id)
                setBrowserType('device')
            }
        }
    })

    const midiController = new MidiController({
        refreshState: () => refreshState({chains:$Chains}),
        alert: args => setPopAlert(args),
        prompt: args => setPop(args),
    })

    // SHARED MidiBrowser control wiring (standalone dialog + config dialog MIDI tab)
    const makeMidiControl = () => ({
        midi: { menu: midiController },
        chains: {
            update: (chains:TRecChain) => updateState({chains}),
        },
        sessions: {
            update: (sessions:TRecSession) => {
                $Sessions = {...sessions}
                setSessions($Sessions)
            },
        },
        settings: {
            update: settings => {
                $AppState.settings = {
                    ...$AppState.settings,
                    ...settings,
                }
                setAppState({...$AppState})
                LocalStore.saveApp($AppState)
            },
        },
    })

    const modController = (chain:IChain,node:IModule) =>  {
        return ModuleController.create({
            chain,
            node,
            setDevice (id:string) {
                if ($Devices[id]) {
                    setBrowserType('device')
                    setBrowserDevice(id)
                }
            },
            controller: {
                midi: midiController
            },
            midi: {
                activate ({chain:$chain,node:$node,note}) {
                    const chains = clone($Chains)
                    const chain = chains.find(({id})=> $chain === id)
                    if (!chain) return
                    const module = Chain.indexNodes(chain)[$node]
                    if (!module) return
                    module.midi = module.midi || {activate: {}}
                    // DESSASSIGN
                    if (!note) {
                        module.midi.activate = {}
                    }
                    // ASSIGN
                    else {
                        module.midi.activate = {
                            L: note,
                            R: note,
                        }
                    }
                    updateState({chains})
                }
            }
        })
    }

    const sessionControl: ISessionControl = {
        load: session => {
            const target = Sessions[session.name]
            // IGNORE missing session (?!?!?!)
            if (!target) {
                D('No session for name',session.name)
                return
            }
            if ($ActiveSession?.dirty) {
                setPop({
                    title: 'DISCARD CHANGES?',
                    prompt: 'Unsaved changes will be lost. Proceed?',
                    yes: () => {
                        loadSession(session.name)
                    }
                })
            }
            else {
                D('LOADING...')
                loadSession(session.name)
            }
        },
        remove: () => {
            if (!$ActiveSession) return
            const {name} = $ActiveSession
            // NO TARGET?
            if (!$Sessions[name]) return
            const target = $Sessions[name]
            if (!Session.mutable(target)) {
                setAlert(`Session [${name}] is locked or immutable`)
            }
            else {
                setPop({
                    title: 'DELETE?',
                    prompt: `DELETE session [${name}]?`,
                    yes: () => removeSession(name),
                })
            }
        },
        revert: () => {
            if (!$ActiveSession) return
            const {name} = $ActiveSession
            // NO TARGET?
            if (!$Sessions[name]) return
            D('SESSION-REVERT',name)
            const target = $Sessions[name]
            if ($ActiveSession.dirty) {
                setPop({
                    title: 'DISCARD CHANGES?',
                    prompt: 'Unsaved changes will be lost. Proceed?',
                    yes: () => {
                        loadSession(name)
                    }
                })
            }
        },
        midi: note => {
            D('SESSION NOTE',note)
            if (!$ActiveSession) return
            const {name} = $ActiveSession
            // NO TARGET?
            if (!$Sessions[name]) return
            // SESSION NOTE is EXCLUSIVE to EVERYTHING
            if (note) midiController.clear(note)
            const target = $Sessions[name]
            target.meta.midi = note
        },
        lock: lock => {
            if (!$ActiveSession) return
            const {name} = $ActiveSession
            // NO TARGET?
            if (!$Sessions[name]) return
            D('SESSION-LOCK',name)
            const target = $Sessions[name]
            if (!target?.meta.immutable) {
                target.meta.locked = lock
                saveActiveSession($ActiveSession)
            }
        },
        rename: () => {
            if (!$ActiveSession?.name) return
            const curName = $ActiveSession.name
            setPrompt({
                title: 'ENTER NEW NAME',
                prompt: 'Enter new session name',
                // DEFAULT to current session name (if any)
                value: findUniqueSessionName($Sessions,curName),
                onClose: $name => {
                    setPrompt(undefined)
                    if ($name) {
                        const name = $name.trim()
                        // EMPTY
                        if (!name.length) {
                            setAlert('Name is blank/empty')
                        }
                        // TOO LARGE
                        else if (name.length > 64) {
                            setAlert('Name exceeds max length (64)')
                        }
                        // VALID NAME
                        else {
                            // EXISTING session?
                            if ($Sessions[name]) {
                                setAlert(`Session [${name}] already taken`)
                            }
                            else {
                                D('SESSION-NAME',name)
                                $Sessions[name] = $Sessions[curName]
                                delete $Sessions[curName]
                                $Sessions[name].name = name
                                resetSessions($Sessions)
                                setActive({
                                    ...$ActiveSession!,
                                    name,
                                })
                            }
                        }
                    }
                }
            })
        },
        create: () => {
            // Show dirty warning before opening dialog
            if ($ActiveSession?.dirty) {
                setPop({
                    title: 'DISCARD CHANGES?',
                    prompt: 'Unsaved changes will be lost. Proceed?',
                    yes: () => {
                        setCreateSessionDialog(true)
                    }
                })
            } else {
                setCreateSessionDialog(true)
            }
        },
        save: () => $ActiveSession && saveActiveSession($ActiveSession),
        saveas: () => {
            if (!$ActiveSession) return
            setPrompt({
                title: 'ENTER NAME',
                prompt: 'Enter session name',
                // DEFAULT to current session name (if any)
                value: $ActiveSession?.name ? findUniqueSessionName(Sessions,$ActiveSession.name) : SESSION_DEFAULT,
                onClose: $name => {
                    setPrompt(undefined)
                    if ($name) {
                        const name = $name.trim()
                        // EMPTY
                        if (!name.length) {
                            setAlert('Name is blank/empty')
                        }
                        // LARGE
                        else if (name.length > 64) {
                            setAlert('Name exceeds max length (64)')
                        }
                        // NAME valid
                        else {
                            const target = Sessions[name]
                            // EXISTING session?
                            if (target) {
                                if (Session.mutable(target)) {
                                    setPop({
                                        title: 'OVERWRITE',
                                        prompt: `Overwrite existing session [${name}]?`,
                                        yes () {
                                            copyActiveSession(
                                                {...$ActiveSession!},
                                                name,
                                            )
                                            loadSession(name)
                                            setAlert(`Saved session [${name}]`)
                                        }
                                    })
                                }
                                else {
                                    setAlert(`Session [${name}] is read-only and cannot be overwritten`)
                                }
                            }
                            else {
                                copyActiveSession(
                                    {...$ActiveSession!},
                                    name,
                                )
                                loadSession(name)
                                setInfo(`Saved session [${name}]`)
                            }
                        }
                    }
                },
            })
        },
        copy: () => {
            setPrompt({
                title: 'ENTER NAME',
                prompt: 'Enter session copy name',
                // DEFAULT to current session name (if any)
                value: $ActiveSession?.name ? findUniqueSessionName(Sessions,$ActiveSession.name) : SESSION_DEFAULT,
                onClose: $name => {
                    setPrompt(undefined)
                    if ($name) {
                        const name = $name.trim()
                        // EMPTY
                        if (!name.length) {
                            setAlert('Name is blank/empty')
                        }
                        // LARGE
                        else if (name.length > 64) {
                            setAlert('Name exceeds max length (64)')
                        }
                        // NAME valid
                        else {
                            const target = $Sessions[name]
                            // EXISTING session?
                            if (target) {
                                if (!target.meta.immutable) {
                                    setPop({
                                        title: 'OVERWRITE?',
                                        prompt: `Overwrite existing session [${name}]?`,
                                        yes () {
                                            $ActiveSession && copyActiveSession($ActiveSession,name)
                                            setInfo(`Saved session copy [${name}]`)
                                        }
                                    })
                                }
                                else {
                                    setAlert(`Session [${name}] is read-only and cannot be overwritten`)
                                }
                            }
                            else {
                                $ActiveSession && copyActiveSession($ActiveSession,name)
                                setInfo(`Saved session copy [${name}]`)
                            }
                        }
                    }
                },
            })
        },
        import: () => {
            $ActiveSession?.dirty
            ?  setPop({
                title: 'DISCARD CHANGES?',
                prompt: 'Unsaved changes will be lost. Proceed?',
                yes: () => importSessRef.current?.click(),
            })
            : importSessRef.current?.click() 
        },
        export: () => {
            $ActiveSession && exportActiveSession($ActiveSession)
        },
        support: DEMO ? undefined : () => {
            setSupportDialog({
                name: DOC_PARAMS.firstname ?? '',
                email: DOC_PARAMS.email ?? '',
                hasSession: !!$ActiveSession,
                env: APP_ENV,
                updatePending,
                onClose: async (result) => {
                    setSupportDialog(undefined)
                    if (!result) return
                    // BUILD session file (.propatchs format)
                    const session = result.includeSession && $ActiveSession
                        ? activeToSession($ActiveSession)
                        : undefined
                    // BUILD payload
                    const payload = SupportPayload.build({
                        ts: nowTimestamp(),
                        contact: { name: result.name, email: result.email },
                        message: result.message,
                        env: {
                            serial: OP_PARAMS.UNIT ?? null,
                            hw_model: OP_PARAMS.ARCH.model.name,
                            paw: { version: APP.version, channel: APP.channel, build: APP.build },
                            wolffhound: APP_ENV.wolffhound ?? null,
                            system: await AppEnv.system(),
                        },
                        sessionFile: session
                            ? { name: session.name, data: DeSer.packSession(session) }
                            : undefined,
                    })
                    // POST
                    try {
                        const response = await net.UOurL.fetch({
                            url: SUPPORT_URL,
                            options: {
                                method: 'POST',
                                body: JSON.stringify(payload),
                                headers: { 'Content-Type': 'application/json' },
                            },
                            timeout: 15000,
                        })
                        if (!response.ok) throw new Error(`${response.status}`)
                        setInfo('Session sent to support')
                    } catch (e) {
                        D('SUPPORT POST ERROR',e)
                        setAlert('Failed to send to support. Please try again.')
                    }
                },
            })
        },
    }

    const sysMenu: IMenuChoice[] = [
        updatePending && {
            label: 'Load update...',
            k: 'shimmer',
            choose: () => setPop({
                title: 'WARNING: Proceed with UPDATE?',
                prompt: 'You lose any unsaved changes when updating. It is highly recommended to perform full backup of your workspace before continuing. Proceed?',
                yes: () => {
                    // RUNNING as WOLFFHOUND WEBVIEW?
                    if (window['wh']?.reload) {
                        window['wh'].reload()
                    }
                    else {
                        window.location.href = window.location.href
                    }
                }
            }),
        },
        {
            label: 'Configuration...',
            divider: updatePending && 'top',
            choose: () => setSysConfig(true),
        },
        {
            label: 'Backups',
            divider: 'top',
            choices: [
                {
                    label: 'Load...',
                    choose: () => setPop({
                        title: 'DELETE EVERYTHING?',
                        prompt: 'Restoring backup DELETES EVERYTHING. Proceed?',
                        yes: () => restoreRef.current?.click() 
                    })
                },
                {
                    label: 'Save...',
                    choose: () => saveBackup()
                }
            ]
        },
        {
            label: 'Devices',
            divider: 'top',
            choices: [
                {
                    label: 'Import...',
                    choose: () =>
                        setPop({
                            title: 'OVERWRITE EVERYTHING?',
                            prompt: 'Import operation OVERWRITES all DEVICES + CHAINS. Proceed?',
                            yes: () => importRef.current?.click()
                        })
                },
                {
                    label: 'Export...',
                    choose: () => exportDevices(Object.values($Devices))
                },
            ]
        },
        {
            label: 'Data',
            divider: 'top',
            choices: [{
                label: 'Clear All...',
                choose: () =>
                    setPop({
                        title: 'DELETE EVERYTHING?',
                        prompt: "Delete EVERYTHING? This cannot be undone.",
                        yes () {
                            // YIELD to React
                            setTimeout(() => {
                                setPop({
                                    title: 'REALLY SURE?',
                                    prompt: "ARE YOU REALLY SURE? This cannot be undone.",
                                    yes () {
                                        clearState()
                                        clearSessions()
                                    }
                                })
                            },0)
                        }
                    }),
            }]
        },
        {
            label: 'MIDI',
            divider: 'top',
            choices: [
                {
                    label: 'Port',
                    k: 'midi-ports',
                    choices: (() => {
                        // Build port list: selected-but-disconnected ports + available ports
                        const availablePorts = Object.values(midiPorts)
                        const disconnectedSelected = Array.from(selectedPorts.values())
                            .filter(p => p.id !== 'ANY' && !midiPorts[p.id])
                        const allPorts = [...disconnectedSelected, ...availablePorts]

                        const choices: IMenuChoice[] = allPorts
                            .map(port => {
                                const isSelected = selectedPorts.has(port.id)
                                const isAnyPort = port.id === 'ANY'
                                const isConnected = isAnyPort || !!midiPorts[port.id]
                                return {
                                    k: ['port', isSelected && isConnected && 'selected', isSelected && !isConnected && 'bad'],
                                    label: `${isSelected ? '✓ ' : ''}${port.name}${isSelected && !isConnected ? ' (disconnected)' : ''}`,
                                    choose: () => {
                                        const newPorts = new Map(selectedPorts)
                                        if (isSelected) {
                                            newPorts.delete(port.id)
                                        } else if (isAnyPort) {
                                            newPorts.clear()
                                            newPorts.set('ANY', port)
                                        } else {
                                            newPorts.delete('ANY')
                                            newPorts.set(port.id, port)
                                        }
                                        setSelectedPorts(newPorts)
                                        return false
                                    }
                                }
                            })
                        if (selectedPorts.size > 0) {
                            choices.push({
                                k: ['port'],
                                label: 'Clear',
                                divider: 'top',
                                choose: () => {
                                    setSelectedPorts(new Map())
                                    return false
                                }
                            })
                        }
                        return choices
                    })()
                },
                {
                    label: 'Manage...',
                    choose: () => setMidiManager(true),
                }
            ],
        },
        {
            label: `${$AppState.settings.offlineWarn ? '✓ ' : ''}Offline Warning`,
            divider: 'top',
            choose: () => {
                $AppState.settings.offlineWarn = !$AppState.settings.offlineWarn
                setAppState({...$AppState})
                LocalStore.saveApp($AppState)
            },
        },
        {
            label: 'Background Image...',
            choose: () => setBgDialog(true),
        },
    ].filter(_ => _) as IMenuChoice[]

    // Help menu state and items
    const [helpMenuOpen, setHelpMenuOpen] = useState(false)
    const helpRef = useRef<HTMLDivElement>(null)
    const openShortcuts = useCallback(() => {
        if ($HotkeyTargets) setShowShortcuts($HotkeyTargets)
    }, [])
    const openHelp = useCallback(() => {
        window.open('https://www.wolffaudio.com/support/propatch', '_blank')
    }, [])
    const helpMenu = useMemo<IMenuChoice[]>(() => [
        { label: 'Keyboard Shortcuts', choose: openShortcuts },
        { label: 'General Help', choose: openHelp }
    ], [openShortcuts, openHelp])

    return (
        <DndProvider backend={HTML5Backend}>
            {conflict && <ConflictDialog {...conflict} /> }
            {conflictInfo && <ConflictInfoDialog {...conflictInfo} /> }
            {demoDialog &&
                <widget.DemoModal
                    mode={demoDialog}
                    // Keep the first-run demo welcome lightweight; release notes
                    // remain available from App Info and update notifications.
                    changelogHtml={demoDialog === 'demo' ? null : changelogHtml}
                    version={APP.version}
                    dismiss={() => setDemoDialog(null)}/>
            }
            {pop && 
                <widget.Popup 
                    k='binary'
                    title={pop.title}
                    prompt={pop.prompt}
                    no={pop.no}
                    yes={pop.yes}
                    clear={()=>setPop(undefined)}/>
            }
            {appInfo && <widget.AppInfo
                    data={APP_ENV}
                    clear={() => setAppInfo(undefined)}
                    onReleaseNotes={() => {
                        setAppInfo(undefined)
                        setDemoDialog('notes')
                    }}
                />
            }
            {popInfo && 
                <widget.PopAlert
                    k='info'
                    title={popInfo.title}
                    message={popInfo.message}
                    clear={()=>setPopInfo(undefined)}/>
            }
            {popAlert && 
                <widget.PopAlert
                    k='alert'
                    title={popAlert.title}
                    message={popAlert.message}
                    clear={()=>setPopAlert(undefined)}/>
            }
            {muteAll && 
                <widget.PopAlert
                    k='alert'
                    title='ALL MUTE ACTIVE'
                    message='All ProPatch outputs are muted while preserving routing configuration. Click Unmute to restore.'
                    dismiss={'UNMUTE'}
                    options={{ blur: false }}
                    clear={() => {
                        // RESTORE muted chains
                        $Chains
                            .filter(c => muteAll[c.id])
                            .forEach(c => c.cond.active = true)
                        refreshState({chains:$Chains})
                        appState.cond.muteAll = false
                        setMuteAll(undefined)
                    }}
                />
            }
            {prompt &&
                <widget.Prompt
                    k='prompt'
                    value={prompt.value}
                    prompt={prompt.prompt}
                    title={prompt.title}
                    onClose={prompt.onClose}
                />
            }
            {supportDialog &&
                <SupportDialog {...supportDialog}/>
            }
            {createSessionDialog &&
                <CreateSessionDialog
                    sessions={Sessions}
                    defaultSessionName={SESSION_DEFAULT}
                    findUniqueName={findUniqueSessionName}
                    onClose={(result?: ICreateSessionResult) => {
                        setCreateSessionDialog(false)
                        if (!result) return

                        const { name, templateId, importChains } = result

                        if (!templateId) {
                            createActiveSession(name)
                            loadSession(name)
                        } else {
                            const success = createFromTemplate(name, templateId, importChains ?? false)
                            if (success) {
                                loadSession(name)
                            } else {
                                setCreateSessionDialog(true)
                            }
                        }
                    }}
                />
            }
            {midiManager &&
                <MidiBrowser
                    midiActive={hasConnectedPort}
                    control={makeMidiControl()}
                    chains={Chains}
                    sessions={Sessions}
                    app={appState}
                    close={() => setMidiManager(false)}
                />
            }
            {bgDialog &&
                <widget.BackgroundImageDialog
                    value={$AppState.settings.background}
                    onClose={bg => {
                        if (bg) {
                            $AppState.settings.background = bg
                            setAppState({...$AppState})
                            LocalStore.saveApp($AppState)
                        }
                        setBgDialog(false)
                        // Commit applies the new vars; cancel restores the saved ones.
                        widget.applyBackgroundToSpace($AppState.settings.background)
                    }}
                />
            }
            {sysConfig &&
                <SystemConfig
                    tab={sysConfigTab}
                    setTab={setSysConfigTab}
                    close={() => setSysConfig(false)}
                    tabs={{
                        midi: (
                            <Div k='midi-tab'>
                                <MidiPortSelector
                                    ports={midiPorts}
                                    selected={selectedPorts}
                                    setSelected={setSelectedPorts}
                                />
                                <MidiBrowser
                                    embed
                                    midiActive={hasConnectedPort}
                                    control={makeMidiControl()}
                                    chains={Chains}
                                    sessions={Sessions}
                                    app={appState}
                                />
                                <MidiStorageReference/>
                            </Div>
                        ),
                        devices: (
                            <DevicesConfig
                                onExport={() => exportDevices(Object.values($Devices))}
                                onImport={() => setPop({
                                    title: 'OVERWRITE EVERYTHING?',
                                    prompt: 'Import operation OVERWRITES all DEVICES + CHAINS. Proceed?',
                                    yes: () => importRef.current?.click(),
                                })}
                            />
                        ),
                        backup: (
                            <BackupConfig
                                onSave={() => saveBackup()}
                                onRestore={() => setPop({
                                    title: 'DELETE EVERYTHING?',
                                    prompt: 'Restoring backup DELETES EVERYTHING. Proceed?',
                                    yes: () => restoreRef.current?.click(),
                                })}
                            />
                        ),
                    }}
                />
            }
            <DragLayer/>
            {!PRODUCTION && !DEMO && <TimeDisplay/>}
            <input ref={importSessRef} type='file' className='hidden' onChange={importSession}/>
            <input ref={restoreRef} type='file' className='hidden' onChange={restoreBackup}/>
            <div id='ingest-errors' className={!importErrors ? ' hidden' : ''}>
                <div>One or more import errors have occurred</div>
                <pre>{importErrors}</pre>
                <button onClick={() => setImportErrors(undefined)}>OK</button>
            </div>
            <MidiEventPopup event={midiEvent}/>
            {offlineNotice &&
                <widget.PopAlert
                    k='offline'
                    title='OFFLINE'
                    message={'ProPatch is not responding. Check USB/Ethernet cable.\n\nThis warning can be disabled in Configuration menu.'}
                    dismiss='Close'
                    options={{ esc: true, click: false, blur: false }}
                    clear={() => setOfflineNotice(false)}
                />
            }
            <Div id='screen' k={[!apiState && 'disconnected']} s={{pointerEvents: midiLearn ? 'none' : 'all'}}>
                <Div id="wolfflogo" {...onClick(() => setAppInfo(true))} ><WolffLogo/></Div>
                <Div id='midi-over' s={{display: midiLearn ? 'block' : 'none'}}/>
                <Div id='top-icons'>
                    <Div id='top-left'>
                        {$ActiveSession?.name && $Sessions[$ActiveSession.name] &&
                            <Snapper
                                midi={midiLearn}
                                session={$Sessions[$ActiveSession.name]}
                                sessions={$Sessions}
                                dirty={$ActiveSession.dirty}
                                controllers={{
                                    midi: midiController,
                                    session: sessionControl,
                                }}
                            />
                        }
                        {browserType === 'device' ?
                            <Div {...{
                                    id: 'device-new',
                                    k:['cta','top'],
                                    ...onClick(() => newDevModal?.current && newDevModal.current()),
                                }}>
                                <Div k='symbol'>add</Div>NEW DEVICE
                            </Div>
                            :
                            <Div {...{
                                    id: 'chain-new',
                                    k:['cta','top'],
                                    ...onClick(e => {
                                        setTimeout(() => {
                                            const chain = control.addChain({replace: e.shiftKey})
                                            NEW_CHAIN = chain.id
                                        },0)
                                    })
                                }}>
                                <Div k='symbol'>add</Div>NEW CHAIN
                            </Div>
                        }
                    </Div>
                    { /* Zoom hint: placed between left and right clusters */ }
                    <Div id='zoom-hint' s={{pointerEvents:'none'}}>
                        {(() => {
                            try {
                                const ua = navigator.userAgent || ''
                                const plat = (navigator.platform || '').toLowerCase()
                                const isMac = /mac|macintosh|mac os/.test(plat) || /Mac OS X/.test(ua)
                                return isMac
                                    ? 'Zoom — In ⌘ +  /  Out ⌘ −  /  Reset ⌘ 0'
                                    : 'Zoom — In Ctrl +  /  Out Ctrl −  /  Reset Ctrl 0'
                            } catch {
                                return 'Zoom — In ⌘ +  /  Out ⌘ −  /  Reset ⌘ 0'
                            }
                        })()}
                    </Div>
                    <Div id='top-right'>
                        <input ref={importRef} type='file' className='hidden' onChange={e => importDevices(e,{destructive:true})}/>
                        <widget.MidiLearn
                            connected={hasConnectedPort}
                            selected={!!midiLearn}
                            enabled={space === 'chain' && hasConnectedPort}
                            setSelected={v => setMidiLearn(v ? new MidiLearn() : undefined)}
                        />
                        <Div {...{
                            id: 'mute-all',
                            k:['cta','top',midiLearn?.state('mute') && 'midi-learn', midiLearn?.state('mute')],
                                ...onClick(midiLearn?.state('mute')
                                ? () => midiLearn.learn('mute')
                                : e => {
                                    const muted = $Chains.filter(({cond}) => cond.active)
                                    muted.forEach(({cond}) => cond.active = false)
                                    $AppState.cond.muteAll = true
                                    refreshState({chains:$Chains})
                                    setMuteAll(
                                        muted
                                        .reduce((p,c) => {
                                            p[c.id] = true
                                            return p
                                        },{})
                                    )
                                }),
                            }}>
                            <Div k='symbol'>power_settings_new</Div>&nbsp;MUTE
                        </Div>
                        <Div id='friendly' k={[apiState && 'connected',OP_PARAMS.FRIEND_NAME ? 'defined' : 'undefined']}>
                            <Div k={['name']}>
                                {OP_PARAMS.FRIEND_NAME?.toUpperCase() ?? 'disconnected'}
                            </Div>
                        </Div>
                        <TriPop
                            id='system-menu' k={[updatePending && 'update',updatePending && 'shimmer']}
                            el={updatePending && <Span k='text'>Update</Span>}
                            offset={updatePending ? {x:100,y:10} : {x:10,y:20}}
                            items={sysMenu}
                        />
                        <Div id='system-help' k='acticon' ref={helpRef} onClick={() => setHelpMenuOpen(p => !p)}>
                            <span className="symbols">help</span>
                            <PopupMenu
                                triggerRef={helpRef}
                                isVisible={helpMenuOpen}
                                close={() => setHelpMenuOpen(false)}
                                items={helpMenu}
                                offset={{x: 10, y: 20}}
                            />
                        </Div>
                    </Div>
                </Div>
                <Div id='browser-main'>
                    <Div k='types'>
                        <Div
                            k={['choice', browserType === 'chain' && 'selected']}
                            onClick={()=>setBrowserType('chain')}
                        >
                            <Div k='symbols'>flowchart</Div>
                            <Div k='item'>CHAINS</Div>
                        </Div>
                        <Div 
                            k={['choice', browserType === 'module' && 'selected']}
                            onClick={()=>setBrowserType('module')}
                        >
                            <NodeIcon size={18}/>
                            <Div k='item'>NODES</Div>
                        </Div>
                        <Div 
                            k={['choice', browserType === 'device' && 'selected']}
                            onClick={()=>setBrowserType('device')}
                        >
                            <DeviceIcon/>
                            <Div k='item'>CONFIG</Div>
                        </Div>
                    </Div>
                </Div>
                {
                    browserType === 'module'
                    ?
                        <NodeBrowser
                            arch={$Arch}
                            key={DEVICE_EPOCH}
                            chains={Chains}
                            devices={Devices}
                            modType={browserModType}
                            searchState={browserSearch}
                            controllers={{
                                module: modDefController,
                                browser: {
                                    setItemType: setBrowserModType,
                                    setSearch: setBrowserSearch,
                                    dragSeer: {
                                        start: (dragged:IDragged) => setDragItem(dragged),
                                        stop: () => setDragItem(undefined),
                                    },
                                    setDevice (id:string) {
                                        if ($Devices[id]) {
                                            setBrowserDevice(id)
                                            setBrowserType('device')
                                        }
                                    }
                                },
                                chain: control,
                            }}
                        />
                    :
                    browserType === 'chain'
                    ?
                        <ChainBrowser
                            key={CHAIN_EPOCH}
                            chains={Chains}
                            searchState={browserSearch}
                            chainFilter={chainFilter}
                            controllers={{
                                chain: control,
                                midi: midiController,
                                browser: {
                                    chainSearch,
                                    setSearch: setBrowserSearch,
                                },
                            }}
                            arch={$Arch}
                        />
                    :
                        <DeviceBrowser
                            key={DEVICE_EPOCH}
                            devices={Devices}
                            searchState={browserSearch}
                            selected={browserDevice ? Devices[browserDevice] : undefined}
                            onChoose={device => {
                                setSpace('device')
                                setBrowserDevice(device.id)
                            }}
                            onDelete={device => {
                                // GUARD: a device whose nodes are referenced by a chain cannot be deleted.
                                if (deviceInUse(device, $Chains)) {
                                    setAlert('Cannot delete: One or more chains reference nodes/ports of this device')
                                    return
                                }
                                // CONFIRM: deleting a whole device (all its nodes) is heavy — verify first.
                                setPop({
                                    title: 'DELETE DEVICE?',
                                    prompt: `Delete device [${device.name}]? This removes the device and all its nodes and cannot be undone.`,
                                    yes: () => {
                                        L('DEVICE-DELETE',device.id)
                                        if ($Devices[device.id]) {
                                            const devices = clone($Devices)
                                            delete devices[device.id]
                                            updateState({devices})
                                        }
                                        // RESET selection if the deleted device was open
                                        if (browserDevice === device.id) setBrowserDevice(undefined)
                                    },
                                })
                            }}
                            controllers={{
                                browser: {
                                    setSearch: setBrowserSearch,
                                },
                            }}
                        />
                }
                <Div id='space' k={midiLearn && 'midi-learning'}>
                    <Div k='space-bg'/>
                    {
                        space === 'device'
                        ?
                            <DeviceConfig 
                                arch={$Arch}
                                setAlert={setAlert}
                                newDevModal={newDevModal}
                                device={browserDevice ? Devices[browserDevice] : undefined}
                                devices={Devices}
                                setDevice={setBrowserDevice}
                                control={{
                                    chains: { get: () => clone($Chains) },
                                    devices: {
                                        add: $device => {
                                            const device = clone($device)
                                            const devices = clone($Devices)
                                            devices[device.id] = device
                                            L('DEVICE-CREATE',device.id)
                                            updateState({devices})
                                            return $device
                                        },
                                        modify: $device => {
                                            L('DEVICE-MODIFY',$device.id)
                                            if ($Devices[$device.id]) {
                                                const device = clone($device)
                                                // SYNCHRONIZE child nodes with device mods
                                                Object
                                                    .values(device.nodes)
                                                    .forEach(node => node.device = Device.fromDef(device))
                                                const devices = clone($Devices)
                                                devices[device.id] = device
                                                updateState({devices})
                                                return $device
                                            }
                                        },
                                        remove: device => {
                                            L('DEVICE-DELETE',device.id)
                                            if ($Devices[device.id]) {
                                                const devices = clone($Devices)
                                                delete devices[device.id]
                                                updateState({devices})
                                            }
                                        },
                                        copy: device => {
                                            if (!$Devices[device.id]) return undefined
                                            const devices = clone($Devices)
                                            // INDEX all DEVICE names
                                            const names = Object.values(devices)
                                                .map(({name}) => name)
                                            // PRODUCE suitable *indexed* unique node name(s)
                                            const dupeName = Names.indexedName({name: device.name, names})
                                            const copy = DeviceDef.copy({self:device,idgen:Identity.create,name:dupeName.copy})
                                            devices[copy.id] = copy
                                            L('DEVICE-COPY',device.id,copy.id)
                                            // MODIFY origin NAME?
                                            if (dupeName.origin) {
                                                const origin = devices[device.id]
                                                origin.name = dupeName.origin
                                                // UPDATE node device names
                                                Object
                                                    .values(devices[device.id].nodes)
                                                    .forEach(node => node.device =  Device.fromDef(origin))
                                            }
                                            updateState({devices})
                                            return copy
                                        },
                                        tags: $node => {
                                            return {
                                                // COLLECT all TAGS of all NODES
                                                all () {
                                                    return TagCache.node
                                                    /*
                                                    return Object.values($Devices).reduce((p,device) => {
                                                        Object.values(device.nodes).forEach(node => {
                                                            node.tags.forEach(tag => p[tag.name] = tag)
                                                        })
                                                        return p
                                                    },{})
                                                    */
                                                },
                                                assign (tag) {
                                                    const node = $Devices[$node.device.id]?.nodes[$node.id]
                                                    // NOT FOUND or ALREADY present
                                                    if (!node || node.tags.some(_ => _.name === tag.name)) return
                                                    L('NODE-TAG-ASSIGN',node.id,tag.name)
                                                    const devices = clone($Devices)
                                                    const device = devices[node.device.id]
                                                    device.nodes[node.id].tags = [...node.tags,tag]
                                                    updateState({devices})
                                                },
                                                unassign (tag) {
                                                    let node = $Devices[$node.device.id]?.nodes[$node.id]
                                                    // NOT FOUND or NOT present
                                                    if (!node || !node.tags.some(_ => _.name === tag.name)) return
                                                    L('NODE-TAG-UNASSIGN',node.id,tag.name)
                                                    const devices = clone($Devices)
                                                    const device = devices[node.device.id]
                                                    device.nodes[node.id].tags = node.tags.filter(_ => _.name !== tag.name)
                                                    updateState({devices})
                                                },
                                                add (tag) {
                                                    L('NODE-TAG-CREATE',tag.name)
                                                    TagCache.node[tag.name] = tag
                                                },
                                                remove (tag) {
                                                    L('NODE-TAG-DELETE',tag.name)
                                                    delete TagCache.node[tag.name]
                                                    const devices = clone($Devices)
                                                    Object.values(devices).forEach(device => {
                                                        Object.values(device.nodes).forEach(node => {
                                                            node.tags = node.tags.filter(_ => _.name !== tag.name)
                                                        })
                                                    })
                                                    updateState({devices})
                                                },
                                                modify (tag) {
                                                    // LOCATE original tag by ID (in case of name change)
                                                    const $tag = Object.values(TagCache.node).find(({id}) => tag.id === id)
                                                    if (!$tag) return
                                                    // NAME change
                                                    if ($tag.name !== tag.name) {
                                                        // REMOVE old from cache
                                                        delete TagCache.node[$tag.name]
                                                    }
                                                    // UPDATE cache
                                                    TagCache.node[tag.name] = tag
                                                    const devices = clone($Devices)
                                                    Object.values(devices).forEach(device => {
                                                        Object.values(device.nodes).forEach(node => {
                                                            node.tags = node.tags.map(_ => _.id === tag.id ? tag : _)
                                                        })
                                                    })
                                                    updateState({devices})
                                                },
                                            }
                                        },
                                        all: () => clone($Devices)
                                    },
                                }}
                            >
                            </DeviceConfig>
                        :
                        (
                            <ChainSpace 
                                chains={Chains}
                                arch={$Arch}
                                midi={midiLearn}
                                dragged={dragItem && { ...dragItem }}
                                dragSeer={{
                                    start: (dragged:IDragged) => {
                                        D('START DRAG',dragged)
                                        setTimeout(() => setDragItem(dragged),0)
                                    },
                                    stop: () => {
                                        D('STOP DRAG')
                                        setTimeout(() => setDragItem(undefined),0)
                                    },
                                }}
                                controllers={{
                                    chain: control,
                                    module: modController,
                                    midi: midiController,
                                }}
                                overlay={$PortOverlay}
                            />
                        )
                    }
                    <Toaster richColors position="bottom-left" expand={true} />
                    {$ShowShortcuts 
                        && <KeyboardShortcutsModal
                            targets={$ShowShortcuts}
                            onClose={() => setTimeout(() => setShowShortcuts(undefined), 0)}
                        />
                    }
                </Div>
            </Div>
        </DndProvider>
    )
}
